
import { BasePresenter } from "@presenters/base/base.presenter";
import { useSelector } from "react-redux";
import { IFollowup, ILang, IFollowupIndicators, IFollowupFinancial, IFollowupBeneficiaries } from "@models/*";
import { Constants, Dictionary, moment, Utilities, queryString, Enums, LocalStorageHelper } from "@utils";
import {  FollowupService, IFollowupService } from "@services/proyecta/followup.service";
import DataGrid from "devextreme/ui/data_grid";
import HtmlEditor from "devextreme/ui/html_editor";
import dxDataGrid from "devextreme/ui/data_grid";
import { setTimeout } from "timers";

export class FollowupSubprojectPresenter extends BasePresenter<any> {
    public currentLabels: Dictionary<string, string>;
    public followupLabels: Dictionary<string, string>;
    public formMasters: any;
    public indicatorsData: IFollowupIndicators;
    public beneficiariesData: IFollowupBeneficiaries;
    public financialData: IFollowupFinancial;
    public resultsFramework: IFollowupIndicators;
    public institutionalIndicators: any;
    public narrativeReport: any;
    public stepData: any;
    public followupData: IFollowup[];
    public subProjectDimensions: any[];
    public subProjectPeriods: any[];
    public currentLang: ILang;
    private followupService: IFollowupService;
    public subProjectMonths: any;
    public isLoadingContainer: any;
    public params: any;
    public tourIndSeen: any;
    public tourBenSeen: any;
    public tourFinSeen: any;
    public tourINSeen: any;
    public stepTourInd: any;
    public stepTourBen: any;
    public stepTourFin: any;
    public stepTourIN: any;

    public debug: boolean;

    constructor(view: any) {
        super(view);
        this.params = useSelector(this.context.sessionStore.selectParams);
        this.followupData = useSelector(this.context.followupStore.selectFollowupData);
        this.subProjectDimensions = useSelector(this.context.followupStore.selectSubProjectDimensions);
        this.formMasters = useSelector(this.context.followupStore.selectFormMasters);
        this.currentLabels = useSelector(this.context.sessionStore.selectLabels);
        this.followupLabels = useSelector(this.context.followupStore.selectLabels);
        this.currentLang = useSelector(this.context.sessionStore.selectLang) as any;

        this.indicatorsData = useSelector(this.context.followupStore.selectIndicatorsData);
        this.institutionalIndicators = useSelector(this.context.followupStore.selectInstitutionalIndicators);
        this.beneficiariesData = useSelector(this.context.followupStore.selectBeneficiariesData);
        this.financialData = useSelector(this.context.followupStore.selectFinancialData);
        this.resultsFramework = useSelector(this.context.followupStore.selectResultsFramework);
        this.narrativeReport = useSelector(this.context.followupStore.selectNarrativeReport);
        this.subProjectMonths = useSelector(this.context.followupStore.selectSubProjectMonths);
        this.stepData = useSelector(this.context.followupStore.selectStepData);
        this.subProjectPeriods = useSelector(this.context.followupStore.selectSubProjectPeriods);
        this.isLoadingContainer = useSelector(this.context.sessionStore.selectLoadingContainer);
        
        this.tourIndSeen = useSelector(this.context.followupStore.selectSeenAppTourInd);
        this.tourBenSeen = useSelector(this.context.followupStore.selectSeenAppTourBen);
        this.tourFinSeen = useSelector(this.context.followupStore.selectSeenAppTourFin);
        this.tourINSeen = useSelector(this.context.followupStore.selectSeenAppTourIN);
        this.stepTourInd = useSelector(this.context.followupStore.selectStepTourInd);
        this.stepTourBen = useSelector(this.context.followupStore.selectStepTourBen);
        this.stepTourFin = useSelector(this.context.followupStore.selectStepTourFin);
        this.stepTourIN = useSelector(this.context.followupStore.selectStepTourIN);

        this.saveData = this.saveData.bind(this);
        this.checkData = this.checkData.bind(this);
        this.saveTable = this.saveTable.bind(this);
        this.toggleEditing = this.toggleEditing.bind(this);
        this.getReportPermissions = this.getReportPermissions.bind(this);
        this.getCommonFieldProps = this.getCommonFieldProps.bind(this);
        this.followupService = new FollowupService();

        this.debug = false;
    }

    public async initialize(reload?: boolean, step: number = 0){ 
        this.dispatcherProgress(40);
        this.dispatcherPendingLoad(1);
        this.dispatcherSetLoading();

        this.dispatcherProgress(50);  

        let projectData = this.view.state.projectData
        let subvCode = !Utilities.isNullEmptyObject(projectData.subvCode) ? this.formMasters.grantCodes.find((x: any) => x.Title === projectData.subvCode)?.Id : null

        if(step === 4 || !step) this.dispatcher(this.context.followupStore.getNarrativeReport(this.getParamId()));
        if(step === 1 || !step){
            this.dispatcher(this.context.followupStore.getIndicatorsData(this.getParamId())); 
            this.dispatcher(this.context.followupStore.getInstitutionalIndicators(projectData.projectId, subvCode));
        }
        if(step === 2 || !step) this.dispatcher(this.context.followupStore.getBeneficiariesData(this.getParamId()));
        if(step === 3 || !step) this.dispatcher(this.context.followupStore.getFinancialData(this.getParamId()));

        if(reload) this.dispatcher(this.context.followupStore.getFollowupData()); 

        this.dispatcherProgress(60);            

        if (!reload){
            this.dispatcher(this.context.followupStore.getResultsFramework(projectData.projectId, subvCode));
            this.dispatcher(this.context.followupStore.getSPFE(projectData.projectId, subvCode));
        }

        this.dispatcherProgress(80);  
    }

    public async clear(){
        this.dispatcher(this.context.followupStore.getResultsFramework(null, null));
        this.dispatcher(this.context.followupStore.getSPFE(null, null));
        this.dispatcher(this.context.followupStore.getIndicatorsData(null)); 
        this.dispatcher(this.context.followupStore.getInstitutionalIndicators(null, null));
        this.dispatcher(this.context.followupStore.getBeneficiariesData(null));
        this.dispatcher(this.context.followupStore.getFinancialData(null));
        this.dispatcher(this.context.followupStore.getNarrativeReport(null));
    }

    public async initIndicators(){
        const columns = await this.followupService.getDefinitionColumnsIndicators(this.currentLang?.id);
        this.view.state.setDataColumns(columns || []);
    }

    public async initBeneficiaries(){ 
        const columns = await this.followupService.getDefinitionColumnsBeneficiaries(this.currentLang?.id);
        this.view.state.setDataColumns(columns || []);
    }

    public async initFinancial(){ 
        const columns = await this.followupService.getDefinitionColumnsFinancial(this.currentLang?.id);
        this.view.state.setDataColumns(columns || []);
    }

    public async initNarrativeReport(){
        await this.getDimensionData(Enums.FollowupDimensions.narrativeReport)
    }

    public getParamId(){
        const query = queryString.parseUrl(window.location.href.replace('#', '')).query as any;
        return query.Id
    }

    public async unSetLoading(){
        if(this.isLoadingContainer){
            setTimeout(() => {
                this.dispatcherProgress(90); 
            }, 2000);
            setTimeout(() => {
                this.dispatcherUnsetSetLoading();
            }, 4000);
        }
    }

    public async keepLoading(){
        if(this.isLoadingContainer){
            this.dispatcherProgress(90); 
        }else{
            this.dispatcherProgress(90); 
            this.dispatcherPendingLoad(1);
            this.dispatcherSetLoading();
        }
    }

    public loadingFinished(){
        return !this.isLoadingContainer
    }

    public changeStepTour(step: number, dimension: string){
        switch (dimension) {
            case Enums.FollowupDimensions.indicators: this.dispatcher(this.context.followupStore.changeStepTourInd(step))
                break;
            case Enums.FollowupDimensions.beneficiaries: this.dispatcher(this.context.followupStore.changeStepTourBen(step))
                break;
            case Enums.FollowupDimensions.financial: this.dispatcher(this.context.followupStore.changeStepTourFin(step))
                break;
            case Enums.FollowupDimensions.narrativeReport: this.dispatcher(this.context.followupStore.changeStepTourIN(step))
                break;
        }
    }

    public toggleTour(dimension: string, state: boolean){
        switch (dimension) {
            case Enums.FollowupDimensions.indicators:{
                LocalStorageHelper.set<any>(Enums.LocalStorageKeys.seenAppTourInd, state);
                this.dispatcher(this.context.followupStore.stateAppTourInd(state));
            } break;
            case Enums.FollowupDimensions.beneficiaries:{
                LocalStorageHelper.set<any>(Enums.LocalStorageKeys.seenAppTourBen, state);
                this.dispatcher(this.context.followupStore.stateAppTourBen(state));
            } break;
            case Enums.FollowupDimensions.financial:{
                LocalStorageHelper.set<any>(Enums.LocalStorageKeys.seenAppTourFin, state);
                this.dispatcher(this.context.followupStore.stateAppTourFin(state));
            } break;
            case Enums.FollowupDimensions.narrativeReport:{
                LocalStorageHelper.set<any>(Enums.LocalStorageKeys.seenAppTourIN, state);
                this.dispatcher(this.context.followupStore.stateAppTourIN(state));
            } break;
        }
    }

    public getTourDimension(dimension: any, currentDimension: any): any{
        const MonthsEn = Constants.Months.English
        switch (dimension) {
            case Enums.FollowupDimensions.indicators:{
                let auxDimension = Utilities.copyObject(currentDimension)
                auxDimension.EstadoDimension = this.formMasters?.followupState?.find((z: any) => z.OCTAVIACodigo === Enums.FollowupState.substantialModifications)?.Title
                auxDimension.CodigoDimension = Enums.FollowupState.substantialModifications
                return auxDimension;
            }
            case Enums.FollowupDimensions.beneficiaries:{
                let auxDimension = Utilities.copyObject(currentDimension)
                auxDimension.EstadoDimension = this.formMasters?.followupState?.find((z: any) => z.OCTAVIACodigo === Enums.FollowupState.followup)?.Title
                auxDimension.CodigoDimension = Enums.FollowupState.followup
                return auxDimension;
            }
            case Enums.FollowupDimensions.financial:{
                let auxDimension = Utilities.copyObject(currentDimension)
                auxDimension.EstadoDimension = this.formMasters?.followupState?.find((z: any) => z.OCTAVIACodigo === Enums.FollowupState.planning)?.Title
                auxDimension.CodigoDimension = Enums.FollowupState.planning
                return auxDimension;
            }
            case Enums.FollowupDimensions.narrativeReport:{
                let auxDimension = Utilities.copyObject(currentDimension)
                if(this.stepTourIN < 8){
                    auxDimension.EstadoPeriodo = this.formMasters?.followupState?.find((z: any) => z.OCTAVIACodigo === Enums.FollowupState.process)?.Title
                    auxDimension.EstadoPeriodoCod = Enums.FollowupState.process
                }else{
                    auxDimension.EstadoPeriodo = this.formMasters?.followupState?.find((z: any) => z.OCTAVIACodigo === Enums.FollowupState.presented)?.Title
                    auxDimension.EstadoPeriodoCod = Enums.FollowupState.presented
                }
                return auxDimension;
            }
        }
    }

    public getTourData(step: number, dimension: string, year?: string): any{
        const MonthsEn = Constants.Months.English
        switch (dimension) {
            case Enums.FollowupDimensions.indicators:
                switch (step) {
                    case 8:{
                        return [{
                            id: 1,
                            indicator: this.formMasters?.strategicLine?.[0]?.Title,
                            lbUnity: this.formMasters?.measureUnits?.[0]?.Title,
                            lbAnio: "01/" + new Date().getFullYear(),
                            lbValue: 0,
                            actualAchievedGoal: 100,
                            isMainLineTour: true,
                        }]
                    }
                    case 11:{
                        let auxValue: any = [{
                                id: 1,
                                selected: true,
                                indicator: this.formMasters?.strategicLine?.[0]?.Title,
                                lbUnity: this.formMasters?.measureUnits?.[0]?.Title,
                                plannedGoal: 100,
                                isProductLineTour: true,
                            },{
                                id: 2,
                                selected: false,
                                indicator: this.formMasters?.strategicLine?.[1]?.Title,
                                lbUnity: this.formMasters?.measureUnits?.[1]?.Title,
                                plannedGoal: 200,
                            }]
                        MonthsEn.forEach(mt => { auxValue[0][mt+"Pl"] = 0});
                        return auxValue
                    }
                }
            case Enums.FollowupDimensions.beneficiaries:
                switch (step) {
                    case 3:{
                        return [{
                            id: 1,
                            fieldDescription: this.followupLabels?.get('countryDummy1'),
                            fieldTotal: 0
                        },{
                            id: 2,
                            fieldDescription: this.followupLabels?.get('countryDummy2'),
                            fieldTotal: 0
                        }]
                    }
                    case 4:{
                        return [{
                            id: 1,
                            description: this.followupLabels?.get('subProjectBeneficiaries') + ' ' + year || '',
                            planningMen: 790,
                            planningWomen: 730,
                            planningTotal: 1520,
                        },{
                            id: 2,
                            description: this.followupLabels?.get('subProjectTotalBeneficiaries') || '',
                            planningMen: 1580,
                            planningWomen: 1460,
                            planningTotal: 3040,
                        }]
                    }
                    case 5:{
                        let auxValue: any = [{
                            id: 1,
                            activity: this.followupLabels?.get('activityDummy1'),
                            beneficiariesByActivity: 790,
                        },{
                            id: 2,
                            activity: this.followupLabels?.get('activityDummy2'),
                            beneficiariesByActivity: 0,
                        },{
                            id: 3,
                            activity: this.followupLabels?.get('subTotalBeneficiariesResult'),
                            beneficiariesByActivity: 790,
                        },{
                            id: 4,
                            activity: this.followupLabels.get('benefitedActivities'),
                            beneficiariesByActivity: 120,
                            isResultTour: true
                        },{
                            id: 4,
                            activity: this.followupLabels.get('totalBeneficiariesResult'),
                            beneficiariesByActivity: 670,
                        }]
                        MonthsEn.forEach(mt => { 
                            auxValue[0][mt+"Pl"] = 79
                            auxValue[2][mt+"Pl"] = 79
                            auxValue[3][mt+"Pl"] = 10
                            auxValue[4][mt+"Pl"] = 69
                        });
                        return auxValue
                    }
                    case 8:{
                        let auxValue: any = [{
                            id: 1,
                            result: this.followupLabels?.get('resultDummy1'),
                            beneficiariesByActivity: 690,
                            isOverlapTour: true,
                            overlaps:[{
                                id: 2,
                                SegBenefParentId: 1,
                                result: this.followupLabels?.get('resultDummy2'),
                                isOverChildTour: true
                            },{
                                id: 3,
                                SegBenefParentId: 1,
                                result: this.followupLabels?.get('resultDummy3'),
                            }]
                        },{
                            id: 2,
                            result: this.followupLabels?.get('resultDummy2'),
                            beneficiariesByActivity: 730,
                            overlaps:[{
                                id: 3,
                                SegBenefParentId: 1,
                                result: this.followupLabels?.get('resultDummy3'),
                            }]
                        },{
                            id: 3,
                            result: this.followupLabels?.get('resultDummy3'),
                            beneficiariesByActivity: 100,
                        }]
                        MonthsEn.forEach(mt => { 
                            auxValue[0][mt+"Pl"] = 69
                            auxValue[1][mt+"Pl"] = 73
                            auxValue[2][mt+"Pl"] = 10
                        });
                        return auxValue
                    }
                    case 10:{
                        let auxValue: any = [{
                            activity: this.followupLabels?.get("beneficiariesTotalMonthSubtotal"),
                            beneficiariesByActivity: 1520
                        },{
                            activity: this.followupLabels?.get("beneficiariesTotalMonthTotalBeneficiaries"),
                            beneficiariesByActivity: 0,
                            isTotalBen: true
                        },{
                            activity: this.followupLabels?.get("beneficiariesTotalMonthTotalNumBeneficiariesMonth"),
                            beneficiariesByActivity: 1520,
                            isTotalMonth: true
                        },{
                            activity: this.followupLabels?.get("beneficiariesTotalMonthTotalNumBeneficiariesAcumulative"),
                            beneficiariesByActivity: 1520,
                            januaryPl: 152,
                            februaryPl: 304,
                            marchPl: 456,
                            aprilPl: 608,
                            mayPl: 760,
                            junePl: 912,
                        },{
                            activity: this.followupLabels?.get("beneficiariesTotalMonthTotalBeneficiariesPorcent"),
                            beneficiariesByActivity: '100.00 %',
                            januaryPl: '10.00 %',
                            februaryPl: '20.00 %',
                            marchPl: '30.00 %',
                            aprilPl: '40.00 %',
                            mayPl: '50.00 %',
                            junePl: '60.00 %',
                        },
                        ]
                        MonthsEn.forEach(mt => { 
                            auxValue[0][mt+"Pl"] = 152
                            auxValue[1][mt+"Pl"] = 0
                            auxValue[2][mt+"Pl"] = 152
                            auxValue[0][mt+"Re"] = 0
                            auxValue[1][mt+"Re"] = 0
                            auxValue[2][mt+"Re"] = 0
                            auxValue[3][mt+"Re"] = 0
                            auxValue[4][mt+"Re"] = '0.00 %'
                        });
                        return {
                            id: 1,
                            staticTitle: this.followupLabels?.get("beneficiariesTotals"),
                            section: "benResult",
                            data: auxValue
                        }
                    }
                    case 12:{
                        return ({
                            id: 1,
                            data:[{
                                description: this.followupLabels?.get("beneficiariesTotalAuxValidation"),
                                value: this.followupLabels?.get("beneficiariesTotalAuxValidationValid"),
                            },{
                                description: this.followupLabels?.get("beneficiariesTotalAuxPorcentAdvance")+" " + year,
                                value: '5.20 %',
                            },{
                                description: this.followupLabels?.get("beneficiariesTotalAuxPorcentAdvanceProyect"),
                                value: '2.60 %',
                            }]
                        })
                    }
                    case 13:{
                        return{
                            staticTitle: this.followupLabels?.get("beneficiariesDissaggregationGender"),
                            data: [{
                                disaggregationDescription: this.followupLabels?.get("BeneficiariesDissaggregationTotal"),
                                descriptionValue: 79,
                            },
                            {
                                disaggregationDescription: this.followupLabels?.get("beneficiariesDissaggregationMen"),
                                descriptionValue: 39,
                                isDissag: true,
                            },
                            {
                                disaggregationDescription:this.followupLabels?.get("beneficiariesDissaggregationWoman"),
                                descriptionValue: 40,
                            }],
                        }
                    }
                }
            case Enums.FollowupDimensions.financial:
                switch (step) {
                    case 2:{
                        return [{
                            budgetType: this.followupLabels.get("financialForActivity"),
                            id: 0,
                            budgetTypeId: 0,
                            yearBudget: 123667,
                            yearBudgetMod: 123667,
                            yearBudgetExecuted: 0
                        },{
                            budgetType: this.followupLabels.get("financialForImplement"),
                            id: 1,
                            budgetTypeId: 1,
                            yearBudget: 124084,
                            yearBudgetMod: 124084,
                            yearBudgetExecuted: 0
                        },{
                            budgetType: this.followupLabels.get("financialForLocation"),
                            id: 2,
                            budgetTypeId: 2,
                            yearBudget: 124084,
                            yearBudgetMod: 124084,
                            yearBudgetExecuted: 0
                        },{
                            budgetType: this.followupLabels.get("financialForADT"),
                            id: 3,
                            budgetTypeId: 3,
                            yearBudget: 0,
                            yearBudgetMod: 0,
                            yearBudgetExecuted: 0
                        },{
                            budgetType: this.followupLabels.get("financialForOriginFunds"),
                            id: 4,
                            budgetTypeId: 4,
                            yearBudget: 0,
                            yearBudgetMod: 0,
                            yearBudgetExecuted: 0
                        }]
                    }
                    case 4:{
                        return [{
                            id: 0,
                            staticTitle: this.followupLabels?.get("financialActivityExpectedResultTitle"),
                            name: this.followupLabels?.get("resultDummy1"),
                            data:[{
                                activity: this.followupLabels?.get("activityDummy1"),
                                id: 0,
                                currentBudget: 0,
                                totalBudget: 1393
                            },{
                                activity: this.followupLabels?.get("activityDummy2"),
                                id: 1,
                                currentBudget: 9091,
                                totalBudget: 9091
                            },{
                                activity: this.followupLabels?.get("activityDummy3"),
                                id: 2,
                                currentBudget: 606,
                                totalBudget: 606,
                                isActivityTour: true
                            }]
                        },{
                            id: 1,
                            staticTitle: this.followupLabels?.get("financialActivityExpectedResultTitle"),
                            name: this.followupLabels?.get("resultDummy2"),
                            data:[{
                                activity: this.followupLabels?.get("activityDummy1"),
                                id: 0,
                                currentBudget: 0,
                                totalBudget: 1393
                            },{
                                activity: this.followupLabels?.get("activityDummy2"),
                                id: 1,
                                currentBudget: 9091,
                                totalBudget: 9091
                            },{
                                activity: this.followupLabels?.get("activityDummy3"),
                                id: 2,
                                currentBudget: 606,
                                totalBudget: 606,
                                isActivityTour: true
                            }]
                        },{
                            id: 2,
                            staticTitle: this.followupLabels?.get("financialActivityExpectedResultTitle"),
                            name: this.followupLabels?.get("resultDummy3"),
                            data:[{
                                activity: this.followupLabels?.get("activityDummy1"),
                                id: 0,
                                currentBudget: 0,
                                totalBudget: 1393
                            },{
                                activity: this.followupLabels?.get("activityDummy2"),
                                id: 1,
                                currentBudget: 9091,
                                totalBudget: 9091
                            },{
                                activity: this.followupLabels?.get("activityDummy3"),
                                id: 2,
                                currentBudget: 606,
                                totalBudget: 606,
                                isActivityTour: true
                            }]
                        }]
                    }
                    case 8:{
                        return [{
                            id: 0,
                            implementer: this.followupLabels?.get("implementerDummy1"),
                            currentBudget: 79051
                        },{
                            id: 1,
                            implementer: this.followupLabels?.get("implementerDummy2"),
                            currentBudget: 45033
                        }]
                    }
                    case 9:{
                        return [{
                            id: 0,
                            location: this.followupLabels?.get('countryDummy1'),
                            currentBudget: 93063
                        },{
                            id: 1,
                            location: this.followupLabels?.get('countryDummy2'),
                            currentBudget: 31021
                        }]
                    }
                }
            case Enums.FollowupDimensions.narrativeReport:
                switch (step) {
                    case 4:{
                        return { 
                            current: `<div class="ExternalClassBCD55AE5ED574AEDA73E9F83415683F5"><p style="text-align&#58;center;"><strong style="font-family&#58;&quot;Open Sans&quot;, Arial, sans-serif;color&#58;rgb(0, 0, 0);font-size&#58;10pt;">
                                ${this.followupLabels?.get('reportCurrentDummy1')}
                                </strong></p><p><br></p><p><u style="font-family&#58;&quot;Open Sans&quot;, Arial, sans-serif;color&#58;rgb(18, 52, 222);background-color&#58;rgb(255, 255, 255);">
                                ${this.followupLabels?.get('reportCurrentDummy2')}
                                </u></p><p><br></p><p><span style="font-family&#58;&quot;Lucida Console&quot;;color&#58;rgb(0, 0, 0);">
                                ${this.followupLabels?.get('reportCurrentDummy3')}
                                </span></p></div>`,
                            history: `<div class="ExternalClassA558B7B683724151A089DA94B36AC61C"><p>
                                ${this.followupLabels?.get('reportHistoryDummy1')}
                            </p></div>`
                        }
                    }
                }
        }
    }

    public getPositionTour(step: number, dimension: string): "bottom" | "left" | "right" | "top"{
        switch (dimension) {
            case Enums.FollowupDimensions.indicators:
                switch (step) {
                    case 2:
                    case 13:
                    case 14:
                    case 15:{
                        return "bottom"
                    }
                    case 8:
                    case 9:{
                        return "right"
                    }
                    case 10:
                    case 12:{
                        return "left"
                    }
                    default: return "top"
                }
            case Enums.FollowupDimensions.beneficiaries:
                switch (step) {
                    case 2:
                    case 3:
                    case 10:
                    case 12:
                    case 13:{
                        return "right"
                    }
                    case 5:{
                        return "left"
                    }
                    case 11:
                    case 14:{
                        return "bottom"
                    }
                    default: return "top"
                }
            case Enums.FollowupDimensions.financial:
                switch (step) {
                    case 2:{
                        return "right"
                    }
                    case 3:
                    case 4:{
                        return "left"
                    }
                    case 8:
                    case 9:{
                        return "bottom"
                    }
                    default: return "top"
                }
            case Enums.FollowupDimensions.narrativeReport:
                switch (step) {
                    case 3:{
                        return "right"
                    }
                    default: return "top"
                }
        }
    }

    public getScrollTour(step: number, dimension: string): ScrollLogicalPosition{
        switch (dimension) {
            case Enums.FollowupDimensions.indicators:
                switch (step) {
                    case 14: return "end"
                    default: return "center"
                }
            case Enums.FollowupDimensions.beneficiaries:
                switch (step) {
                    default: return "center"
                }
            case Enums.FollowupDimensions.financial:
                switch (step) {
                    case 7: return "end"
                    case 9: return "start"
                    default: return "center"
                }
            case Enums.FollowupDimensions.narrativeReport:
                switch (step) {
                    default: return "center"
                }
        }
    }
    
    public async getInstitutionalIndData(instInd: any, year:number){
        let indicators: any = {}
        let mainLine = instInd.MainLine
        let isWrong: boolean = false
        let missingInformation = false

        let plannedGoal = mainLine?.baseLineYears?.find((x: any) => x.PPresAnioMeta === year)
        let indData = []

        if(!Utilities.isNullObject(plannedGoal?.PProaAnioValue)){ 
            let strategicLine = this.formMasters.strategicLine.find((x: any) => x.Id === Number(mainLine.genericInfo.PPresIndicadorLineaEstrategica1Id))
            let measureUnits = (!!strategicLine) ? this.formMasters.measureUnits.find((x: any) => x.Id === Number(strategicLine?.PMedidaId)) : null
            let relatedIndicator = this.indicatorsData?.indLineaPrincipal.filter((a: any) => a.PPresIndicadorLineaEstrategicaId == mainLine.genericInfo.PPresIndicadorLineaEstrategica1Id && plannedGoal?.Id === a.IndicadorLineaEstrategicaAniosId);
            relatedIndicator = relatedIndicator?.length > 0 ? relatedIndicator[0] : null;
            let auxInd: any = {
                id: mainLine.genericInfo.Id,
                idParent: mainLine.genericInfo.PPresIndicadorLineaEstrategica1Id, //mainLine.genericInfo.Id,
                indicator: strategicLine?.Title,
                lbValue: mainLine.genericInfo.PFFRRLineaBase,
                lbUnity: measureUnits?.Title,
                lbAnio: mainLine.genericInfo.PProaAnioLineaBase,
                plannedGoal: plannedGoal?.PProaAnioValue,
                endGoal: mainLine.genericInfo.PProaMetaFinal,
                currentProgressPercent: 0,
                totalProgressPercent: 0,
                idYear: plannedGoal?.Id,

                criteria: relatedIndicator?.CriterioExitoId,
                frecuency: relatedIndicator?.FrecuenciaMedicionId,
                relatedId: relatedIndicator?.Id,

                plannedGoalMod: relatedIndicator?.MetaPlaneadaAnioMod,
                endGoalMod: relatedIndicator?.MetaFinalProyectoMod,
                actualAchievedGoal: relatedIndicator?.MetaAlcanzada,
                plannedGoalModNv: relatedIndicator?.MetaPlaneadaAnioMod,
                endGoalModNv: relatedIndicator?.MetaFinalProyectoMod,
                actualAchievedGoalNv: relatedIndicator?.MetaAlcanzada,
                achievedGoalAnt: null
            };

            let sortYears = Utilities.copyObject(mainLine.baseLineYears)
            sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));

            for (const actYear of sortYears) {
                let findYear = this.indicatorsData?.indLineaPrincipal.find((a: any) => a.IndicadorLineaEstrategicaAniosId == actYear?.Id);
                if(!!findYear && actYear.PPresAnioMeta === (year-1)){
                    auxInd.achievedGoalAnt = findYear?.MetaAlcanzada
                }
            }

            if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxInd.plannedGoal) || Utilities.isNullEmptyObject(auxInd.endGoal) || 
                Utilities.isNullEmptyObject(auxInd.lbUnity) || Utilities.isNullEmptyObject(auxInd.lbAnio))
            
            if(this.debug) console.log('missingInformation1', missingInformation)

            indData.push(auxInd)
        }

        indicators.mainLine = [{
            staticTitle: this.followupLabels.get("mainStrategicLine"),
            schema: "indicadores",
            name: "",
            data: indData,
        }]

        let secondData: any = []
        let resultsData: any = []
        let productsData: any = []
        let relatedIndicatorMRI: any = []

        instInd.PProaMRI.forEach((mri: any, i:number) => {
            let strategicLine: any = null
            let step = ""
            let indicatorId = 0;
            let plannedGoal = mri.years?.find((x: any) => x.PPresAnioMeta === year )

            //if(!Utilities.isNullObject(plannedGoal?.PProaAnioValue)){
                if(!!mri.genericInfo?.PFFRRIndicadorResultadoIntermediId?.length){
                    step = "resultsData"
                    indicatorId = Number(mri.genericInfo.PFFRRIndicadorResultadoIntermediId[0])
                    relatedIndicatorMRI = this.indicatorsData?.indResInstitucionales.filter((a: any) => a.PFFRRIndicadorResultadoIntermediId[0] == indicatorId && plannedGoal?.Id === a.IndicadorInstitucionalResultados0Id);
                    strategicLine = this.formMasters.intermediateIndicatorResults.find((x: any) => x.Id === Number(mri.genericInfo.PFFRRIndicadorResultadoIntermediId[0]))
                }else if(!!mri.genericInfo?.PFFRRIndicadorProductoId){
                    step = "productsData"
                    indicatorId = Number(mri.genericInfo?.PFFRRIndicadorProductoId)
                    relatedIndicatorMRI = this.indicatorsData?.indProInstitucionales.filter((a: any) => a.PFFRRIndicadorProductoId == indicatorId && plannedGoal?.Id === a.IndicadorInstitucionalProductosAId);
                    strategicLine = this.formMasters.productsIndicator.find((x: any) => x.Id === Number(mri.genericInfo?.PFFRRIndicadorProductoId))
                }else{
                    step = "secondData"
                    indicatorId = Number(mri.genericInfo?.PPresIndicadorLineaEstrategicaId)
                    relatedIndicatorMRI = this.indicatorsData?.indLineaSecundaria.filter((a: any) => a.PPresIndicadorLineaEstrategicaId == indicatorId && plannedGoal?.Id === a.IndLineaEstrategicaAniosSecundarId);
                    strategicLine = this.formMasters.strategicLine.find((x: any) => x.Id === Number(mri.genericInfo?.PPresIndicadorLineaEstrategicaId))
                }
                    
                let measureUnits = (!!strategicLine) ? this.formMasters.measureUnits.find((x: any) => x.Id === Number(strategicLine?.PMedidaId)) : null
                relatedIndicatorMRI = relatedIndicatorMRI?.length > 0 ? relatedIndicatorMRI[0] : null;

                let sortYears = Utilities.copyObject(mri.years)
                sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
               
                let auxMri: any = {
                    id: mri.genericInfo.Id,
                    idParent: indicatorId, //mri.genericInfo.Id,
                    indicator: strategicLine?.Title,
                    lbValue: mri.genericInfo.PFFRRLineaBase,
                    lbUnity: measureUnits?.Title,
                    lbAnio: mri.genericInfo.PProaAnioLineaBase,
                    plannedGoal: plannedGoal?.PProaAnioValue,
                    endGoal: mri.genericInfo.PProaMetaMRI,
                    currentProgressPercent: 0,
                    totalProgressPercent: 0,
                    idYear: plannedGoal?.Id,

                    relatedId: relatedIndicatorMRI?.Id,
                    criteria: relatedIndicatorMRI?.CriterioExitoId,
                    frecuency: relatedIndicatorMRI?.FrecuenciaMedicionId,

                    plannedGoalMod: relatedIndicatorMRI?.MetaPlaneadaAnioMod,
                    endGoalMod: relatedIndicatorMRI?.MetaFinalProyectoMod,
                    actualAchievedGoal: relatedIndicatorMRI?.MetaAlcanzada,

                    plannedGoalModNv: relatedIndicatorMRI?.MetaPlaneadaAnioMod,
                    endGoalModNv: relatedIndicatorMRI?.MetaFinalProyectoMod,
                    actualAchievedGoalNv: relatedIndicatorMRI?.MetaAlcanzada,
                    achievedGoalAnt: null
                }

                if (step === "productsData"){
                    auxMri = {...auxMri, 
                        isProductLine: true,
                        januaryPl: relatedIndicatorMRI?.EneroPlan,
                        februaryPl: relatedIndicatorMRI?.FebreroPlan,
                        marchPl: relatedIndicatorMRI?.MarzoPlan,
                        aprilPl: relatedIndicatorMRI?.AbrilPlan,
                        mayPl: relatedIndicatorMRI?.MayoPlan,
                        junePl: relatedIndicatorMRI?.JunioPlan,
                        julyPl: relatedIndicatorMRI?.JulioPlan,
                        augustPl: relatedIndicatorMRI?.AgostoPlan,
                        septemberPl: relatedIndicatorMRI?.SeptiembrePlan,
                        octoberPl: relatedIndicatorMRI?.OctubrePlan,
                        novemberPl: relatedIndicatorMRI?.NoviembrePlan,
                        decemberPl: relatedIndicatorMRI?.DiciembrePlan,

                        januaryRe: relatedIndicatorMRI?.EneroReal,
                        februaryRe: relatedIndicatorMRI?.FebreroReal,
                        marchRe: relatedIndicatorMRI?.MarzoReal,
                        aprilRe: relatedIndicatorMRI?.AbrilReal,
                        mayRe: relatedIndicatorMRI?.MayoReal,
                        juneRe: relatedIndicatorMRI?.JunioReal,
                        julyRe: relatedIndicatorMRI?.JulioReal,
                        augustRe: relatedIndicatorMRI?.AgostoReal,
                        septemberRe: relatedIndicatorMRI?.SeptiembreReal,
                        octoberRe: relatedIndicatorMRI?.OctubreReal,
                        novemberRe: relatedIndicatorMRI?.NoviembreReal,
                        decemberRe: relatedIndicatorMRI?.DiciembreReal,

                        actualActAchievedGoal: relatedIndicatorMRI?.MetaAlcanzada,

                        previousAchievements: null
                    }
                    delete auxMri.criteria
                    delete auxMri.actualAchievedGoal

                    for (let index = 0; index < sortYears.length; index++) {
                        const actYear = sortYears[index];
                        let findYear = this.indicatorsData?.indProInstitucionales.find((a: any) => a.IndicadorInstitucionalProductosAId == actYear?.Id);
                        if(!!findYear){
                            let auxLastYear: any = {
                                januaryRe: findYear?.EneroReal,
                                februaryRe: findYear?.FebreroReal,
                                marchRe: findYear?.MarzoReal,
                                aprilRe: findYear?.AbrilReal,
                                mayRe: findYear?.MayoReal,
                                juneRe: findYear?.JunioReal,
                                julyRe: findYear?.JulioReal,
                                augustRe: findYear?.AgostoReal,
                                septemberRe: findYear?.SeptiembreReal,
                                octoberRe: findYear?.OctubreReal,
                                novemberRe: findYear?.NoviembreReal,
                                decemberRe: findYear?.DiciembreReal
                            }
                            if(actYear.PPresAnioMeta < year){
                                auxMri.previousAchievements = (auxMri.previousAchievements || 0) + (findYear?.MetaAlcanzada ? findYear.MetaAlcanzada : this.sumRealMonths(auxLastYear))
                            }
                            if(actYear.PPresAnioMeta === (year-1)){
                                auxLastYear.achievedGoalAnt = null
                                let yearLast = index -1
                                while(sortYears[yearLast]){
                                    let findLastYear = this.indicatorsData?.indProInstitucionales.find((a: any) => a.IndicadorInstitucionalProductosAId == sortYears[yearLast].Id);
                                    if(!findLastYear){
                                        let objLastYear = {
                                            januaryRe: findLastYear?.EneroReal,
                                            februaryRe: findLastYear?.FebreroReal,
                                            marchRe: findLastYear?.MarzoReal,
                                            aprilRe: findLastYear?.AbrilReal,
                                            mayRe: findLastYear?.MayoReal,
                                            juneRe: findLastYear?.JunioReal,
                                            julyRe: findLastYear?.JulioReal,
                                            augustRe: findLastYear?.AgostoReal,
                                            septemberRe: findLastYear?.SeptiembreReal,
                                            octoberRe: findLastYear?.OctubreReal,
                                            novemberRe: findLastYear?.NoviembreReal,
                                            decemberRe: findLastYear?.DiciembreReal
                                        }
                                        auxLastYear.achievedGoalAnt = (auxLastYear.achievedGoalAnt || 0) + (findLastYear?.MetaAlcanzada ? findLastYear.MetaAlcanzada : this.sumRealMonths(objLastYear))

                                    }
                                    yearLast -= 1
                                }
                                auxMri.achievedGoalAnt = (auxMri.achievedGoalAnt || 0) + (findYear?.MetaAlcanzada ? findYear.MetaAlcanzada : this.sumRealMonths(auxLastYear))
                            }
                        }
                    }

                    if(!isWrong){
                        isWrong = !this.checkAchievement(auxMri, year)
                    }

                    if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxMri.plannedGoal) || Utilities.isNullEmptyObject(auxMri.endGoal) || 
                        Utilities.isNullEmptyObject(auxMri.lbUnity))
                        if(this.debug) console.log('missingInformation2', missingInformation)

                    productsData.push(auxMri)
                }else if(step === "resultsData"){
                    for (const actYear of sortYears) {
                        let findYear = this.indicatorsData?.indResInstitucionales.find((a: any) => a.IndicadorInstitucionalResultados0Id == actYear?.Id);
                        if(!!findYear && actYear.PPresAnioMeta === (year-1)){
                            auxMri.achievedGoalAnt = findYear?.MetaAlcanzada
                        }
                    }

                    if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxMri.plannedGoal) || Utilities.isNullEmptyObject(auxMri.endGoal) || 
                    Utilities.isNullEmptyObject(auxMri.lbUnity) || Utilities.isNullEmptyObject(auxMri.lbAnio))
                    if(this.debug) console.log('missingInformation3', missingInformation)

                    resultsData.push(auxMri)
                }else{
                    for (const actYear of sortYears) {
                        let findYear = this.indicatorsData?.indLineaSecundaria.find((a: any) => a.IndLineaEstrategicaAniosSecundarId == actYear?.Id);
                        if(!!findYear && actYear.PPresAnioMeta === (year-1)){
                            auxMri.achievedGoalAnt = findYear?.MetaAlcanzada
                        }
                    }

                    if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxMri.plannedGoal) || Utilities.isNullEmptyObject(auxMri.endGoal) || 
                    Utilities.isNullEmptyObject(auxMri.lbUnity) || Utilities.isNullEmptyObject(auxMri.lbAnio))
                    if(this.debug) console.log('missingInformation4', missingInformation)

                    secondData.push(auxMri)
                }
            //}
        });

        indicators.secondLine = [{
            staticTitle: this.followupLabels.get("secondaryStrategicLine"),
            schema: "indicadores",
            name: "",
            data: secondData,
        }]

        indicators.resultsIndicators = [{
            staticTitle: this.followupLabels.get("outcomeIndicators"),
            schema: "indicadores",
            name: "",
            data: resultsData,
        }]

        indicators.productIndicators = [{
            staticTitle: this.followupLabels.get("productIndicators"),
            schema: "indicadores",
            name: "",
            data: productsData,
        }]

        if(!missingInformation) missingInformation = (!indData?.length || !resultsData?.length || (productsData?.length <= 1))
        if(this.debug) console.log('missingInformation5', missingInformation)

        if(isWrong && this.view.state.dimensionData?.CodigoDimension === Enums.FollowupState.followup){
            this.view.state.setExistError(true)
        }else{
            this.view.state.setInstitutionalIndData(indicators || [])
            if(this.view.state.dimensionData?.CodigoDimension === Enums.FollowupState.planning){
                this.view.state.setInitialValidateIns(missingInformation)
            }
        }
    }

    public getTotalResults(sectionData: any){ 
        let byAct: any[] = [];
        sectionData.forEach((item: any) => {
            const newItem: any = {};
            newItem.result = item.name;
            item.data.forEach((child: any, index: number) => {
                const keys = Object.keys(child);
                keys.forEach((key: string) => {
                    if(!['result', 'resultName', 'id', 'idParent', 'relatedIdParent', 'activity', 'relatedTitleParent'].includes(key)){
                        if(index === 0) newItem[key] = 0;
                        if(key === Enums.FollowupBasicFields.currentBudgetMod){
                            newItem.currentBudgetMod += (child.currentBudgetMod || 0)
                        }else if(key === Enums.FollowupBasicFields.totalBudgetMod){
                            newItem.totalBudgetMod += (child.totalBudgetMod || 0)
                        }else newItem[key] += child[key]; 
                    }                                       
                })
                newItem.idParent = child.idParent
                newItem.relatedIdParent = child.relatedIdParent
                newItem.relatedTitleParent = child.relatedTitleParent
                newItem.totalMonthPl = this.calTotalMonthPl(newItem)
                newItem.executedBudget = this.sumRealMonths(newItem)
                newItem.accumBudgetExecuted = this.calTotalExecutedBudget(newItem)
                newItem.yearPercentage = this.calYearPercentage(newItem)
                newItem.projectPercentage = this.calProjectPercentage(newItem)
            })
            byAct.push(newItem);
        });
        return byAct
    }
    
    public getFinancialResume(sectionData: any){ 
        let resume: any[] = []
        let byAct: any[] = [];
        let byImplementer = sectionData?.implementers[0]?.data || []
        let byLocation = sectionData?.locations[0]?.data || []
        let byAdt = sectionData?.adts[0]?.data || []
        let bySdc = sectionData?.sdcs[0]?.data || []

        byAct = this.getTotalResults(sectionData?.results)

        let data = [byAct, byImplementer, byLocation, byAdt, bySdc]
        let dataText = [this.followupLabels.get("financialForActivity"), this.followupLabels.get("financialForImplement"), this.followupLabels.get("financialForLocation"), this.followupLabels.get("financialForADT"), this.followupLabels.get("financialForOriginFunds")]

        data.forEach((step, i) => {
            let stepObj = {
                budgetType: dataText[i],
                budgetTypeId: i,
                yearBudget: 0,
                yearBudgetMod: 0,
                yearBudgetExecuted: 0
            }
            for (const item of step) {
                stepObj.yearBudget += (item?.currentBudget || 0)
                stepObj.yearBudgetMod += (item?.currentBudgetMod || 0)
                stepObj.yearBudgetExecuted += this.sumRealMonths(item)
            }
            resume.push(stepObj)
        });

        let resumeObj = {
            staticTitle: this.followupLabels.get("financialBudgetResumeTitle"),
            section: "resume",
            name:  this.followupLabels.get("financialBudgetResumeTitle"),
            data: resume,
        }

        return resumeObj
    }

    public getBeneficiariesByLocationData(stepData: any, year: string){
        let locations: any[] = []

        for (const step of stepData.step3.zones) {
            let locYear = step.details.find((x: any) => x.PPresAnioMeta === Number(year))
            if(!Utilities.isNullObject(locYear?.PProaAnioValue)){
                let dataSaved = this.beneficiariesData?.benByLocation.find((x: any) => x.PProaLocationId === step.genericInfo.PProaLocation.Id)
                let newLocation = {
                    fieldDescriptionId: step.genericInfo.PProaLocation.Id,
                    fieldDescription: step.genericInfo.PProaLocation?.Title || '',
                    fieldMen: dataSaved?.PFFRRTotalHombres,
                    fieldWomen: dataSaved?.PFRRTotalMujeres,
                    fieldTotal: dataSaved?.PFFRRTotal,
                    relatedId: dataSaved?.Id,
                }
                locations.push(newLocation)
            }
        }

        let results = {
            staticTitle: this.followupLabels.get("beneficiariesByLocation"),
            data: locations,
        }
        return results
    }

    public getBeneficiariesByRuralityData(){
        let ruralitiesArr = this.formMasters?.ruralities || []
        let ruralities: any[] = []

        for (const el of ruralitiesArr) {
            let dataSaved = this.beneficiariesData?.benByRurality.find((x: any) => x.RuralityId === el.Id)
            let newRurality = {
                fieldDescriptionId: el.Id,
                fieldDescription: el.Title,
                fieldMen: dataSaved?.PFFRRTotalHombres,
                fieldWomen: dataSaved?.PFRRTotalMujeres,
                fieldTotal: dataSaved?.PFFRRTotal,
                relatedId: dataSaved?.Id,
            }
            ruralities.push(newRurality)
        }

        let results = {
            staticTitle: this.followupLabels.get("beneficiariesByRurality"),
            data: ruralities,
        }
        return results
    }

    public getBeneficiariesByAgeData(){
        let ageRange = this.formMasters?.ageRange || []
        let ranges: any[] = []

        for (const el of ageRange) {
            let dataSaved = this.beneficiariesData?.benByAge.find((x: any) => x.AgeRangeId === el.Id)
            let newRange = {
                fieldDescriptionId: el.Id,
                fieldDescription: el.Title,
                fieldMen: dataSaved?.PFFRRTotalHombres,
                fieldWomen: dataSaved?.PFRRTotalMujeres,
                fieldTotal: dataSaved?.PFFRRTotal,
                relatedId: dataSaved?.Id,
            }
            ranges.push(newRange)
        }

        let results = {
            staticTitle: this.followupLabels.get("beneficiariesByAge"),
            data: ranges,
        }
        return results
    }

    public async getBeneficiariesDissagregateData(totalData: any, currentData: any, checkData?: any){ 
        let benAchieved = totalData?.data?.find((x: any) => x.isTotalMonth)?.achievedBeneficiaries
        let results = {}

        if(!currentData && !checkData){
            let dataSaved = this.beneficiariesData?.benByGen?.[0]
           
            let dissagregateData: any = [
                {
                    relatedId: dataSaved?.Id,
                    disaggregationDescription: this.followupLabels.get("BeneficiariesDissaggregationTotal"),
                    descriptionValue: benAchieved,
                },
                {
                    disaggregationDescription: this.followupLabels.get("beneficiariesDissaggregationMen"),
                    descriptionValue: dataSaved?.PFFRRTotalHombres,
                    isDissag: true,
                },
                {
                    disaggregationDescription:this.followupLabels.get("beneficiariesDissaggregationWoman"),
                    descriptionValue: dataSaved?.PFRRTotalMujeres,
                }
            ]
            results = {
                staticTitle: this.followupLabels.get("beneficiariesDissaggregationGender"),
                data: dissagregateData,
            }
        }else if(!!checkData){
            let data: any = checkData || []
            if(!!data[0]) data[0].descriptionValue = benAchieved
            results = checkData
        }else{
            let data: any = currentData?.data || []
            if(!!data[0]) data[0].descriptionValue = benAchieved
            results = currentData
        }

        return results
    }

    public checkProjectIsValid(actData: any, totalData: any){ 
        let benPlan = totalData?.find((x: any) => x.isTotalMonth)?.beneficiariesByActivity
        let auxData = actData || []
        let isValid = !Utilities.isNullEmptyObject(auxData[0]?.totalMod) ? (auxData[0]?.totalMod === benPlan) : (auxData[0]?.planningTotal === benPlan)
        
        return isValid
    }

    public getBeneficiariesTotalAuxData(actData: any, totalData: any, currentYear: string){ 
        let benPlan = totalData?.data?.find((x: any) => x.isTotalMonth)?.beneficiariesByActivity
        let benAchieved = totalData?.data?.find((x: any) => x.isTotalMonth)?.achievedBeneficiaries
        let benAccum = totalData?.data?.find((x: any) => x.isTotalMonth)?.accumBeneficiaries 
        let auxData = actData?.data || []

        let isValid = !Utilities.isNullEmptyObject(auxData[0]?.totalMod) ? (auxData[0]?.totalMod === benPlan) : (auxData[0]?.planningTotal === benPlan)

        let percentageAchieved: any = !Utilities.isNullEmptyObject(benAchieved) 
            ? (!Utilities.isNullEmptyObject(auxData[0]?.totalMod) ? ((benAchieved / auxData[0]?.totalMod)* 100)
            : !Utilities.isNullEmptyObject(auxData[0]?.planningTotal) ? ((benAchieved / auxData[0]?.planningTotal)* 100) : 0) : 0
            let percentageProject: any = !Utilities.isNullEmptyObject(benAccum) 
            ? (!Utilities.isNullEmptyObject(auxData[1]?.totalMod) ? ((benAccum / auxData[1]?.totalMod)* 100)
            : !Utilities.isNullEmptyObject(auxData[1]?.planningTotal) ? ((benAccum / auxData[1]?.planningTotal)* 100) : 0) : 0
            
        if(!isFinite(percentageAchieved)) percentageAchieved = '0.00 %';
        else percentageAchieved = `${percentageAchieved?.toFixed(2)} %`
        if(!isFinite(percentageProject)) percentageProject = '0.00 %';
        else percentageProject = `${percentageProject?.toFixed(2)} %`

        let totalAux = [
            {
                description: this.followupLabels.get("beneficiariesTotalAuxValidation"),
                value: isValid ? this.followupLabels.get("beneficiariesTotalAuxValidationValid") : this.followupLabels.get("beneficiariesTotalAuxValidationNotValid"),
            },
            {
                description: this.followupLabels.get("beneficiariesTotalAuxPorcentAdvance")+" " + currentYear,
                value: percentageAchieved,
            },
            {
                description: this.followupLabels.get("beneficiariesTotalAuxPorcentAdvanceProyect"),
                value: percentageProject,
            }
        ]
        let results = {
            id: 456,
            data: totalAux,
        }

        return results
    }

    public async getBeneficiariesTotalsData(resData: any, overData: any){ 
        let monthsEn = Constants.Months.English
        let subTotal: any = { beneficiariesByActivity: null }
        let totalBen: any = { beneficiariesByActivity: null }
        let totalMonth: any = { beneficiariesByActivity: null }
        let totalAccum: any = { beneficiariesByActivity: null }
        let percentAccum: any = { beneficiariesByActivity: null, isPercentage: true }

        monthsEn.forEach((month: string) => {
            subTotal[month + 'Re'] = null
            subTotal[month + 'Pl'] = null
        });
        let keysTotal = Object.keys(subTotal)

        for (const res of resData) {
            let auxData = res?.data || []
            for (const childData of auxData) {
                if(childData?.isSubtracted){
                    for (const ky of keysTotal) {
                        subTotal[ky] = (subTotal?.[ky] || 0) + (childData?.[ky] || 0)
                    }
                    subTotal.previousAchievedBeneficiaries = (subTotal.previousAchievedBeneficiaries || 0) + (childData.previousAchievedBeneficiaries || 0)
                }
            }
        }

        subTotal.totalMonthPl = this.calTotalMonthPl(subTotal)
        subTotal.achievedBeneficiaries = this.sumRealMonths(subTotal)
        subTotal.accumBeneficiaries = this.calAccumBeneficiaries(subTotal) 

        for (const overParent of overData.data) {
            let overlap = overParent?.overlaps || []
            for (const childData of overlap) {
                for (const ky of keysTotal) {
                    totalBen[ky] = (totalBen?.[ky] || 0) + (childData?.[ky] || 0)
                }
                totalBen.previousAchievedBeneficiaries = (totalBen.previousAchievedBeneficiaries || 0) + (childData.previousAchievedBeneficiaries || 0)
            }
        }

        totalBen.totalMonthPl = this.calTotalMonthPl(totalBen)
        totalBen.achievedBeneficiaries = this.sumRealMonths(totalBen)
        totalBen.accumBeneficiaries = this.calAccumBeneficiaries(totalBen) 

        for (const ky of keysTotal) {
            totalMonth[ky] = (subTotal?.[ky] || 0) - (totalBen?.[ky] || 0)
        }

        totalMonth.totalMonthPl = this.calTotalMonthPl(totalMonth)
        totalMonth.achievedBeneficiaries = this.sumRealMonths(totalMonth)
        totalMonth.accumBeneficiaries = (subTotal.accumBeneficiaries || 0) - (totalBen.accumBeneficiaries || 0) 

        totalAccum.beneficiariesByActivity = totalMonth.beneficiariesByActivity

        let accumRe = 0, accumPl =0
        monthsEn.forEach((month: string) => {
            accumRe += (totalMonth[month + 'Re'] || 0)
            accumPl += (totalMonth[month + 'Pl'] || 0)
            totalAccum[month + 'Re'] = accumRe
            totalAccum[month + 'Pl'] = accumPl
        });

        for (const ky of keysTotal) {
            percentAccum[ky] = (totalAccum.beneficiariesByActivity) ? ((totalAccum?.[ky] || 0) / (totalAccum.beneficiariesByActivity)) : 0
        }

        let totals = [
            { activity: this.followupLabels.get("beneficiariesTotalMonthSubtotal"), ...subTotal },
            { activity: this.followupLabels.get("beneficiariesTotalMonthTotalBeneficiaries"), ...totalBen, isTotalBen: true },
            { activity: this.followupLabels.get("beneficiariesTotalMonthTotalNumBeneficiariesMonth"), isTotalMonth: true, ...totalMonth },
            { activity: this.followupLabels.get("beneficiariesTotalMonthTotalNumBeneficiariesAcumulative"), ...totalAccum },
            { activity: this.followupLabels.get("beneficiariesTotalMonthTotalBeneficiariesPorcent"), ...percentAccum },
        ]
        let results = {
            id: 123,

            staticTitle: this.followupLabels.get("beneficiariesTotals"),
            section: "benResult",
            data: totals,
        }

        return results
    }

    public getBeneficiariesOverlapsData(resFramework: any, resData: any, currentData?: any, year?: string){ 
        let monthsSp = Constants.Months.Spanish
        let monthsEn = Constants.Months.English
        let results: any[] = []
        let beneficiariesOverlaps = {}

        if(!currentData){
            for (const resFrame of resFramework) {
                resFrame?.results?.forEach((res: any, i:number) => {
                    let activities = res.activities || []
                    let haveData = null
                    for (const act of activities) {
                        if(!haveData) haveData = act.years.find((x: any) => x.PPresAnioMeta === Number(year))
                    }
                    if(!!haveData){
                        let dataSaved = resData?.find((x: any) => x.id === res.genericInfo.Id )
                        let dataCal = dataSaved?.data?.find((y: any) => y.isSubtracted)
                        let totalMonthPl = this.calTotalMonthPl(dataCal)
                        let achievedBeneficiaries = this.sumRealMonths(dataCal)
                        let accumBeneficiaries = this.calAccumBeneficiaries(dataCal) 
                        let activityPercentage = accumBeneficiaries/totalMonthPl
                        let result: any = {
                            beneficiariesByActivity: dataCal?.beneficiariesByActivity,
                            id: res.genericInfo.Id,
                            idParent: res.genericInfo.PProaFEStep4Id,
                            result: res.genericInfo.PProaResultadoEsperado,
                            accumBeneficiaries,
                            achievedBeneficiaries,
                            totalMonthPl,
                            activityPercentage,
                            section: "benResult",
                        }
                        monthsEn.forEach((month: string, i: number) => {
                            result[month + 'Pl'] = dataCal?.[month + 'Pl'] ?? null
                            result[month + 'Re'] = dataCal?.[month + 'Re'] ?? null
                        });
                        
                        results.push(result)
                    }
                });
                
            }
    
            for (let i = 0; i < results.length; i++) {
                const element = results[i];
                element.overlaps = []
                for (let j = i+1; j < results.length; j++) {
                    let elOver = Utilities.copyObject(results[j])
                    let dataSaved = this.beneficiariesData?.benByOverlap.find((x: any) => x.SegBenefParentId === element.id && x.PProaFEStep4ResultadosEsperadosId === elOver.id)
                    let overObj: any = {
                        section: elOver.section, 
                        id: elOver.id,
                        result: elOver.result,
                        relatedId: dataSaved?.Id,
                        beneficiariesByActivity: dataSaved?.BeneficiariosPlan,
                        SegBenefParentId: dataSaved?.SegBenefParentId || element.id,
                        parentResult: element.result,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        overObj[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                        overObj[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                    });
                    element.overlaps.push(overObj)
                }
            }
            beneficiariesOverlaps = {
                staticTitle: "Superposicion de beneficiarios: ",
                section: "benResult",
                name:  "Superposicion de beneficiarios: ",
                data: results,
            }
        }else{
            for (const it of currentData.data) {
                let dataSaved = resData?.find((x: any) => x.id === it.id )
                let dataCal = dataSaved?.data?.find((y: any) => y.isSubtracted)
                it.achievedBeneficiaries = this.sumRealMonths(dataCal)
                it.totalMonthPl = this.calTotalMonthPl(dataCal)
                it.accumBeneficiaries = this.calAccumBeneficiaries(dataCal) 
                it.beneficiariesByActivity = dataCal?.beneficiariesByActivity
                monthsEn.forEach((month: string, i: number) => {
                    it[month + 'Pl'] = dataCal?.[month + 'Pl'] ?? null
                    it[month + 'Re'] = dataCal?.[month + 'Re'] ?? null
                });
            }
            beneficiariesOverlaps = currentData
        }
        return beneficiariesOverlaps
    }

    public getBeneficiariesResultsData(resFramework: any, beneficiariesData: any, year: number, projectData: any){ 
        let monthsSp = Constants.Months.Spanish
        let monthsEn = Constants.Months.English
        let followupId = Number(this.getParamId())
        let projectId = projectData?.projectId
        let subvCode = Utilities.isNullEmptyObject(projectData?.subvCode) ? null : projectData?.subvCode

        let missedActData: any[] = []

        let results: any[] = []
        for (const resFrame of resFramework) {
            resFrame?.results.forEach((res: any, i:number) => {
                let actData: any = []
                res?.activities.forEach((act: any, k:number) => {
                    let sortYears = Utilities.copyObject(act.years)
                    sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                    let actYear = act.years.find((x: any) => x.PPresAnioMeta === year)

                    let dataSaved = beneficiariesData?.benByResult.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.IndicadorActividadAnioId === actYear?.Id && y.SubProyectoAnioId === followupId))

                    if(!(!!dataSaved) && !!followupId && !!act.genericInfo && !!actYear) {

                        let actResultData = beneficiariesData?.benByResult.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.SubProyectoAnioId === followupId))

                        if(!!actResultData) missedActData.push({
                            'subProyectoAnio': followupId,
                            'indicadorActividad': act.genericInfo.Id,
                            'indicadorActividadAnio': actYear?.Id,
                            'actResultData': actResultData
                        })
                        else console.log('Beneficiarios',
                            {
                                'act' : act.genericInfo,
                                'subProyectoAnio': followupId,
                                'indicadorActividad': act.genericInfo.Id,
                                'indicadorActividadAnio': actYear?.Id,
                                'actResultData': actResultData
                            }
                        )
                    }
                    
                    if(!!actYear && !Utilities.isNullObject(actYear?.PProaAnioValue)){
                        let auxAct: any = {
                            resultName: res.genericInfo.PProaResultadoEsperado,
                            id: act.genericInfo.Id,
                            idYear: actYear.Id,
                            idParent: res.genericInfo.Id,
                            activity: act.genericInfo.PProaActividad,
                            relatedId: dataSaved?.Id,
                            resultId: res.genericInfo.Id,
                            idParentParent: resFrame.genericInfo.Id,
                            toSave: true,

                            beneficiariesByActivity: dataSaved?.BeneficiariosPlan,
                        }
                        monthsSp.forEach((month: string, i: number) => {
                            auxAct[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                            auxAct[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                        });
                        for (let index = 0; index < sortYears.length; index++) {
                            const elYear = sortYears[index];
                            if(elYear.PPresAnioMeta < year){
                                let findYear = beneficiariesData?.benByResult.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.IndicadorActividadAnioId === elYear?.Id))
                                if(!!findYear){
                                    auxAct.previousAchievedBeneficiaries  = (auxAct.previousAchievedBeneficiaries  || 0) + (findYear?.BeneficiariosAlcanzados || 0)
                                }
                            }
                        }
                        actData.push(auxAct)
                    }
                })
                if(actData?.length){
                    let dataSaved2 = beneficiariesData?.benByResult.find((y: any) => (y.SubProyectoAnioId === followupId && y.Title === Enums.FollowupBasicFields.benefitedActivities && y.PProaFEStep4ResultadosEsperadosId === res.genericInfo.Id))
                    let benefitedActivities: any = {
                        resultName: res.genericInfo.PProaResultadoEsperado,
                        relatedId: dataSaved2?.Id,
                        idParent: res.genericInfo.Id,
                        beneficiariesByActivity: dataSaved2?.BeneficiariosPlan
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        benefitedActivities[monthsEn[i] + 'Pl'] = dataSaved2?.[month + 'Plan'] ?? null
                        benefitedActivities[monthsEn[i] + 'Re'] = dataSaved2?.[month + 'Real'] ?? null
                    });
                    let calData: any = {
                        beneficiariesByActivity: null,
                    }
                    let totalData: any = {}
                    monthsEn.forEach((month: string, i: number) => {
                        calData[month + 'Re'] = null
                        calData[month + 'Pl'] = null
                    });
                    let oldData = beneficiariesData?.benByResult.filter((y: any) => (y.SubProyectoAnio?.IdProyecto === projectId && y.SubProyectoAnio?.CodigoSubvencion === subvCode && y.Title === Enums.FollowupBasicFields.benefitedActivities && Number(y.SubProyectoAnio?.Anio) < year && y.PProaFEStep4ResultadosEsperadosId === res.genericInfo.Id))
                    for (let index = 0; index < oldData.length; index++) {
                        const element = oldData[index];
                        let auxLastYear: any = {}
                        monthsSp.forEach((month: string, i: number) => {
                            auxLastYear[monthsEn[i] + 'Re'] = element?.[month + 'Real'] ?? null
                        });
                        benefitedActivities.previousAchievedBeneficiaries  = (benefitedActivities.previousAchievedBeneficiaries  || 0) + this.sumRealMonths(auxLastYear)
                    }
                    benefitedActivities.achievedBeneficiaries = this.sumRealMonths(benefitedActivities)
                    benefitedActivities.accumBeneficiaries = this.calAccumBeneficiaries(benefitedActivities)
                    let keys = Object.keys(calData) || []
                    for (const act of actData) {
                        let achievedBeneficiaries = this.sumRealMonths(act)
                        calData.achievedBeneficiaries = (calData.achievedBeneficiaries || 0) + (achievedBeneficiaries || 0)
                        calData.previousAchievedBeneficiaries = (calData.previousAchievedBeneficiaries || 0) + (act.previousAchievedBeneficiaries || 0)
                        for (const ky of keys) {
                            calData[ky] = (calData[ky] || 0) + (act[ky] || 0)
                        }
                    }

                    for (const ky of keys) {
                        totalData[ky] = (calData[ky] || 0) - (benefitedActivities[ky] || 0)
                    }
                    totalData.achievedBeneficiaries = (calData.achievedBeneficiaries || 0) - (benefitedActivities.achievedBeneficiaries || 0)
                    totalData.previousAchievedBeneficiaries = (calData.previousAchievedBeneficiaries || 0) - (benefitedActivities.previousAchievedBeneficiaries || 0)

                    let totals = [{activity: this.followupLabels.get('subTotalBeneficiariesResult'), isCalculated: true, resultId: res.genericInfo.Id, ...calData }]

                    if(actData.length > 1){
                        totals.push({activity: this.followupLabels.get('benefitedActivities') + res.genericInfo.PProaResultadoEsperado, isModified: true, toSave: true, ...benefitedActivities})
                    }

                    totals.push({activity: this.followupLabels.get('totalBeneficiariesResult'), isSubtracted: true, resultId: res.genericInfo.Id, ...totalData})
                    actData = [...actData, ...totals]
                }
                results.push({
                    id: res.genericInfo.Id,
                    idParent: res.genericInfo.PProaFEStep4Id,

                    staticTitle: this.followupLabels.get("beneficiariesExpectedResult"),
                    section: "benResult",
                    name: res.genericInfo.PProaResultadoEsperado,
                    data: actData,
                })
                
            });
        }

        this.view.state.setMissedBeneficiariesActData(missedActData)

        return results
    }

    public getByActData(stepData: any, year: string, beneficiariesData: any){
        let byAct: any = null
        let findYear = stepData.step1?.beneficiaries?.find((x: any) => x.PPresuAnio === year)
        if(!!findYear){
            let dataSaved = beneficiariesData?.benBySubPr.find((y: any) => y.SPCBeneficiariosAniosId === findYear?.Id)
            let infoByAct: any = {
                idYear: findYear?.Id,
                description: this.followupLabels.get('subProjectBeneficiaries') + ' ' + year,
                planningMen: findYear?.PFFRRTotalHombres,
                planningWomen: findYear?.PFRRTotalMujeres,
                planningTotal: findYear?.PPresTotalAnio,

                menMod: dataSaved?.TotalHombresModificado,
                womenMod: dataSaved?.TotalMujeresModificado,
                totalMod: dataSaved?.TotalBeneficiarios,
                totalModNv: dataSaved?.TotalBeneficiarios,
                relatedId: dataSaved?.Id
            }

            let totalMen = 0, totalWomen = 0, total = 0
            stepData.step1?.beneficiaries?.forEach((x: any) => {
                totalMen += (x.PFFRRTotalHombres || 0)
                totalWomen += (x.PFRRTotalMujeres || 0)
                total += (x.PPresTotalAnio || 0)
            })

            let dataSaved2 = beneficiariesData?.benBySubPr.find((y: any) => y.Title === 'total')
            let totalByAct: any = {
                description: this.followupLabels.get('subProjectTotalBeneficiaries'),
                planningMen: totalMen,
                planningWomen: totalWomen,
                planningTotal: total,
                title: 'total',
                menMod: dataSaved2?.TotalHombresModificado,
                womenMod: dataSaved2?.TotalMujeresModificado,
                totalMod: dataSaved2?.TotalBeneficiarios,
                totalModNv: dataSaved2?.TotalBeneficiarios,
                relatedId: dataSaved2?.Id
            }

            byAct = {
                staticTitle: this.followupLabels.get('beneficiariesPlanSubprojectTitle'),
                section: "beneficiaries",
                name: this.followupLabels.get('beneficiariesPlanSubprojectTitle'),
                data: [infoByAct, totalByAct],
            }
        }else{
            byAct = {
                staticTitle: this.followupLabels.get('beneficiariesPlanSubprojectTitle'),
                section: "beneficiaries",
                name: this.followupLabels.get('beneficiariesPlanSubprojectTitle'),
                data: [],
            }
        }

        return byAct;
    }

    /* public async fixMissedActFinancialData(data: any) {

        let currUser = this.currentUser.currentUser.userPrincipalName

        for (const item of data){
            let actResultData: any = {...item?.actResultData}
            try {
                actResultData.IndicadorActividadAnioId = item.indicadorActividadAnio
                await this.followupService.fixActFinancialData(Enums.ListTypes.FinancieraResultadosEsperados, actResultData, currUser)
            } catch(error) {
                console.log(error)
            }
        }

    } */

    public async getFinancialData(resFramework: any, stepData: any, year: number){

        let followupId = Number(this.getParamId())
        let results: any[] = []
        let implementers: any[] = []
        let locations: any[] = []
        let adts: any[] = []
        let sdcs: any[] = []
        let monthsSp = Constants.Months.Spanish
        let monthsEn = Constants.Months.English
        let missingInfo: boolean = false

        let missedActData: any[] = []

        for (const resFrame of resFramework) {
            resFrame.results.forEach((res: any, i:number) => {
                let dataSavedRes = this.financialData?.finTotalsResults.find((y: any) => (y.PProaFEStep4ResultadosEsperadosId === res.genericInfo.Id && Number(y.Title) === year))
                let actData: any = []
                res.activities.forEach((act: any, k:number) => {
                    let sortYears = Utilities.copyObject(act.years)
                    sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                    let actYear = act.years.find((x: any) => x.PPresAnioMeta === year)
                    let dataSaved = this.financialData?.finResults.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.IndicadorActividadAnioId === actYear?.Id && y.SubProyectoAnioId === followupId))

                    if(!(!!dataSaved) && !!followupId && !!act.genericInfo && !!actYear) {

                        let actResultData = this.financialData?.finResults.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.SubProyectoAnioId === followupId))
                       
                        if(!!actResultData) missedActData.push({
                            'subProyectoAnio': followupId,
                            'indicadorActividad': act.genericInfo.Id,
                            'indicadorActividadAnio': actYear?.Id,
                            'actResultData': actResultData
                        })
                        else console.log('Financiera',
                            {
                                'act' : act.genericInfo,
                                'subProyectoAnio': followupId,
                                'indicadorActividad': act.genericInfo.Id,
                                'indicadorActividadAnio': actYear?.Id,
                                'actResultData': actResultData
                            }
                        )
                    }

                    if(!Utilities.isNullObject(actYear) && !Utilities.isNullObject(actYear?.PProaAnioValue)){
                        let auxAct: any = {
                            resultName: res.genericInfo?.PProaResultadoEsperado,
                            id: act.genericInfo.Id,
                            idYear: actYear.Id,
                            idParent: res.genericInfo.Id,
                            activity: act.genericInfo.PProaActividad,
                            idParentParent: resFrame.genericInfo.Id,
                            relatedId: dataSaved?.Id,
                            relatedIdParent: dataSavedRes?.Id,
                            relatedTitleParent: dataSavedRes?.Title || year?.toString(),
    
                            currentBudget: actYear?.PProaAnioValue,
                            currentBudgetMod: dataSaved?.PresupuestoAnioMod ?? null,
                            totalBudget: act.genericInfo.PProaValorActividad,
                            totalBudgetMod: dataSaved?.PresupuestoTotalAnioMod ?? null,
                            previousExecutedBudget: 0
                        }
                        monthsSp.forEach((month: string, i: number) => {
                            auxAct[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                            auxAct[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                        });
                        for (let index = 0; index < sortYears.length; index++) {
                            const elYear = sortYears[index];
                            if(elYear.PPresAnioMeta < year){
                                let findYear = this.financialData?.finResults.find((y: any) => (y.IndicadorActividadId === act.genericInfo.Id && y.IndicadorActividadAnioId === elYear?.Id))
        
                                if(!!findYear){
                                    auxAct.previousExecutedBudget = (auxAct.previousExecutedBudget || 0) + (findYear?.PProaEjecutadoAnio || 0)
                                }
                            }
                        }

                        //if(!missingInfo) missingInfo = (Utilities.isNullObject(auxAct?.currentBudget) || Utilities.isNullObject(auxAct?.totalBudget))
                        //if(this.debug) console.log('__missingInfo1', missingInfo)

                        actData.push(auxAct)
                    }
                })


                //if(!missingInfo) missingInfo = !actData.length
                //if(this.debug) console.log('__missingInfo2', missingInfo)

                results.push({
                    id: res.genericInfo?.Id,
                    idParent: res.genericInfo?.PProaFEStep4Id,

                    staticTitle: this.followupLabels.get("financialActivityExpectedResultTitle"),
                    section: "actResult",
                    name: res.genericInfo?.PProaResultadoEsperado,
                    data: actData,
                })
            });
        }

        this.view.state.setMissedFinancialActData(missedActData)

        let impData: any = []
        let adtData: any = []
        let locData: any = []
        let sdcData: any = []
        if (stepData.step2){
            for (const step of stepData.step2.genericInfo) {
                let sortYears = Utilities.copyObject(step.implementerYears)
                sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                let impYear = step.implementerYears.find((x: any) => x.PPresAnioMeta === year)
                let dataSaved = this.financialData?.finImplementers.find((y: any) => (y.PProaFRStep2Id === step.basicInfo.Id && y.PProaFRStep2AniosId === impYear?.Id && y.SubProyectoAnioId === followupId))

                if(!Utilities.isNullObject(impYear)){
                    let auxImp: any = {
                        id: step.basicInfo.Id,
                        idYear: impYear.Id,
                        idParent: 1,
                        implementer: step.basicInfo.OCTAVIASLEP?.Title || '',
                        relatedId: dataSaved?.Id,
        
                        currentBudget: impYear?.PProaAnioValue,
                        currentBudgetMod: dataSaved?.PresupuestoAnioMod,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxImp[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                        auxImp[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                    });
                    for (let index = 0; index < sortYears.length; index++) {
                        const elYear = sortYears[index];
                        if(elYear.PPresAnioMeta < year){
                            let findYear = this.financialData?.finImplementers.find((y: any) => (y.PProaFRStep2Id === step.basicInfo.Id && y.PProaFRStep2AniosId === elYear?.Id))

                            if(!!findYear){
                               auxImp.previousExecutedBudget = (auxImp.previousExecutedBudget || 0) + (findYear?.PProaEjecutadoAnio || 0)
                            }
                        }
                    }

                    if(!missingInfo) missingInfo = Utilities.isNullObject(auxImp?.currentBudget)
                    if(this.debug) console.log('__missingInfo3', missingInfo)

                    impData.push(auxImp)
                }
            }
        }

        if(!missingInfo) missingInfo = !impData.length
        if(this.debug) console.log('__missingInfo4', missingInfo)

        implementers.push({
            id: 0,
            staticTitle: this.followupLabels.get("financialBudgetByImplementersTitle"),
            section: "implementer",
            name: this.followupLabels.get("financialBudgetByImplementersTitle"),
            data: impData,
        })

        if (stepData.step3){
            for (const step of stepData.step3.zones) {
                let sortYears = Utilities.copyObject(step.details)
                sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                let locYear = step.details.find((x: any) => x.PPresAnioMeta === year)
                let dataSaved = this.financialData?.finLocations.find((y: any) => (y.PProaFRStep3Id === step.genericInfo.Id && y.PProaFRStep3AniosId === locYear?.Id && y.SubProyectoAnioId === followupId))

                if(!Utilities.isNullObject(locYear)){
                    let auxLoc: any = {
                        id: step.genericInfo.Id,
                        idYear: locYear.Id,
                        idParent: 1,
                        location: step.genericInfo.PProaLocation?.Title || '',
                        relatedId: dataSaved?.Id,
        
                        currentBudget: locYear?.PProaAnioValue,
                        currentBudgetMod: dataSaved?.PresupuestoAnioMod,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxLoc[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                        auxLoc[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                    });
                    for (let index = 0; index < sortYears.length; index++) {
                        const elYear = sortYears[index];
                        if(elYear.PPresAnioMeta < year){
                            let findYear = this.financialData?.finLocations.find((y: any) => (y.PProaFRStep3Id === step.genericInfo.Id && y.PProaFRStep3AniosId === elYear?.Id))

                            if(!!findYear){
                                auxLoc.previousExecutedBudget = (auxLoc.previousExecutedBudget || 0) + (findYear?.PProaEjecutadoAnio || 0)
                            }
                        }
                    }

                    //if(!missingInfo) missingInfo = Utilities.isNullObject(auxLoc?.currentBudget)
                    //if(this.debug) console.log('__missingInfo5', missingInfo)

                    locData.push(auxLoc)
                }
            }
        }

        //if(!missingInfo) missingInfo = !locData.length
        //if(this.debug) console.log('__missingInfo6', missingInfo)

        locations.push({
            id: 1,
            staticTitle: this.followupLabels.get("financialBudgetByLocationsTitle"),
            section: "location",
            name: this.followupLabels.get("financialBudgetByLocationsTitle"),
            data: locData,
        })
        if (stepData.step3){
            for (const step of stepData.step3.adts) {
                let sortYears = Utilities.copyObject(step.details)
                sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                let adtYear = step.details.find((x: any) => x.PPresAnioMeta === year)
                let dataSaved = this.financialData?.finAdts.find((y: any) => (y.PProaFRStep3Id === step.genericInfo.Id && y.PProaFRStep3AniosId === adtYear?.Id && y.SubProyectoAnioId === followupId))

                if(!Utilities.isNullObject(adtYear)){
                    let auxAdt: any = {
                        id: step.genericInfo.Id,
                        idYear: adtYear.Id,
                        idParent: 1,
                        adt: step.genericInfo.PProaAdt?.Title || '',
                        relatedId: dataSaved?.Id,
        
                        currentBudget: adtYear?.PProaAnioValue,
                        currentBudgetMod: dataSaved?.PresupuestoAnioMod,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxAdt[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                        auxAdt[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                    });
                    for (let index = 0; index < sortYears.length; index++) {
                        const elYear = sortYears[index];
                        if(elYear.PPresAnioMeta < year){
                            let findYear = this.financialData?.finAdts.find((y: any) => (y.PProaFRStep3Id === step.genericInfo.Id && y.PProaFRStep3AniosId === elYear?.Id))

                            if(!!findYear){
                                auxAdt.previousExecutedBudget = (auxAdt.previousExecutedBudget || 0) + (findYear?.PProaEjecutadoAnio || 0)
                            }
                        }
                    }
                    adtData.push(auxAdt)
                }
            }
        }
        adts.push({
            id: 2,
            staticTitle: this.followupLabels.get("financialBudgetByAdtTitle"),
            section: "adt",
            name: this.followupLabels.get("financialBudgetByAdtTitle"),
            data: adtData,
        })

        if (stepData.stepSDC) {
            for (const step of stepData.stepSDC.genericInfo) {
                let sortYears = Utilities.copyObject(step.counterpartsYears)
                sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                let sdcYear = step.counterpartsYears.find((x: any) => x.PPresAnioMeta === year)
                let counterpartType = this.formMasters.counterpartType.find((x: any) => x.Id === step.basicInfo.PProaTipoContrapartidaId)?.Title
                let counterpart = (!!step.basicInfo.OCTAVIACodigoFinanciacionId) ? this.formMasters.grantCodes.find((x: any) => x.Id === step.basicInfo.OCTAVIACodigoFinanciacionId)?.Title 
                    : (!!step.basicInfo.OCTAVIAFinanciadorSapId) ? this.formMasters.allFinancers.find((x: any) => x.Id === step.basicInfo.OCTAVIAFinanciadorSapId)?.Title
                    : (!!step.basicInfo.OCTAVIASLEPId) ? this.formMasters.slep.find((x: any) => x.Id === step.basicInfo.OCTAVIASLEPId)?.Title : step.basicInfo.Title
                let dataSaved = this.financialData?.finSdcs.find((y: any) => (y.PProaContrapartidaId === step.basicInfo.Id && y.PProaContrapartidaAnioId === sdcYear?.Id && y.SubProyectoAnioId === followupId))

                if(!Utilities.isNullObject(sdcYear?.PProaAnioValue)){
                    let auxSdc: any = {
                        id: step.basicInfo.Id,
                        idYear: sdcYear.Id,
                        idParent: 1,
                        sdc: counterpartType || '',
                        counterpart: counterpart || '',
                        relatedId: dataSaved?.Id,
        
                        currentBudget: sdcYear?.PProaAnioValue,
                        currentBudgetMod: dataSaved?.PresupuestoAnioMod,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxSdc[monthsEn[i] + 'Pl'] = dataSaved?.[month + 'Plan'] ?? null
                        auxSdc[monthsEn[i] + 'Re'] = dataSaved?.[month + 'Real'] ?? null
                    });
                    for (let index = 0; index < sortYears.length; index++) {
                        const elYear = sortYears[index];
                        if(elYear.PPresAnioMeta < year){
                            let findYear = this.financialData?.finSdcs.find((y: any) => (y.PProaContrapartidaId === step.basicInfo.Id && y.PProaContrapartidaAnioId === elYear?.Id))

                            if(!!findYear){
                                auxSdc.previousExecutedBudget = (auxSdc.previousExecutedBudget || 0) + (findYear?.PProaEjecutadoAnio || 0)
                            }
                        }
                    }
                    sdcData.push(auxSdc)
                }
            }
        }
        sdcs.push({
            id: 3,
            staticTitle: this.followupLabels.get("financialBudgetBySdcTitle"),
            section: "sdc",
            name: this.followupLabels.get("financialBudgetBySdcTitle"),
            data: sdcData,
        })

        let auxRes = this.getFinancialResume({
            results: results || [], 
            implementers: implementers || [],
            locations: locations || [],
            adts: adts || [], 
            sdcs: sdcs || [], 
        })


        if(!missingInfo) missingInfo = auxRes?.data?.length ? (auxRes?.data?.[0]?.yearBudget?.toFixed(2) !== auxRes?.data?.[1]?.yearBudget?.toFixed(2)) : true
        if(this.debug) console.log('__missingInfo7', missingInfo)

        if(this.stepTourFin > 1){
            let auxImp = this.getTourData(8, Enums.FollowupDimensions.financial)
            let auxLoc = this.getTourData(9, Enums.FollowupDimensions.financial)

            implementers[0].data = auxImp
            locations[0].data = auxLoc
            adts[0].data = []
            sdcs[0].data = []
            this.view.state.setData({
                results: this.getTourData(4, Enums.FollowupDimensions.financial) || [], 
                implementers: implementers || [],
                locations: locations || [],
                adts: adts || [], 
                sdcs: sdcs || [],  
            })
        }else{

            this.view.state.setData({
                results: results || [], 
                implementers: implementers || [],
                locations: locations || [],
                adts: adts || [], 
                sdcs: sdcs || [], 
            })
        }

        if(this.view.state?.dimensionData?.CodigoDimension === Enums.FollowupState.planning){
            this.view.state.setInitialValidate(missingInfo)
        }
    }

    /* public async fixMissedActIndicatorsData(data: any) {

        let currUser = this.currentUser.currentUser.userPrincipalName

        for (const item of data){
            let actResultData: any = {...item?.actResultData}
            try {
                actResultData.IndicadorActividadAnioId = item.indicadorActividadAnio
                await this.followupService.fixActIndicatorsData(Enums.ListTypes.SeguimientoActividades, actResultData, currUser)
            } catch(error) {
                console.log(error)
            }
        }

    } */

    public async getIndicatorsData(resFramework: any, year: number){ 
        let followupId = Number(this.getParamId())
        let objetives: any[] = []
        let results: any[] = []
        let activities: any[] = []
        let isWrong: boolean = false
        let missingInformation: boolean = false
        let missingActPlanInformation: boolean = false

        let missedActData: any[] = []

        for (const resFrame of resFramework) {
            let objData: any = []
            resFrame.objetives.forEach((obj: any, i:number) => {
                let measureUnit = this.formMasters.measureUnits.find((x: any) => x.Id === obj.genericInfo.PProaMedidaId)?.Title ?? null
                let plannedGoal = obj.years?.find((x: any) => x.PPresAnioMeta === year)
                let relatedIndicator = this.indicatorsData?.indObjEspecificos.filter((a: any) => a.IndicadorObjetivoId == obj.genericInfo.Id && plannedGoal?.Id === a.IndicadorObjetivoAnioId);
                relatedIndicator = relatedIndicator?.length > 0 ? relatedIndicator[0] : null;

                //if(!Utilities.isNullObject(plannedGoal?.PProaAnioValue)){
                    let auxObj: any = {
                        id: obj.genericInfo.Id,
                        idParent: resFrame.genericInfo.Id,
                        indicator: obj.genericInfo.PProaIndicadorObjetivo,
                        lbValue: obj.genericInfo.PFFRRLineaBase,
                        lbUnity: measureUnit,
                        lbAnio: obj.genericInfo.PProaAnioLineaBase,
                        plannedGoal: plannedGoal?.PProaAnioValue,
                        endGoal: obj.genericInfo.PProaMetaFinanciera,
                        currentProgressPercent: 0,
                        totalProgressPercent: 0,
                        idYear: plannedGoal?.Id,

                        criteria: relatedIndicator?.CriterioExitoId,
                        frecuency: relatedIndicator?.FrecuenciaMedicionId,
                        relatedId: relatedIndicator?.Id,

                        plannedGoalMod: relatedIndicator?.MetaPlaneadaAnioMod,
                        endGoalMod: relatedIndicator?.MetaFinalProyectoMod,
                        actualAchievedGoal: relatedIndicator?.MetaAlcanzada,
                        plannedGoalModNv: relatedIndicator?.MetaPlaneadaAnioMod,
                        endGoalModNv: relatedIndicator?.MetaFinalProyectoMod,
                        actualAchievedGoalNv: relatedIndicator?.MetaAlcanzada,
                    }

                    let sortYears = Utilities.copyObject(obj.years)
                    sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                    for (const actYear of sortYears) {
                        let findYear = this.indicatorsData?.indObjEspecificos.find((a: any) => a.IndicadorObjetivoAnioId == actYear?.Id);
                        if(!!findYear && actYear.PPresAnioMeta === (year-1)){
                            auxObj.achievedGoalAnt = findYear?.MetaAlcanzada
                        }
                    }

                    if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxObj.plannedGoal) || Utilities.isNullEmptyObject(auxObj.endGoal) || 
                        Utilities.isNullEmptyObject(auxObj.lbUnity) || Utilities.isNullEmptyObject(auxObj.lbAnio))
                        if(this.debug) console.log('missingInformation6', missingInformation)

                    objData.push(auxObj)
                //}
            });

            if(!missingInformation) missingInformation = !objData?.length
            if(this.debug) console.log('missingInformation7', missingInformation)

            objetives.push({
                id: resFrame.genericInfo?.Id,
                staticTitle: this.followupLabels.get("specificGoal"),
                schema: "indicadores",
                name: resFrame.genericInfo?.PProaObjetivo,
                data: objData,
            })
            
            resFrame.results.forEach((res: any, i:number) => {
                let resData: any = []
                res.details.forEach((det: any, j:number) => {
                    let measureUnit = this.formMasters.measureUnits.find((x: any) => x.Id === det.genericInfo.PProaMedidaId)?.Title ?? null
                    let plannedGoal = det.years?.find((x: any) => x.PPresAnioMeta === year)
                    let relatedIndicator = this.indicatorsData?.indResEsperados.filter((a: any) => a.IndicadorResultadoEsperadoId == det.genericInfo.Id && plannedGoal?.Id === a.IndicadorResultadoEsperadoAnioId);
                    relatedIndicator = relatedIndicator?.length > 0 ? relatedIndicator[0] : null;

                    //if(!Utilities.isNullObject(plannedGoal?.PProaAnioValue)){
                        let auxRes: any = {
                            id: det.genericInfo.Id,
                            idParent: res.genericInfo.Id,
                            idParentParent: resFrame.genericInfo.Id,
                            indicator: det.genericInfo.PProaIndicadorResultadoEsperado,
                            lbValue: det.genericInfo.PFFRRLineaBase,
                            lbUnity: measureUnit,
                            lbAnio: det.genericInfo.PProaAnioLineaBase,
                            plannedGoal: plannedGoal?.PProaAnioValue,
                            endGoal: det.genericInfo.PProaMetaFinanciera,
                            currentProgressPercent: 0,
                            totalProgressPercent: 0,
                            idYear: plannedGoal?.Id,
                            
                            criteria: relatedIndicator?.CriterioExitoId,
                            frecuency: relatedIndicator?.FrecuenciaMedicionId,
                            relatedId: relatedIndicator?.Id,

                            plannedGoalMod: relatedIndicator?.MetaPlaneadaAnioMod,
                            endGoalMod: relatedIndicator?.MetaFinalProyectoMod,
                            actualAchievedGoal: relatedIndicator?.MetaAlcanzada,
                            plannedGoalModNv: relatedIndicator?.MetaPlaneadaAnioMod,
                            endGoalModNv: relatedIndicator?.MetaFinalProyectoMod,
                            actualAchievedGoalNv: relatedIndicator?.MetaAlcanzada,
                        }
                        let sortYears = Utilities.copyObject(det.years)
                        sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));

                        for (const actYear of sortYears) {
                            let findYear = this.indicatorsData?.indResEsperados.find((a: any) => a.IndicadorResultadoEsperadoAnioId == actYear?.Id);
                            if(!!findYear && actYear.PPresAnioMeta === (year-1)){
                                auxRes.achievedGoalAnt = findYear?.MetaAlcanzada
                            }
                        }

                        if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxRes.plannedGoal) || Utilities.isNullEmptyObject(auxRes.endGoal) || 
                            Utilities.isNullEmptyObject(auxRes.lbUnity) || Utilities.isNullEmptyObject(auxRes.lbAnio))
                            if(this.debug) console.log('missingInformation8', missingInformation)

                        resData.push(auxRes)
                    //}
                })

                if(!missingInformation) missingInformation = !resData?.length
                if(this.debug) console.log('missingInformation9', missingInformation)

                results.push({
                    id: res.genericInfo?.Id,
                    idParent: res.genericInfo?.PProaFEStep4Id,
                    staticTitle: this.followupLabels.get("indicatorsExpectedResultTitle"),
                    schema: "indicadores",
                    name: res.genericInfo?.PProaResultadoEsperado,
                    data: resData,
                })

                let actData: any = []

                res.activities.forEach((act: any, k:number) => {
                                        let missedPlan = false
                    let sortYears = Utilities.copyObject(act.years)
                    sortYears = sortYears.sort((a: any, b: any) => ((a.PPresAnioMeta < b.PPresAnioMeta) ? -1 : ((a.PPresAnioMeta > b.PPresAnioMeta) ? 1 : 0)));
                    let measureUnit = this.formMasters.measureUnits.find((x: any) => x.Id === act.genericInfo.PProaMedidaId)?.Title ?? null
                    let plannedGoal = act.years?.find((x: any) => x.PPresAnioMeta === year)
                    let relatedIndicator = this.indicatorsData?.indActividades.find((a: any) => a.IndicadorActividadId == act.genericInfo.Id && plannedGoal?.Id === a.IndicadorActividadAnioId && a.SubProyectoAnioId === followupId);

                    if(!(!!relatedIndicator) && !!followupId && !!act.genericInfo && !!plannedGoal) {

                        let actResultData = this.indicatorsData?.indActividades.find((a: any) => (a.IndicadorActividadId == act.genericInfo.Id && a.SubProyectoAnioId === followupId))
                       
                        if(!!actResultData) {
                            missedActData.push({
                                'subProyectoAnio': followupId,
                                'indicadorActividad': act.genericInfo.Id,
                                'indicadorActividadAnio': plannedGoal?.Id,
                                'actResultData': actResultData
                            })
                        }else {
                            missedPlan = true
                            console.log('Indicadores',
                                {
                                    'act' : act.genericInfo,
                                    'subProyectoAnio': followupId,
                                    'indicadorActividad': act.genericInfo.Id,
                                    'indicadorActividadAnio': plannedGoal?.Id,
                                    'actResultData': actResultData
                                }
                            )

                        }
                    }


                    if(!Utilities.isNullObject(plannedGoal?.PFFRRSubTotal)){
                        let auxAct: any = {
                            id: act.genericInfo.Id,
                            idParent: res.genericInfo.Id,
                            idParentParent: resFrame.genericInfo.Id,
                            indicator: act.genericInfo.PProaActividad,
                            lbUnity: measureUnit,
                            plannedGoal: plannedGoal?.PFFRRSubTotal,
                            endGoal: act.genericInfo.PProaMetaTotalProyecto,
                            currentProgressPercent: 0,
                            totalProgressPercent: 0,
                            idYear: plannedGoal?.Id,

                            januaryPl: relatedIndicator?.EneroPlan,
                            februaryPl: relatedIndicator?.FebreroPlan,
                            marchPl: relatedIndicator?.MarzoPlan,
                            aprilPl: relatedIndicator?.AbrilPlan,
                            mayPl: relatedIndicator?.MayoPlan,
                            junePl: relatedIndicator?.JunioPlan,
                            julyPl: relatedIndicator?.JulioPlan,
                            augustPl: relatedIndicator?.AgostoPlan,
                            septemberPl: relatedIndicator?.SeptiembrePlan,
                            octoberPl: relatedIndicator?.OctubrePlan,
                            novemberPl: relatedIndicator?.NoviembrePlan,
                            decemberPl: relatedIndicator?.DiciembrePlan,

                            januaryRe: relatedIndicator?.EneroReal,
                            februaryRe: relatedIndicator?.FebreroReal,
                            marchRe: relatedIndicator?.MarzoReal,
                            aprilRe: relatedIndicator?.AbrilReal,
                            mayRe: relatedIndicator?.MayoReal,
                            juneRe: relatedIndicator?.JunioReal,
                            julyRe: relatedIndicator?.JulioReal,
                            augustRe: relatedIndicator?.AgostoReal,
                            septemberRe: relatedIndicator?.SeptiembreReal,
                            octoberRe: relatedIndicator?.OctubreReal,
                            novemberRe: relatedIndicator?.NoviembreReal,
                            decemberRe: relatedIndicator?.DiciembreReal,
                            
                            frecuency: relatedIndicator?.FrecuenciaMedicionId ?? null,
                            plannedGoalMod: relatedIndicator?.MetaPlaneadaAnioMod,
                            endGoalMod: relatedIndicator?.MetaFinalProyectoMod,
                            plannedGoalModNv: relatedIndicator?.MetaPlaneadaAnioMod,
                            endGoalModNv: relatedIndicator?.MetaFinalProyectoMod,

                            actualActAchievedGoal: relatedIndicator?.MetaAlcanzada,
                            relatedId: relatedIndicator?.Id,

                            previousAchievements: null
                        }

                        for (let index = 0; index < sortYears.length; index++) {
                            const actYear = sortYears[index];
                            let findYear = this.indicatorsData?.indActividades.find((a: any) => a.IndicadorActividadAnioId == actYear.Id);
                            if(!!findYear){
                                let auxLastYear: any = {
                                    januaryRe: findYear?.EneroReal,
                                    februaryRe: findYear?.FebreroReal,
                                    marchRe: findYear?.MarzoReal,
                                    aprilRe: findYear?.AbrilReal,
                                    mayRe: findYear?.MayoReal,
                                    juneRe: findYear?.JunioReal,
                                    julyRe: findYear?.JulioReal,
                                    augustRe: findYear?.AgostoReal,
                                    septemberRe: findYear?.SeptiembreReal,
                                    octoberRe: findYear?.OctubreReal,
                                    novemberRe: findYear?.NoviembreReal,
                                    decemberRe: findYear?.DiciembreReal
                                }
                                if(actYear.PPresAnioMeta < year){
                                    auxAct.previousAchievements = (auxAct.previousAchievements || 0) + (findYear?.MetaAlcanzada ? findYear.MetaAlcanzada : this.sumRealMonths(auxLastYear))
                                }
                                if(actYear.PPresAnioMeta === (year-1)){
                                    auxLastYear.achievedGoalAnt = null
                                    let yearLast = index -1
                                    while(sortYears[yearLast]){
                                        let findLastYear = this.indicatorsData?.indActividades.find((a: any) => a.IndicadorActividadAnioId == sortYears[yearLast].Id);
                                        if(!findLastYear){
                                            let objLastYear = {
                                                januaryRe: findLastYear?.EneroReal,
                                                februaryRe: findLastYear?.FebreroReal,
                                                marchRe: findLastYear?.MarzoReal,
                                                aprilRe: findLastYear?.AbrilReal,
                                                mayRe: findLastYear?.MayoReal,
                                                juneRe: findLastYear?.JunioReal,
                                                julyRe: findLastYear?.JulioReal,
                                                augustRe: findLastYear?.AgostoReal,
                                                septemberRe: findLastYear?.SeptiembreReal,
                                                octoberRe: findLastYear?.OctubreReal,
                                                novemberRe: findLastYear?.NoviembreReal,
                                                decemberRe: findLastYear?.DiciembreReal
                                            }
                                            auxLastYear.achievedGoalAnt = (auxLastYear.achievedGoalAnt || 0) + (findLastYear?.MetaAlcanzada ? findLastYear.MetaAlcanzada : this.sumRealMonths(objLastYear))
        
                                        }
                                        yearLast -= 1
                                    }
                                    auxAct.achievedGoalAnt = (auxAct.achievedGoalAnt || 0) + (findYear?.MetaAlcanzada ? findYear.MetaAlcanzada : this.sumRealMonths(auxLastYear))
                                }
                            }
                        }
                       
                        if(!isWrong){
                            isWrong = !this.checkAchievement(auxAct, year)    
                        }

                        if(this.debug && !this.checkAchievement(auxAct, year)) {
                            console.log('isWrong', isWrong, 'auxAct', auxAct)
                            this.checkAchievement(auxAct, year, true)
                        }


                        // if(!missingActPlanInformation && !missedPlan) missingActPlanInformation = this.checkActPlan(auxAct, this.view.state.dimensionData)

                        /* if(!missingInformation) missingInformation = (Utilities.isNullEmptyObject(auxAct.plannedGoal) || Utilities.isNullEmptyObject(auxAct.endGoal) || 
                            Utilities.isNullEmptyObject(auxAct.lbUnity)) */
                            if(this.debug) console.log('missingInformation10', missingInformation)

                        actData.push(auxAct)
                    }
                })

                // if(!missingInformation) missingInformation = !actData?.length // Change alerts 128

                activities.push({
                    id: i,
                    idParent: res.genericInfo?.Id,
                    staticTitle:this.followupLabels.get("activitiesTitle"),
                    schema: "indicadores-detalle",
                    name: this.followupLabels.get("activitiesTitle"),
                    data: actData,
                })

            });
        }

        this.view.state.setMissedIndicatorsActData(missedActData)


        if(isWrong && this.view.state.dimensionData?.CodigoDimension === Enums.FollowupState.followup){
            this.view.state.setExistError(true)
        }else{
            this.view.state.setActIndicatorData(activities || [])
            this.view.state.setResIndicatorData(results || [])
            this.view.state.setObjIndicatorData(objetives || [])
            if(this.view.state.dimensionData?.CodigoDimension === Enums.FollowupState.planning){
                this.view.state.setInitialValidateFrame(missingInformation)
                //this.view.state.setInitialValidateActPlan(missingActPlanInformation)
            }
        }
    }

    public getReportPermissions(){
        const TEAM_CENTRAL = this.params[Enums.Params.proyectaCentralTeam]
        const ADMIN_GROUP = this.params[Enums.Params.proyectaAdminModule]
        let userGroups: any[] = this?.currentUser?.userGroups?.map((x: any) => x.displayName)

        return (userGroups?.includes(TEAM_CENTRAL) || userGroups?.includes(ADMIN_GROUP))
    }

    public async getNarrativeReportData(currentYear: string){
        let subPrAnio = Number(this.getParamId())
        let currUser = this.currentUser.currentUser.userPrincipalName

        let yearPeriods: any = this.formMasters.inPeriods?.filter((x: any) => x.Anio === currentYear) || []

        if(yearPeriods?.length){
            let reports = []
            let newReports = []
            let periods = []
            let newPeriods = []
            for (let i = 0; i < yearPeriods.length; i++) {
                const yp = yearPeriods[i];
                let dataSaved = this.subProjectPeriods.find((x: any) => x.SubProyectoAnioId === subPrAnio && x.SeguimientoPeriodosId === yp.Id)
                
                if(dataSaved) periods.push(dataSaved)
                else{
                    this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.process)?.Id?.toString()
                    newPeriods.push({
                        EstadoPeriodo: this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.suspended)?.Id?.toString(),
                        DimensionId: this.formMasters.followupDimensions.find((x: any) => x.OCTAVIACodigo === Enums.FollowupDimensions.narrativeReport)?.Id, 
                        SubProyectoAnioId: subPrAnio,
                        SeguimientoPeriodosId: yp.Id
                    })
                }
            }
                        
            newPeriods = await Promise.all([
                ...newPeriods.map((newP: any) => this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, newP))
            ]);
            periods = [...periods, ...newPeriods]

            for (const per of periods) {
                let dataSaved = this.narrativeReport.followupReport.find((y: any) => y.ReportePeriodoId === per.Id)
                
                if(dataSaved) reports.push(dataSaved)
                else{
                    this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.process)?.Id?.toString()
                    newReports.push({
                        SubProyectoAnioId: subPrAnio,
                        ReportePeriodoId: per.Id
                    })
                }
            }

            newReports = await Promise.all([
                ...newReports.map((newR: any) => this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoInformeNarrativo, newR, currUser))
            ]);
            reports = [...reports, ...newReports]
            
            let actualPeriod: any = null

            let auxPeriods = Utilities.copyObject(periods)
            for (const period of auxPeriods) {
                let statePer = this.formMasters.followupState.find((x: any) => x.Id === Number(period.EstadoPeriodo))
                period.EstadoPeriodo = statePer?.Title
                period.EstadoPeriodoCod = statePer?.OCTAVIACodigo
                if(period.EstadoPeriodoCod !== Enums.FollowupState.approved && !actualPeriod){
                    if(period.EstadoPeriodoCod === Enums.FollowupState.suspended){
                        let stateProcess = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.process)
                        period.EstadoPeriodo = stateProcess?.Id?.toString()
                        delete period.EstadoPeriodoCod
                        actualPeriod = await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, period)
                        actualPeriod.EstadoPeriodo = stateProcess?.Title
                        actualPeriod.EstadoPeriodoCod = stateProcess?.OCTAVIACodigo   
                    }else{
                        actualPeriod = period
                    }
                }
            };

            if(!actualPeriod) actualPeriod = auxPeriods[auxPeriods.length - 1]
            let actualReport = reports?.find((report: any) => Number(report.ReportePeriodoId) === Number(actualPeriod?.Id))
            let dataReport = yearPeriods.find((x: any) => x.Id === actualPeriod.SeguimientoPeriodosId)
            actualPeriod.OCTAVIAFechaFinalizacion = dataReport.OCTAVIAFechaFinalizacion
            actualPeriod.OCTAVIAFechaInicio = dataReport.OCTAVIAFechaInicio
            actualPeriod.ProximoCierre = this.getMonthName(moment.utc(new Date(dataReport.OCTAVIAFechaFinalizacion)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
            actualPeriod.UltimoCierre = this.getMonthName(moment.utc(new Date(dataReport.OCTAVIAFechaInicio)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
            
            this.view.state.setReports(reports)
            this.view.state.setPeriods(auxPeriods)
            this.view.state.setCurrentPeriod(actualPeriod)
            this.view.state.setChkEnded(actualReport?.Finalizado || false)
        }
        this.view.state.setIsCreated(true)
    }

    public async getDimensionData(dimension: string, data?: any){
        let followupId = this.getParamId()
        let subProjectDimensions = Utilities.copyObject(this.subProjectDimensions)
        let dimensionId = this.formMasters.followupDimensions.find((x: any) => x.OCTAVIACodigo === dimension)?.Id
        data = (data || subProjectDimensions?.find((y: any) => y.DimensionId === dimensionId && y.SubProyectoAnioId.toString() === followupId))

        if(!data){
            let year = this.view.state.projectData.year || new Date().getFullYear()
            let Planning = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.planning)?.Id
            let Suspended = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.suspended)?.Id
            let createDimension: any = {
                EstadoDimension: (dimension === Enums.FollowupDimensions.narrativeReport) ? Suspended.toString() : Planning.toString(),
                DimensionId: dimensionId, 
                SubProyectoAnioId: followupId,
            }
            if(dimension !== Enums.FollowupDimensions.narrativeReport){
                let minMonth: number = null
                let defaultMonth: number = 1
                for (const subPr of subProjectDimensions) {
                    if(subPr.SubProyectoAnio?.Anio === year.toString() && subPr.DimensionId === dimensionId){
                        let num = Number(moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
                        if(Utilities.isNullObject(minMonth) || num < minMonth) minMonth = num
                    }
                }
                createDimension.ProximoCierre = (minMonth || defaultMonth) + '/01/' + year
                createDimension.UltimoCierre = '01/01/' + year
            }
            data = await this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, createDimension)
        }

        if(dimension !== Enums.FollowupDimensions.narrativeReport){
            let dimensionState = this.formMasters.followupState.find((z: any) => z.Id?.toString() === data?.EstadoDimension )
            data.EstadoDimension = dimensionState?.Title
            data.CodigoDimension = dimensionState?.OCTAVIACodigo
            data.FechaInicio = data.UltimoCierre
            data.FechaFin = data.ProximoCierre
            data.UltimoCierreEn = this.getMonthNameEn(moment.utc(new Date(data.UltimoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
            data.ProximoCierreEn = this.getMonthNameEn(moment.utc(new Date(data.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
            data.UltimoCierre = this.getMonthName(moment.utc(new Date(data.UltimoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
            data.ProximoCierre = this.getMonthName(moment.utc(new Date(data.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
        }
        
        this.view.state.setDimensionData(data)
        var date = new Date();
	    var current_time = date.getHours()+":"+date.getMinutes()+":"+ date.getSeconds();
        localStorage.setItem("dimensions",JSON.stringify(data));
        localStorage.setItem("hour_dimensions",JSON.stringify(current_time));
    }

    public async saveTable(instance: dxDataGrid, activeAlerts: boolean = true, isClose: boolean = false, auxDataSource: any[] = []){
        if(activeAlerts){
            Utilities.showLoading(this.followupLabels.get("savingMessage"), this.followupLabels.get("loadingMessage"))
        }

        let followupId = this.getParamId()
        let currUser = this.currentUser.currentUser.userPrincipalName
        const year = Number(this.view.state.projectData?.year);
        let dataSource = instance?.getDataSource()?.items();
        let instaceId = instance?.element()?.id;
        let monthsSp = Constants.Months.Spanish
        let monthsEn = Constants.Months.English

        let indData = this.view.state.institutionalIndData
        let objData = this.view.state.objIndicatorData
        let resData = this.view.state.resIndicatorData
        let actData = this.view.state.actIndicatorData
        let finData = this.view.state.sectionData
        let benActData = this.view.state.benActData
        let benResData = this.view.state.benResData
        let benOverData = this.view.state.benOverData
        let dimensionData = this.view.state.dimensionData
        

        //SpecificObjectives
        if (instaceId?.indexOf("-objetive") >= 0){
            let itemsObjetives = [], updateObjetives = [], newItemToLog = [], newItemToVr = []
            if(dataSource){
                for (const pr of dataSource) {
                    let currentProgressPercent = this.calCurrentProgressPercentT1(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT1(pr, year)

                    let objToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        CriterioExitoId: pr.criteria,
                        ObjetivoEspecificoId: pr.idParent,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: pr?.actualAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,
                        IndicadorObjetivoId: pr.id,
                        IndicadorObjetivoAnioId: pr.idYear,
                    }
                    if (!pr.relatedId)
                        itemsObjetives.push(objToSave)
                    else {
                        objToSave.Id = pr.relatedId;
                        updateObjetives.push(objToSave)
                    }

                    if(isClose){
                        
                        newItemToVr.push({
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,  
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: pr.actualAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                        })
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsObjetives, Enums.ListTypes.SeguimientoIndObjEspecificos, currUser),
                this.followupService.updateIndexFollowup(updateObjetives, Enums.ListTypes.SeguimientoIndObjEspecificos, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoIndObjEspecificosVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let objetiveId = added.data.ObjetivoEspecificoId;
                let indicatorId = added.data.IndicadorObjetivoId;
                objData.filter((a:any) => a.id == objetiveId)[0].data.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

            for (const aux of objData) {
                if(aux?.data?.length){
                    for (const toLog of aux.data) {
                        if(toLog.actualAchievedGoal !== toLog.actualAchievedGoalNv || toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                            newItemToLog.push({
                                SubProyectoAnioId: Number(followupId),
                                MetaAlcAnio: toLog.actualAchievedGoalNv,
                                MetaAlcAnio_nv: toLog.actualAchievedGoal,
                                MetaPlanAnioMod: toLog.plannedGoalModNv,
                                MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                                MetaFinal: toLog.endGoalModNv,
                                MetaFinal_nv: toLog.endGoalMod,
                                V_x002d_IdOrigen: toLog.relatedId
                            })
                        }
                    }
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoIndObjEspecificosLogs, currUser)           
            ]);

            for (const aux of objData) {
                if(aux?.data?.length){
                    aux.data.forEach((x: any) => { 
                        x.actualAchievedGoalNv = x.actualAchievedGoal 
                        x.plannedGoalModNv = x.plannedGoalMod 
                        x.endGoalModNv = x.endGoalMod 
                    })
                }
            }
        }
        else if (instaceId?.indexOf("-er-indicator") >= 0){
            //ResultsFramework
            let itemsExpectedResults = [], updateExpectedResults = [], newItemToLog = [], newItemToVr = []

            if (dataSource){
                for (const pr of dataSource) {
                    let currentProgressPercent = this.calCurrentProgressPercentT1(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT1(pr, year)

                    let resToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        ObjetivoEspecificoId: pr.idParentParent,
                        CriterioExitoId: pr.criteria,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: pr?.actualAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,
                        ResultadoEsperadoId: pr.idParent,
                        IndicadorResultadoEsperadoId: pr.id,
                        IndicadorResultadoEsperadoAnioId: pr.idYear
                    }
                    if (!pr.relatedId)
                        itemsExpectedResults.push(resToSave)
                    else {
                        resToSave.Id = pr.relatedId;
                        updateExpectedResults.push(resToSave)
                    }

                    if(isClose){
                        newItemToVr.push({
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: pr.actualAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                        })
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsExpectedResults, Enums.ListTypes.SeguimientoResultadosEsperados, currUser),
                this.followupService.updateIndexFollowup(updateExpectedResults, Enums.ListTypes.SeguimientoResultadosEsperados, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoResultadosEsperadosVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let resultId = added.data.ResultadoEsperadoId;
                let indicatorId = added.data.IndicadorResultadoEsperadoId;
                resData.filter((a:any) => a.id == resultId)[0].data.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

            for (const aux of resData) {
                if(aux?.data?.length){
                    for (const toLog of aux.data) {
                        if(toLog.actualAchievedGoal !== toLog.actualAchievedGoalNv || toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                            newItemToLog.push({
                                SubProyectoAnioId: Number(followupId),
                                MetaAlcAnio: toLog.actualAchievedGoalNv,
                                MetaAlcAnio_nv: toLog.actualAchievedGoal,
                                MetaPlanAnioMod: toLog.plannedGoalModNv,
                                MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                                MetaFinal: toLog.endGoalModNv,
                                MetaFinal_nv: toLog.endGoalMod,
                                V_x002d_IdOrigen: toLog.relatedId
                            })
                        }
                    }
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoResultadosEsperadosLogs, currUser)           
            ]);

            for (const aux of resData) {
                if(aux?.data?.length){
                    aux.data.forEach((x: any) => { 
                        x.actualAchievedGoalNv = x.actualAchievedGoal 
                        x.plannedGoalModNv = x.plannedGoalMod 
                        x.endGoalModNv = x.endGoalMod 
                    })
                }
            }
        }
        else if (instaceId?.indexOf("-er-activities") >= 0){
            //Activities
            let itemsActivities = [], updateActivities = [], newItemToLog = [], newItemToVr = []
            if (dataSource){
                for (const pr of dataSource) {
                    let actualActAchievedGoal = this.calActualActAchievedGoal(pr, year)
                    let plannedAchievement = this.calPlannedAchievement(pr, year)
                    let plannedAchievementMod = this.calPlannedAchievementMod(pr, year)
                    let totalMonthPl = this.calTotalMonthPl(pr)
                    let currentProgressPercent = this.calCurrentProgressPercentT2(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT2(pr, year)
                    let currentAchievementAch = this.sumRealMonths(pr)
                    let totalAchievement = this.calTotalAchievement(pr)

                    let actToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        ObjetivoEspecificoId: pr.idParentParent,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: actualActAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,
                        ResultadoEsperadoId: pr.idParent,
                        IndicadorActividadId: pr.id,
                        IndicadorActividadAnioId: pr.idYear,
                        LogroPlaneadoAnio: plannedAchievement,
                        LogroPlaneadoAnioMod: plannedAchievementMod,
                        LogroAlcanzadoAnio: currentAchievementAch,
                        LogroAcumuladoProyecto: totalAchievement,

                        EneroReal: pr.januaryRe,
                        FebreroReal: pr.februaryRe,
                        MarzoReal: pr.marchRe,
                        AbrilReal: pr.aprilRe,
                        MayoReal: pr.mayRe,
                        JunioReal: pr.juneRe,
                        JulioReal: pr.julyRe,
                        AgostoReal: pr.augustRe,
                        SeptiembreReal: pr.septemberRe,
                        OctubreReal: pr.octoberRe,
                        NoviembreReal: pr.novemberRe,
                        DiciembreReal: pr.decemberRe,

                        EneroPlan: pr.januaryPl,
                        FebreroPlan: pr.februaryPl,
                        MarzoPlan: pr.marchPl,
                        AbrilPlan: pr.aprilPl,
                        MayoPlan: pr.mayPl,
                        JunioPlan: pr.junePl,
                        JulioPlan: pr.julyPl,
                        AgostoPlan: pr.augustPl,
                        SeptiembrePlan: pr.septemberPl,
                        OctubrePlan: pr.octoberPl,
                        NoviembrePlan: pr.novemberPl,
                        DiciembrePlan: pr.decemberPl,
                    }
                    if (!pr.relatedId)
                        itemsActivities.push(actToSave)
                    else {
                        actToSave.Id = pr.relatedId;
                        updateActivities.push(actToSave)
                    }

                    if(isClose){
                        let auxToVr: any = {
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,  
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: actualActAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                            V_x002d_LogroPlaneadoAnio: plannedAchievement,
                            V_x002d_LogroPlaneadoAnioMod: plannedAchievementMod,
                            V_x002d_LogroAlcanzadoAnio: currentAchievementAch,
                            V_x002d_LogrosAcumuladosProyecto: totalAchievement,
                            V_x002d_SumaMesesPlan: totalMonthPl,
                        }
    
                        monthsSp.forEach((month: string, i: number) => {
                            auxToVr[month + 'Plan'] = pr[monthsEn[i] + 'Pl']
                            auxToVr[month + 'Real'] = pr[monthsEn[i] + 'Re']
                        });
                        newItemToVr.push(auxToVr)
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsActivities, Enums.ListTypes.SeguimientoActividades, currUser),
                this.followupService.updateIndexFollowup(updateActivities, Enums.ListTypes.SeguimientoActividades, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoActividadesVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let resultId = added.data.ResultadoEsperadoId;
                let indicatorId = added.data.IndicadorActividadId;
                dataSource.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
                actData.filter((a:any) => a.idParent == resultId)[0].data.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

            for (const aux of actData) {
                if(aux?.data?.length){
                    for (const toLog of aux.data) {
                        if(toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                            newItemToLog.push({
                                SubProyectoAnioId: Number(followupId),
                                MetaPlanAnioMod: toLog.plannedGoalModNv,
                                MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                                MetaFinal: toLog.endGoalModNv,
                                MetaFinal_nv: toLog.endGoalMod,
                                V_x002d_IdOrigen: toLog.relatedId
                            })
                        }
                    }
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoActividadesLogs, currUser)           
            ]);

            for (const aux of actData) {
                if(aux?.data?.length){
                    aux.data.forEach((x: any) => { 
                        x.actualAchievedGoalNv = x.actualAchievedGoal 
                        x.plannedGoalModNv = x.plannedGoalMod 
                        x.endGoalModNv = x.endGoalMod 
                    })
                }
            }
        }
        else if (instaceId?.indexOf("mainIndicator") >= 0){
            //MainIndicator
            let itemsMainInd = [], updateMainInd = [], newItemToLog = [], newItemToVr = []

            if (dataSource){
                for (const pr of dataSource) {
                    let currentProgressPercent = this.calCurrentProgressPercentT1(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT1(pr, year)

                    let indToSave: any = {
                        SubProyectoAnioId: Number(followupId),

                        CriterioExitoId: pr.criteria,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: pr?.actualAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,

                        IndicadorLineaEstrategicaId: pr.id,
                        PPresIndicadorLineaEstrategicaId: pr.idParent,
                        IndicadorLineaEstrategicaAniosId: pr.idYear
                    }
                    if (!pr.relatedId)
                        itemsMainInd.push(indToSave)
                    else {
                        indToSave.Id = pr.relatedId;
                        updateMainInd.push(indToSave)
                    }

                    if(isClose){
                        newItemToVr.push({
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: pr.actualAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                        })
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsMainInd, Enums.ListTypes.SeguimientoLineaPrincipal, currUser),
                this.followupService.updateIndexFollowup(updateMainInd, Enums.ListTypes.SeguimientoLineaPrincipal, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoLineaPrincipalVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PPresIndicadorLineaEstrategicaId;
                dataSource.filter((a:any) => a.idParent == indicatorId)[0].relatedId = createdId
            }

            let auxDataLog = indData?.mainLine?.[0]?.data || []
            for (const toLog of auxDataLog) {
                if(toLog.actualAchievedGoal !== toLog.actualAchievedGoalNv || toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                    newItemToLog.push({
                        SubProyectoAnioId: Number(followupId),
                        MetaAlcAnio: toLog.actualAchievedGoalNv,
                        MetaAlcAnio_nv: toLog.actualAchievedGoal,
                        MetaPlanAnioMod: toLog.plannedGoalModNv,
                        MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                        MetaFinal: toLog.endGoalModNv,
                        MetaFinal_nv: toLog.endGoalMod,
                        V_x002d_IdOrigen: toLog.relatedId
                    })
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoLineaPrincipalLogs, currUser)           
            ]);

            dataSource.forEach((x: any) => { 
                x.actualAchievedGoalNv = x.actualAchievedGoal 
                x.plannedGoalModNv = x.plannedGoalMod 
                x.endGoalModNv = x.endGoalMod 
            })

        }
        else if (instaceId?.indexOf("secondIndicator") >= 0){
            //SecondIndicator
            let itemsSecInd = [], updateSecInd = [], newItemToLog = [], newItemToVr = []

            if (dataSource){
                for (const pr of dataSource) {
                    let currentProgressPercent = this.calCurrentProgressPercentT1(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT1(pr, year)

                    let indToSave: any = {
                        SubProyectoAnioId: Number(followupId),

                        CriterioExitoId: pr.criteria,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: pr?.actualAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,

                        IndicadorLineaEstrategicaSecundaId: pr.id,
                        PPresIndicadorLineaEstrategicaId: pr.idParent,
                        IndLineaEstrategicaAniosSecundarId: pr.idYear
                    }
                    if (!pr.relatedId)
                        itemsSecInd.push(indToSave)
                    else {
                        indToSave.Id = pr.relatedId;
                        updateSecInd.push(indToSave)
                    }

                    if(isClose){
                        newItemToVr.push({
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: pr.actualAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                        })
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsSecInd, Enums.ListTypes.SeguimientoLineaSecundaria, currUser),
                this.followupService.updateIndexFollowup(updateSecInd, Enums.ListTypes.SeguimientoLineaSecundaria, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoLineaSecundariaVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PPresIndicadorLineaEstrategicaId;
                dataSource.filter((a:any) => a.idParent == indicatorId)[0].relatedId = createdId
            }

            let auxDataLog = indData?.secondLine?.[0]?.data || []
            for (const toLog of auxDataLog) {
                if(toLog.actualAchievedGoal !== toLog.actualAchievedGoalNv || toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                    newItemToLog.push({
                        SubProyectoAnioId: Number(followupId),
                        MetaAlcAnio: toLog.actualAchievedGoalNv,
                        MetaAlcAnio_nv: toLog.actualAchievedGoal,
                        MetaPlanAnioMod: toLog.plannedGoalModNv,
                        MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                        MetaFinal: toLog.endGoalModNv,
                        MetaFinal_nv: toLog.endGoalMod,
                        V_x002d_IdOrigen: toLog.relatedId
                    })
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoLineaSecundariaLogs, currUser)           
            ]);

            dataSource.forEach((x: any) => { 
                x.actualAchievedGoalNv = x.actualAchievedGoal 
                x.plannedGoalModNv = x.plannedGoalMod 
                x.endGoalModNv = x.endGoalMod 
            })
        }
        else if (instaceId?.indexOf("resultsIndicators") >= 0){
            //ResultsIndicators
            let itemsResInd = [], updateResInd = [], newItemToLog = [], newItemToVr = []

            if (dataSource){
                for (const pr of dataSource) {
                    let currentProgressPercent = this.calCurrentProgressPercentT1(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT1(pr, year)

                    let indToSave: any = {
                        SubProyectoAnioId: Number(followupId),

                        CriterioExitoId: pr.criteria,
                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: pr?.actualAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,

                        IndicadorInstitucionalResultadosId: pr.id,
                        PFFRRIndicadorResultadoIntermediId: { '__metadata': { type: 'Collection(Edm.Int32)' }, results: [ pr.idParent ] },
                        IndicadorInstitucionalResultados0Id: pr.idYear //IndicadorInstitucionalResultadosAnios
                    }
                    if (!pr.relatedId)
                        itemsResInd.push(indToSave)
                    else {
                        indToSave.Id = pr.relatedId;
                        updateResInd.push(indToSave)
                    }

                    if(isClose){
                        newItemToVr.push({
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: pr.actualAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                        })
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsResInd, Enums.ListTypes.SeguimientoResultadosInstitucionales, currUser),
                this.followupService.updateIndexFollowup(updateResInd, Enums.ListTypes.SeguimientoResultadosInstitucionales, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoResultadosInstitucionalesVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PFFRRIndicadorResultadoIntermediId[0];
                dataSource.filter((a:any) => a.idParent == indicatorId)[0].relatedId = createdId
            }

            let auxDataLog = indData?.resultsIndicators?.[0]?.data || []
            for (const toLog of auxDataLog) {
                
                if(toLog.actualAchievedGoal !== toLog.actualAchievedGoalNv || toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                    newItemToLog.push({
                        SubProyectoAnioId: Number(followupId),
                        MetaAlcAnio: toLog.actualAchievedGoalNv,
                        MetaAlcAnio_nv: toLog.actualAchievedGoal,
                        MetaPlanAnioMod: toLog.plannedGoalModNv,
                        MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                        MetaFinal: toLog.endGoalModNv,
                        MetaFinal_nv: toLog.endGoalMod,
                        V_x002d_IdOrigen: toLog.relatedId
                    })
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoResultadosInstitucionalesLogs, currUser)           
            ]);

            dataSource.forEach((x: any) => { 
                x.actualAchievedGoalNv = x.actualAchievedGoal 
                x.plannedGoalModNv = x.plannedGoalMod 
                x.endGoalModNv = x.endGoalMod 
            })
        }
        else if (instaceId?.indexOf("productIndicators") >= 0){
            //ProductIndicators
            let itemsProInd = [], updateProInd = [], newItemToLog = [], newItemToVr = []

            if (dataSource){
                for (const pr of dataSource) {
                    let actualActAchievedGoal = this.calActualActAchievedGoal(pr, year)
                    let plannedAchievement = this.calPlannedAchievement(pr, year)
                    let plannedAchievementMod = this.calPlannedAchievementMod(pr, year)
                    let totalMonthPl = this.calTotalMonthPl(pr)
                    let currentProgressPercent = this.calCurrentProgressPercentT2(pr, year)
                    let totalProgressPercent = this.calTotalProgressPercentT2(pr, year)
                    let currentAchievementAch = this.sumRealMonths(pr)
                    let totalAchievement = this.calTotalAchievement(pr)

                    let actToSave: any = {
                        SubProyectoAnioId: Number(followupId),

                        FrecuenciaMedicionId: pr.frecuency,
                        MetaPlaneadaAnioMod: pr.plannedGoalMod,
                        MetaFinalProyectoMod: pr.endGoalMod,
                        MetaAlcanzadaAnioAnterior: pr?.achievedGoalAnt,
                        MetaAlcanzada: actualActAchievedGoal,
                        PorcentajeAvanceAnio: currentProgressPercent,
                        PorcentajeAvanceTotalProyecto: totalProgressPercent,
                        LogroPlaneadoAnio: plannedAchievement,
                        LogroPlaneadoAnioMod: plannedAchievementMod,
                        LogroAlcanzadoAnio: currentAchievementAch,
                        LogroAcumuladoProyecto: totalAchievement,
                        
                        IndicadorInstitucionalProductosId: pr.id,
                        PFFRRIndicadorProductoId: pr.idParent,
                        IndicadorInstitucionalProductosAId: pr.idYear, //IndicadorInstitucionalProductosAnios

                        EneroReal: pr.januaryRe,
                        FebreroReal: pr.februaryRe,
                        MarzoReal: pr.marchRe,
                        AbrilReal: pr.aprilRe,
                        MayoReal: pr.mayRe,
                        JunioReal: pr.juneRe,
                        JulioReal: pr.julyRe,
                        AgostoReal: pr.augustRe,
                        SeptiembreReal: pr.septemberRe,
                        OctubreReal: pr.octoberRe,
                        NoviembreReal: pr.novemberRe,
                        DiciembreReal: pr.decemberRe,

                        EneroPlan: pr.januaryPl,
                        FebreroPlan: pr.februaryPl,
                        MarzoPlan: pr.marchPl,
                        AbrilPlan: pr.aprilPl,
                        MayoPlan: pr.mayPl,
                        JunioPlan: pr.junePl,
                        JulioPlan: pr.julyPl,
                        AgostoPlan: pr.augustPl,
                        SeptiembrePlan: pr.septemberPl,
                        OctubrePlan: pr.octoberPl,
                        NoviembrePlan: pr.novemberPl,
                        DiciembrePlan: pr.decemberPl,
                    }
                    if (!pr.relatedId)
                        itemsProInd.push(actToSave)
                    else {
                        actToSave.Id = pr.relatedId;
                        updateProInd.push(actToSave)
                    }

                    if(isClose){
                        let auxToVr: any = {
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_CriterioExito: pr.criteria?.toString(),
                            V_x002d_FrecuenciaMedicion: pr.frecuency?.toString(),
                            V_x002d_IdOrigen: pr.relatedId,
                            V_x002d_Indicador: pr.indicator?.toString(),
                            V_x002d_LineaBaseAnio: pr.lbAnio?.toString(),
                            V_x002d_LineaBaseUnidadMedida: pr.lbUnity?.toString(),
                            V_x002d_LineaBaseValor: pr.lbValue?.toString(),
                            V_x002d_MetaAlcanzada: actualActAchievedGoal?.toString(),
                            V_x002d_MetaAlcanzadaAnioAnterio: pr.achievedGoalAnt?.toString(),
                            V_x002d_MetaFinalProyecto: pr.endGoal?.toString(),
                            V_x002d_MetaFinalProyectoMod: pr.endGoalMod?.toString(),
                            V_x002d_MetaPlaneadaAnio: pr.plannedGoal?.toString(),
                            V_x002d_MetaPlaneadaAnioMod: pr.plannedGoalMod?.toString(),
                            V_x002d_PorcentajeAvanceAnio: currentProgressPercent?.toString(),
                            V_x002d_PorcentajeAvanceTotal: totalProgressPercent?.toString(),
                            V_x002d_LogroPlaneadoAnio: plannedAchievement,
                            V_x002d_LogroPlaneadoAnioMod: plannedAchievementMod,
                            V_x002d_LogroAlcanzadoAnio: currentAchievementAch,
                            V_x002d_LogrosAcumuladosProyecto: totalAchievement,
                            V_x002d_SumaMesesPlan: totalMonthPl,
                        }
    
                        monthsSp.forEach((month: string, i: number) => {
                            auxToVr[month + 'Plan'] = pr[monthsEn[i] + 'Pl']
                            auxToVr[month + 'Real'] = pr[monthsEn[i] + 'Re']
                        });
                        newItemToVr.push(auxToVr)
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(itemsProInd, Enums.ListTypes.SeguimientoProductosInstitucionales, currUser),
                this.followupService.updateIndexFollowup(updateProInd, Enums.ListTypes.SeguimientoProductosInstitucionales, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.SeguimientoProductosInstitucionalesVersion, currUser),
            ])

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PFFRRIndicadorProductoId;
                dataSource.filter((a:any) => a.idParent == indicatorId)[0].relatedId = createdId
            }

            let auxDataLog = indData?.productIndicators?.[0]?.data || []
            for (const toLog of auxDataLog) {
                if(toLog.plannedGoalMod !== toLog.plannedGoalModNv || toLog.endGoalMod !== toLog.endGoalModNv){
                    newItemToLog.push({
                        SubProyectoAnioId: Number(followupId),
                        MetaPlanAnioMod: toLog.plannedGoalModNv,
                        MetaPlanAnioMod_nv: toLog.plannedGoalMod,
                        MetaFinal: toLog.endGoalModNv,
                        MetaFinal_nv: toLog.endGoalMod,
                        V_x002d_IdOrigen: toLog.relatedId
                    })
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.SeguimientoProductosInstitucionalesLogs, currUser)           
            ]);

            dataSource.forEach((x: any) => { 
                x.plannedGoalModNv = x.plannedGoalMod 
                x.endGoalModNv = x.endGoalMod 
            })
        }

        //Beneficiarios
        if(instaceId?.indexOf("beneficiaries-by-activity") >= 0 && dataSource?.length){
            let bySubPrToAdd = [], bySubPrToUpd = [], newItemToLog = [], newItemToVr = []

            for (const bySubPr of dataSource) {
                let bySubPrToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    SPCBeneficiariosAniosId: bySubPr.idYear,
                    TotalHombresModificado: bySubPr.menMod,
                    TotalMujeresModificado: bySubPr.womenMod,
                    TotalBeneficiarios: bySubPr.totalMod,
                    Title: bySubPr.title
                }
                
                if (!bySubPr.relatedId)
                    bySubPrToAdd.push(bySubPrToSave)
                else {
                    bySubPrToSave.Id = bySubPr.relatedId;
                    bySubPrToUpd.push(bySubPrToSave)
                }

                if(isClose){
                    newItemToVr.push({
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        PFFRRTotal: bySubPr.planningTotal,
                        PFFRRTotalHombres: bySubPr.planningMen,
                        PFRRTotalMujeres: bySubPr.planningWomen,
                        TotalBeneficiarios: bySubPr.totalMod,
                        TotalHombresModificado: bySubPr.menMod,
                        TotalMujeresModificado: bySubPr.womenMod,
                        V_x002d_Descripcion: bySubPr.description,
                        V_x002d_IdOrigen: bySubPr?.relatedId,
                    })
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(bySubPrToAdd, Enums.ListTypes.BeneficiariesBySubPr, currUser), 
                this.followupService.updateIndexFollowup(bySubPrToUpd, Enums.ListTypes.BeneficiariesBySubPr, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesBySubPrVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.SPCBeneficiariosAniosId;
                benActData.data.filter((a:any) => a.idYear == indicatorId)[0].relatedId = createdId
            }
            let auxDataLog = benActData.data || []

            for (const toLog of auxDataLog) {
                if(toLog.totalMod !== toLog.totalModNv){
                    if(!!toLog.idYear){
                        newItemToLog.push({
                            SubProyectoAnioId: Number(followupId),
                            BenYearMod: toLog.totalModNv,
                            BenYearMod_nv: toLog.totalMod,
                            V_x002d_IdOrigen: toLog.relatedId
                        })
                    }else{
                        newItemToLog.push({
                            SubProyectoAnioId: Number(followupId),
                            BenTotalMod: toLog.totalModNv,
                            BenTotalMod_nv: toLog.totalMod,
                            V_x002d_IdOrigen: toLog.relatedId
                        })
                    }
                }
            }

            await Promise.all([
                this.followupService.addIndexFollowup(newItemToLog, Enums.ListTypes.BeneficiariesBySubPrLogs, currUser),
            ]);

            benActData.data.forEach((x: any) => { x.totalModNv = x.totalMod })
        }
        else if((instaceId?.indexOf("beneficiaries-by-overlap") >= 0 && dataSource?.length ) || auxDataSource?.length){
            let newDataSource = (auxDataSource?.length) ? auxDataSource : dataSource
            let byOverToAdd = [], byOverToUpd = [], newItemToVr = []
                for (const byOver of newDataSource) {
                    let SumaPlan = this.calTotalMonthPl(byOver)

                    let byOverToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        SumaPlan,
                        BeneficiariosPlan: byOver.beneficiariesByActivity,

                        SegBenefParentId: byOver.SegBenefParentId,
                        PProaFEStep4ResultadosEsperadosId: byOver.id
                    }

                    monthsSp.forEach((month: string, i: number) => {
                        byOverToSave[month + 'Plan'] = byOver[monthsEn[i] + 'Pl']
                        byOverToSave[month + 'Real'] = byOver[monthsEn[i] + 'Re']
                    });
                    
                    if (!byOver.relatedId)
                        byOverToAdd.push(byOverToSave)
                    else {
                        byOverToSave.Id = byOver.relatedId;
                        byOverToUpd.push(byOverToSave)
                    }

                    if(isClose){
                        let auxToVr: any = {
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_BeneficiariosPlan: byOver.beneficiariesByActivity?.toString(),
                            V_x002d_ResultadoEsperado: byOver?.result,
                            V_x002d_IdOrigen: byOver?.relatedId,
                            SumaPlan
                        }
                        monthsSp.forEach((month: string, i: number) => {
                            auxToVr[month + 'Plan'] = byOver[monthsEn[i] + 'Pl']
                            auxToVr[month + 'Real'] = byOver[monthsEn[i] + 'Re']
                        });
                        newItemToVr.push(auxToVr)
                    }
                }

                let returnIdsAll = await Promise.all([
                    this.followupService.addIndexFollowup(byOverToAdd, Enums.ListTypes.BeneficiariesByOverlap, currUser), 
                    this.followupService.updateIndexFollowup(byOverToUpd, Enums.ListTypes.BeneficiariesByOverlap, currUser),
                    this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesByOverlapVersion, currUser),
                ]);

                let returnIds = returnIdsAll[0];

                //Updating relatedIds
                for (const added of returnIds) {
                    let createdId = added.data.Id;
                    let parentId = added.data.SegBenefParentId;
                    let indicatorId = added.data.PProaFEStep4ResultadosEsperadosId;
                    for (const overData of benOverData.data) {
                        let reg = overData.overlaps.filter((a:any) => a.id == indicatorId && a.SegBenefParentId == parentId)
                        if (reg.length > 0) {
                            reg[0].relatedId = createdId
                            break;
                        }
                    }
                }
        }
        else if(instaceId?.indexOf("-expected-result-beneficiaries") >= 0 && dataSource?.length){
            let byResToAdd = [], byResToUpd = [], newItemToVr = []
            for (const byRes of dataSource) {
                if(!!byRes?.toSave){
                    let SumaPlan = this.calTotalMonthPl(byRes)
                    let BeneficiariosAlcanzados = this.sumRealMonths(byRes)
                    let BeneficiariosAcumulados = this.calAccumBeneficiaries(byRes)
                    let PorcentajeAvancePorActividad = this.calActivityPercentage(byRes)

                    let byResToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        IndicadorActividadId: byRes.id,
                        IndicadorActividadAnioId: byRes.idYear,
                        SumaPlan,
                        BeneficiariosAlcanzados,
                        BeneficiariosAcumulados,
                        PorcentajeAvancePorActividad,
                        BeneficiariosPlan: byRes.beneficiariesByActivity,
                        PProaFEStep4ResultadosEsperadosId: byRes.idParent
                    }

                    if(!byRes?.id) byResToSave.Title = Enums.FollowupBasicFields.benefitedActivities

                    monthsSp.forEach((month: string, i: number) => {
                        byResToSave[month + 'Plan'] = byRes[monthsEn[i] + 'Pl']
                        byResToSave[month + 'Real'] = byRes[monthsEn[i] + 'Re']
                    });
                    
                    if (!byRes.relatedId)
                        byResToAdd.push(byResToSave)
                    else {
                        byResToSave.Id = byRes.relatedId;
                        byResToUpd.push(byResToSave)
                    }

                    if(isClose){
                        let auxToVr: any = {
                            OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                            OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                            SubProyectoAnioId: Number(followupId),
                            V_x002d_BeneficiariosAcumulados: BeneficiariosAcumulados,
                            V_x002d_BeneficiariosAlcanzados: BeneficiariosAlcanzados,
                            V_x002d_BeneficiariosPlan: byRes.beneficiariesByActivity?.toString(),
                            V_x002d_PorcentajeAvancePorActiv: PorcentajeAvancePorActividad,
                            V_x002d_ResultadoEsperado: byRes?.resultName,
                            V_x002d_Descripcion: byRes.activity,
                            V_x002d_IdOrigen: byRes?.relatedId,
                            SumaPlan
                        }
                        monthsSp.forEach((month: string, i: number) => {
                            auxToVr[month + 'Plan'] = byRes[monthsEn[i] + 'Pl']
                            auxToVr[month + 'Real'] = byRes[monthsEn[i] + 'Re']
                        });
                        newItemToVr.push(auxToVr)
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(byResToAdd, Enums.ListTypes.BeneficiariesByResult, currUser), 
                this.followupService.updateIndexFollowup(byResToUpd, Enums.ListTypes.BeneficiariesByResult, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesByResultVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let parentId = added.data.PProaFEStep4ResultadosEsperadosId;
                let indicatorId = added.data.IndicadorActividadId;
                let yearId = added.data.IndicadorActividadAnioId;
                benResData.filter((a:any) => a.id == parentId)[0].data.filter((a:any) => a.id == indicatorId && a.idYear == yearId && !a.isCalculated && !a.isSubtracted)[0].relatedId = createdId
            }

        }
        else if(instaceId?.indexOf("beneficiaries-totals-other-table") >= 0 && dataSource?.length){
            let byGenToAdd = null, byGenToUpd = null, newItemToVr = null
            
            let byGenToSave: any = {
                SubProyectoAnioId: Number(followupId),
                PFFRRTotal: dataSource?.[0]?.descriptionValue,
                PFFRRTotalHombres: dataSource?.[1]?.descriptionValue,
                PFRRTotalMujeres: dataSource?.[2]?.descriptionValue,
            }
            
            if (!dataSource?.[0].relatedId)
                byGenToAdd = byGenToSave
            else {
                byGenToSave.Id = dataSource?.[0].relatedId;
                byGenToUpd = byGenToSave
            }

            if(isClose){
                newItemToVr = {
                    OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                    OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                    SubProyectoAnioId: Number(followupId),
                    V_x002d_TotalBeneficiarios: dataSource?.[0]?.descriptionValue,
                    V_x002d_TotalHombres: dataSource?.[1]?.descriptionValue,
                    V_x002d_TotalMujeres: dataSource?.[2]?.descriptionValue,
                    V_x002d_IdOrigen: dataSource?.[0].relatedId,
                }
            }

            let returnId = null

            if(byGenToAdd) returnId = await this.followupService.addItemIndexFollowup(Enums.ListTypes.BeneficiariesByGen, byGenToAdd, currUser) 
            if(byGenToUpd) await this.followupService.updateItemIndexFollowup(Enums.ListTypes.BeneficiariesByGen, byGenToUpd, currUser)
            if(newItemToVr) await this.followupService.addItemIndexFollowup(Enums.ListTypes.BeneficiariesByGenVersion, newItemToVr, currUser)

            if(returnId){
                dataSource[0].relatedId = returnId?.Id
            }
        }
        else if(instaceId?.indexOf("beneficiaries-byage") >= 0 && dataSource?.length){
            let byAgeToAdd = [], byAgeToUpd = [], newItemToVr = []
            for (const byAge of dataSource) {
                let PFFRRTotal = (byAge.fieldMen || 0) + (byAge.fieldWomen || 0)
                let byAgeToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    AgeRangeId: byAge.fieldDescriptionId,
                    PFFRRTotalHombres: byAge.fieldMen,
                    PFRRTotalMujeres: byAge.fieldWomen,
                    PFFRRTotal
                }
                
                if (!byAge.relatedId)
                    byAgeToAdd.push(byAgeToSave)
                else {
                    byAgeToSave.Id = byAge.relatedId;
                    byAgeToUpd.push(byAgeToSave)
                }

                if(isClose){
                    newItemToVr.push({
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_TotalBeneficiarios: PFFRRTotal,
                        V_x002d_TotalHombres: byAge.fieldMen,
                        V_x002d_TotalMujeres: byAge.fieldWomen,
                        V_x002d_Descripcion: byAge.fieldDescription,
                        V_x002d_IdOrigen: byAge.relatedId,
                    })
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(byAgeToAdd, Enums.ListTypes.BeneficiariesByAge, currUser), 
                this.followupService.updateIndexFollowup(byAgeToUpd, Enums.ListTypes.BeneficiariesByAge, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesByAgeVersion, currUser),
            ]);
            
            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.AgeRangeId;
                dataSource.filter((a:any) => a.fieldDescriptionId == indicatorId)[0].relatedId = createdId
            }
        }
        else if(instaceId?.indexOf("beneficiaries-bylocation") >= 0 && dataSource?.length){
            let byLocToAdd = [], byLocToUpd = [], newItemToVr = []
            for (const byLoc of dataSource) {
                let PFFRRTotal = (byLoc.fieldMen || 0) + (byLoc.fieldWomen || 0)
                let byLocToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    PProaLocationId: byLoc.fieldDescriptionId,
                    PFFRRTotalHombres: byLoc.fieldMen,
                    PFRRTotalMujeres: byLoc.fieldWomen,
                    PFFRRTotal
                }
                
                if (!byLoc.relatedId)
                    byLocToAdd.push(byLocToSave)
                else {
                    byLocToSave.Id = byLoc.relatedId;
                    byLocToUpd.push(byLocToSave)
                }

                if(isClose){
                    newItemToVr.push({
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_TotalBeneficiarios: PFFRRTotal,
                        V_x002d_TotalHombres: byLoc.fieldMen,
                        V_x002d_TotalMujeres: byLoc.fieldWomen,
                        V_x002d_Descripcion: byLoc.fieldDescription,
                        V_x002d_IdOrigen: byLoc.relatedId,
                    })
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(byLocToAdd, Enums.ListTypes.BeneficiariesByLocation, currUser), 
                this.followupService.updateIndexFollowup(byLocToUpd, Enums.ListTypes.BeneficiariesByLocation, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesByLocationVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];
            //Updating relatedIds

            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PProaLocationId;
                dataSource.filter((a:any) => a.fieldDescriptionId == indicatorId)[0].relatedId = createdId
            }
        }
        else if(instaceId?.indexOf("beneficiaries-byrurality") >= 0 && dataSource?.length){
            let byRurToAdd = [], byRurToUpd = [], newItemToVr = []
            for (const byRur of dataSource) {
                let PFFRRTotal = (byRur.fieldMen || 0) + (byRur.fieldWomen || 0)
                let byRurToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    RuralityId: byRur.fieldDescriptionId,
                    PFFRRTotalHombres: byRur.fieldMen,
                    PFRRTotalMujeres: byRur.fieldWomen,
                    PFFRRTotal
                }
                
                if (!byRur.relatedId)
                    byRurToAdd.push(byRurToSave)
                else {
                    byRurToSave.Id = byRur.relatedId;
                    byRurToUpd.push(byRurToSave)
                }

                if(isClose){
                    newItemToVr.push({
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_TotalBeneficiarios: PFFRRTotal,
                        V_x002d_TotalHombres: byRur.fieldMen,
                        V_x002d_TotalMujeres: byRur.fieldWomen,
                        V_x002d_Descripcion: byRur.fieldDescription,
                        V_x002d_IdOrigen: byRur.relatedId,
                    })
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(byRurToAdd, Enums.ListTypes.BeneficiariesByRurality, currUser), 
                this.followupService.updateIndexFollowup(byRurToUpd, Enums.ListTypes.BeneficiariesByRurality, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.BeneficiariesByRuralityVersion, currUser),
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.RuralityId;
                dataSource.filter((a:any) => a.fieldDescriptionId == indicatorId)[0].relatedId = createdId
            }
        }

        //Financiera
        if(instaceId?.indexOf("-implementer") >= 0 && dataSource?.length){
            let impToAdd = [], impToUpd = [], newItemToVr = []
            for (const imp of dataSource) {
                let SumaReal = this.sumRealMonths(imp)
                let PProaEjecutadoAnio = SumaReal
                let SumaPlan = this.calTotalMonthPl(imp)
                let AvanceEjecucionAnio = this.calYearPercentage(imp)
                let AvanceEjecucionProyecto = this.calProjectPercentage(imp)
                let PProaAcumuladoProyecto = this.calTotalExecutedBudget(imp)

                let impToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    PProaFRStep2Id: imp.id,
                    PProaFRStep2AniosId: imp.idYear,

                    PresupuestoAnioMod: imp.currentBudgetMod,
                    SumaReal,
                    PProaEjecutadoAnio,
                    SumaPlan,
                    AvanceEjecucionAnio,
                    AvanceEjecucionProyecto,
                    PProaAcumuladoProyecto,
                }

                monthsSp.forEach((month: string, i: number) => {
                    impToSave[month + 'Plan'] = imp[monthsEn[i] + 'Pl']
                    impToSave[month + 'Real'] = imp[monthsEn[i] + 'Re']
                });
                if (!imp.relatedId)
                    impToAdd.push(impToSave)
                else {
                    impToSave.Id = imp.relatedId;
                    impToUpd.push(impToSave)
                }

                if(isClose){
                    let auxToVr: any = {
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_Descripcion: imp.implementer,
                        V_x002d_IdOrigen: imp.relatedId,
                        V_x002d_PresupuestoAnio: imp.currentBudget,
                        V_x002d_PresupuestoAnioMod: imp.currentBudgetMod,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                        PProaEjecutadoAnio,
                        SumaReal,
                        SumaPlan,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxToVr[month + 'Plan'] = imp[monthsEn[i] + 'Pl']
                        auxToVr[month + 'Real'] = imp[monthsEn[i] + 'Re']
                    });
                    newItemToVr.push(auxToVr)
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(impToAdd, Enums.ListTypes.FinancieraPresupuestoImplementadores, currUser), 
                this.followupService.updateIndexFollowup(impToUpd, Enums.ListTypes.FinancieraPresupuestoImplementadores, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.FinancieraPresupuestoImplementadoresVersion, currUser)
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PProaFRStep2Id;
                dataSource.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }
        }
        else if(instaceId?.indexOf("-location") >= 0 && dataSource?.length){
            let locToAdd = [], locToUpd = [], newItemToVr = []
            for (const loc of dataSource) {
                let SumaReal = this.sumRealMonths(loc)
                let PProaEjecutadoAnio = SumaReal
                let SumaPlan = this.calTotalMonthPl(loc)

                let AvanceEjecucionAnio = this.calYearPercentage(loc)
                let AvanceEjecucionProyecto = this.calProjectPercentage(loc)
                let PProaAcumuladoProyecto = this.calTotalExecutedBudget(loc)

                let locToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    PProaFRStep3Id: loc.id,
                    PProaFRStep3AniosId: loc.idYear,

                    PresupuestoAnioMod: loc.currentBudgetMod,
                    SumaReal,
                    PProaEjecutadoAnio,
                    SumaPlan,
                    AvanceEjecucionAnio,
                    AvanceEjecucionProyecto,
                    PProaAcumuladoProyecto,
                }

                monthsSp.forEach((month: string, i: number) => {
                    locToSave[month + 'Plan'] = loc[monthsEn[i] + 'Pl']
                    locToSave[month + 'Real'] = loc[monthsEn[i] + 'Re']
                });
                
                if (!loc.relatedId)
                    locToAdd.push(locToSave)
                else {
                    locToSave.Id = loc.relatedId;
                    locToUpd.push(locToSave)
                }

                if(isClose){
                    let auxToVr: any = {
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_Descripcion: loc.location,
                        V_x002d_IdOrigen: loc.relatedId,
                        V_x002d_PresupuestoAnio: loc.currentBudget,
                        V_x002d_PresupuestoAnioMod: loc.currentBudgetMod,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                        PProaEjecutadoAnio,
                        SumaReal,
                        SumaPlan,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxToVr[month + 'Plan'] = loc[monthsEn[i] + 'Pl']
                        auxToVr[month + 'Real'] = loc[monthsEn[i] + 'Re']
                    });
                    newItemToVr.push(auxToVr)
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(locToAdd, Enums.ListTypes.FinancieraPresupuestoLocalizaciones, currUser), 
                this.followupService.updateIndexFollowup(locToUpd, Enums.ListTypes.FinancieraPresupuestoLocalizaciones, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.FinancieraPresupuestoLocalizacionesVersion, currUser)
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PProaFRStep3Id;
                dataSource.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

        }
        else if(instaceId?.indexOf("-adt") >= 0 && dataSource?.length){
            let adtToAdd = [], adtToUpd = [],  newItemToVr = []
            for (const adt of dataSource) {
                let SumaReal = this.sumRealMonths(adt)
                let PProaEjecutadoAnio = SumaReal
                let SumaPlan = this.calTotalMonthPl(adt)

                let AvanceEjecucionAnio = this.calYearPercentage(adt)
                let AvanceEjecucionProyecto = this.calProjectPercentage(adt)
                let PProaAcumuladoProyecto = this.calTotalExecutedBudget(adt)

                let adtToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    PProaFRStep3Id: adt.id,
                    PProaFRStep3AniosId: adt.idYear,

                    PresupuestoAnioMod: adt.currentBudgetMod,
                    SumaReal,
                    PProaEjecutadoAnio,
                    SumaPlan,
                    AvanceEjecucionAnio,
                    AvanceEjecucionProyecto,
                    PProaAcumuladoProyecto,
                }

                monthsSp.forEach((month: string, i: number) => {
                    adtToSave[month + 'Plan'] = adt[monthsEn[i] + 'Pl']
                    adtToSave[month + 'Real'] = adt[monthsEn[i] + 'Re']
                });
                if (!adt.relatedId)
                    adtToAdd.push(adtToSave)
                else {
                    adtToSave.Id = adt.relatedId;
                    adtToUpd.push(adtToSave)
                }

                if(isClose){
                    let auxToVr: any = {
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_Descripcion: adt.adt,
                        V_x002d_IdOrigen: adt.relatedId,
                        V_x002d_PresupuestoAnio: adt.currentBudget,
                        V_x002d_PresupuestoAnioMod: adt.currentBudgetMod,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                        PProaEjecutadoAnio,
                        SumaReal,
                        SumaPlan,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxToVr[month + 'Plan'] = adt[monthsEn[i] + 'Pl']
                        auxToVr[month + 'Real'] = adt[monthsEn[i] + 'Re']
                    });
                    newItemToVr.push(auxToVr)
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(adtToAdd, Enums.ListTypes.FinancieraPresupuestoADT, currUser), 
                this.followupService.updateIndexFollowup(adtToUpd, Enums.ListTypes.FinancieraPresupuestoADT, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.FinancieraPresupuestoADTVersion, currUser)
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PProaFRStep3Id;
                dataSource.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

        }
        else if(instaceId?.indexOf("-sdc") >= 0 && dataSource?.length){
            let sdcToAdd = [], sdcToUpd = [], newItemToVr = []
            for (const sdc of dataSource) {
                let SumaReal = this.sumRealMonths(sdc)
                let PProaEjecutadoAnio = SumaReal
                let SumaPlan = this.calTotalMonthPl(sdc)

                let AvanceEjecucionAnio = this.calYearPercentage(sdc)
                let AvanceEjecucionProyecto = this.calProjectPercentage(sdc)
                let PProaAcumuladoProyecto = this.calTotalExecutedBudget(sdc)

                let sdcToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    PProaContrapartidaId: sdc.id,
                    PProaContrapartidaAnioId: sdc.idYear,

                    PresupuestoAnioMod: sdc.currentBudgetMod,
                    SumaReal,
                    PProaEjecutadoAnio,
                    SumaPlan,
                    AvanceEjecucionAnio,
                    AvanceEjecucionProyecto,
                    PProaAcumuladoProyecto,
                }

                monthsSp.forEach((month: string, i: number) => {
                    sdcToSave[month + 'Plan'] = sdc[monthsEn[i] + 'Pl']
                    sdcToSave[month + 'Real'] = sdc[monthsEn[i] + 'Re']
                });
                if (!sdc.relatedId)
                    sdcToAdd.push(sdcToSave)
                else {
                    sdcToSave.Id = sdc.relatedId;
                    sdcToUpd.push(sdcToSave)
                }

                if(isClose){
                    let auxToVr: any = {
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_Descripcion: sdc.sdc,
                        V_x002d_IdOrigen: sdc.relatedId,
                        V_x002d_PresupuestoAnio: sdc.currentBudget,
                        V_x002d_PresupuestoAnioMod: sdc.currentBudgetMod,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                        PProaEjecutadoAnio,
                        SumaReal,
                        SumaPlan,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxToVr[month + 'Plan'] = sdc[monthsEn[i] + 'Pl']
                        auxToVr[month + 'Real'] = sdc[monthsEn[i] + 'Re']
                    });
                    newItemToVr.push(auxToVr)
                }
            }
            
            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(sdcToAdd, Enums.ListTypes.FinancieraPresupuestoSDC, currUser), 
                this.followupService.updateIndexFollowup(sdcToUpd, Enums.ListTypes.FinancieraPresupuestoSDC, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.FinancieraPresupuestoSDCVersion, currUser)
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.PProaContrapartidaId;
                dataSource.filter((a:any) => a.id == indicatorId)[0].relatedId = createdId
            }

        }
        else if(instaceId?.indexOf("-actResult") >= 0 && dataSource?.length){
            let resToAdd = [], resToUpd = [], newItemToVr = []
            for (const res of dataSource) {
                let SumaReal = this.sumRealMonths(res)
                let PProaEjecutadoAnio = SumaReal
                let SumaPlan = this.calTotalMonthPl(res)

                let AvanceEjecucionAnio = this.calYearPercentage(res)
                let AvanceEjecucionProyecto = this.calProjectPercentage(res)
                let PProaAcumuladoProyecto = this.calTotalExecutedBudget(res)

                let resToSave: any = {
                    SubProyectoAnioId: Number(followupId),
                    IndicadorActividadId: res.id,
                    IndicadorActividadAnioId: res.idYear,
                    PProaFEStep4ResultadosEsperadosId: res.idParent,
                    PresupuestoAnioMod: res.currentBudgetMod,
                    PresupuestoTotalAnioMod: res.totalBudgetMod,
                    SumaReal,
                    PProaEjecutadoAnio,
                    SumaPlan,
                    AvanceEjecucionAnio,
                    AvanceEjecucionProyecto,
                    PProaAcumuladoProyecto,
                }

                monthsSp.forEach((month: string, i: number) => {
                    resToSave[month + 'Plan'] = res[monthsEn[i] + 'Pl']
                    resToSave[month + 'Real'] = res[monthsEn[i] + 'Re']
                });
                if (!res.relatedId)
                    resToAdd.push(resToSave)
                else {
                    resToSave.Id = res.relatedId;
                    resToUpd.push(resToSave)
                }

                if(isClose){
                    let auxToVr: any = {
                        OCTAVIAFechaInicio: dimensionData?.FechaInicio,
                        OCTAVIAFechaFinalizacion: dimensionData?.FechaFin,
                        SubProyectoAnioId: Number(followupId),
                        V_x002d_Descripcion: res.activity,
                        V_x002d_IdOrigen: res.relatedId,
                        V_x002d_PresupuestoAnio: res.currentBudget,
                        V_x002d_PresupuestoAnioMod: res.currentBudgetMod,
                        V_x002d_PresupuestoTotalProyecto: res.totalBudget,
                        V_x002d_PresupuestoTotalProyecto0: res.totalBudgetMod,
                        V_x002d_ResultadoEsperado: res.resultName,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                        PProaEjecutadoAnio,
                        SumaReal,
                        SumaPlan,
                    }
                    monthsSp.forEach((month: string, i: number) => {
                        auxToVr[month + 'Plan'] = res[monthsEn[i] + 'Pl']
                        auxToVr[month + 'Real'] = res[monthsEn[i] + 'Re']
                    });
                    newItemToVr.push(auxToVr)
                }
            }

            let totResToAdd = [], totResToUpd = []
            let totalResults = this.getTotalResults(this.view.state.sectionData?.results) || []

            for (const totRes of totalResults) {
                if(totRes?.idParent){
                    let SumaReal = this.sumRealMonths(totRes)
                    let PProaEjecutadoAnio = SumaReal
                    let SumaPlan = this.calTotalMonthPl(totRes)

                    let AvanceEjecucionAnio = this.calYearPercentage(totRes)
                    let AvanceEjecucionProyecto = this.calProjectPercentage(totRes)
                    let PProaAcumuladoProyecto = this.calTotalExecutedBudget(totRes)

                    let totResToSave: any = {
                        SubProyectoAnioId: Number(followupId),
                        PProaFEStep4ResultadosEsperadosId: totRes.idParent,

                        PresupuestoAnioMod: totRes.currentBudgetMod,
                        PresupuestoTotalAnioMod: totRes.totalBudgetMod,
                        Title: totRes.relatedTitleParent,
                        SumaReal,
                        PProaEjecutadoAnio,
                        SumaPlan,
                        AvanceEjecucionAnio,
                        AvanceEjecucionProyecto,
                        PProaAcumuladoProyecto,
                    }

                    monthsSp.forEach((month: string, i: number) => {
                        totResToSave[month + 'Plan'] = totRes[monthsEn[i] + 'Pl']
                        totResToSave[month + 'Real'] = totRes[monthsEn[i] + 'Re']
                    });
                    if (!totRes.relatedIdParent)
                        totResToAdd.push(totResToSave)
                    else {
                        totResToSave.Id = totRes.relatedIdParent;
                        totResToUpd.push(totResToSave)
                    }
                }
            }

            let returnIdsAll = await Promise.all([
                this.followupService.addIndexFollowup(resToAdd, Enums.ListTypes.FinancieraResultadosEsperados, currUser), 
                this.followupService.updateIndexFollowup(resToUpd, Enums.ListTypes.FinancieraResultadosEsperados, currUser),
                this.followupService.addIndexFollowup(totResToAdd, Enums.ListTypes.FinancieraResultadosEsperadosTotales, currUser), 
                this.followupService.updateIndexFollowup(totResToUpd, Enums.ListTypes.FinancieraResultadosEsperadosTotales, currUser),
                this.followupService.addIndexFollowup(newItemToVr, Enums.ListTypes.FinancieraResultadosEsperadosVersion, currUser)
            ]);

            let returnIds = returnIdsAll[0];

            //Updating relatedIds
            for (const added of returnIds) {
                let createdId = added.data.Id;
                let indicatorId = added.data.IndicadorActividadId;
                let yearId = added.data.IndicadorActividadAnioId;
                let reg = dataSource.filter((a:any) => a.id == indicatorId && a.idYear == yearId)
                if (reg.length > 0) {
                    reg[0].relatedId = createdId
                }
            }

            let returnTotIds = returnIdsAll[2];

            for (const added of returnTotIds) {
                let createdId = added.data.Id;
                let resultId = added.data.PProaFEStep4ResultadosEsperadosId;
                for (const resFinData of finData.results) {
                    resFinData.data.forEach((a:any) => {
                        if(a.idParent == resultId){
                            a.relatedIdParent = createdId
                        }
                    })
                }
            }
        }

        if(activeAlerts){
            this.view.state?.setTableAlerts?.(instaceId)
            if(!Utilities.isNullObject(this.view.state.setRefresh)){
                this.view.state.setRefresh(!this.view.state.refresh)
                this.view.state.setRefresh(this.view.state.refresh)
            }
            setTimeout(() => {
                Utilities.notifySuccess(this.currentLabels.get("Guardado exitoso"), this.currentLabels.get("Guardar"))
            }, 300);
        }
    }

    public async toggleEditing(dimensionData: any, enEdicion: boolean, typed:any){

        Utilities.showLoading(enEdicion ? this.followupLabels.get('enablingEdition') : this.followupLabels.get('disablingEdition'),
            this.followupLabels.get("loadingMessage"))

        let datadimension = await this.followupService.getSubProjectDimensionsfilter(dimensionData.SubProyectoAnioId,dimensionData.DimensionId);
        datadimension = datadimension.shift()
        let currUser = this.currentUser.currentUser.userPrincipalName
        let canEdit = !datadimension.EnEdicion ? true : (currUser === datadimension?.PProaModifiedBy?.Name?.replace("i:0#.f|membership|", "") ? true : false)

        if(canEdit) {
            let auxDimensionData: any = { Id: dimensionData.Id }

            auxDimensionData.EnEdicion = enEdicion
            auxDimensionData.PProaModifiedBy = { EMail: currUser, Name: "i:0#.f|membership|" + currUser }

            auxDimensionData = await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, auxDimensionData, currUser)
            auxDimensionData.PProaModifiedBy = { EMail: currUser, Name: "i:0#.f|membership|" + currUser }

            this.getDimensionData(dimensionData.CodigoDimension, auxDimensionData)
            if(!enEdicion) this.view.state?.setTableAlerts?.(null)
            
            Utilities.notifySuccess(enEdicion ? this.followupLabels.get('enabledEdition') : this.followupLabels.get('disabledEdition'),
                this.followupLabels.get('notifyCompleted'))
        } else {
            Utilities.notifyError(this.followupLabels.get('UpdateError'))
                .finally(() => {window.location.reload()})
        }
        
    }

    public async saveData(data: any){
        this.dispatcherProgress(0);
        this.dispatcherPendingLoad(1);
        this.dispatcherSetLoading();
        this.dispatcherProgress(30);

        let followupId = this.getParamId()
        const year = Number(this.view.state.projectData?.year);

        //Indicadores
        if (data === 1){
            for (let index = 0; index < this.view.state.objIndicatorData.length; index++) {
                const objInd = this.view.state.objIndicatorData[index];
                
                let insObj = this.getTableInstance(`${objInd.id}-${index}-objetive`)
                if (insObj?.hasEditData()) await insObj.saveEditData()

                const lisExpRes = this.view.state.resIndicatorData.filter((a: any) => a.idParent == objInd.id)
                if (lisExpRes.length > 0){
                    for (let indexRes = 0; indexRes < lisExpRes.length; indexRes++) {
                        const expRes = lisExpRes[indexRes];
                        
                        let insExp = this.getTableInstance(`${expRes.id}-${indexRes}-er-indicator`)
                        if (insExp?.hasEditData()) await insExp.saveEditData()

                        const lisAct = this.view.state.actIndicatorData.filter((a: any) => a.idParent == expRes.id)

                        if (lisAct.length > 0){
                            const act = lisAct[0];
                            let insAct = this.getTableInstance(`${act.idParent}-${indexRes}-er-activities`)
                            if (insAct?.hasEditData()) await insAct.saveEditData()
                        }
                    }
                }
            }

            if (this.institutionalIndicators){
                let insMI = this.getTableInstance(`mainIndicator`)
                if (insMI?.hasEditData()) await insMI.saveEditData()

                let insSI = this.getTableInstance(`secondIndicator`)
                if (insSI?.hasEditData()) await insSI.saveEditData()
                
                let insRI = this.getTableInstance(`resultsIndicators`)
                if (insRI?.hasEditData()) await insRI.saveEditData()
                   
                let insPI = this.getTableInstance(`productIndicators`)
                if (insPI?.hasEditData()) await insPI.saveEditData()
            }

            this.view.state.setTableAlerts(null)

            await Promise.all([
                ...this.view.state.objIndicatorData.map(async (objInd: any, index: number) => {
                    //Expected results
                    const lisExpRes = this.view.state.resIndicatorData.filter((a: any) => a.idParent == objInd.id)

                    if (lisExpRes.length > 0){
                        lisExpRes.map(async (expRes: any, indexRes: number) => {
                            let insExp = this.getTableInstance(`${expRes.id}-${indexRes}-er-indicator`)
                            await this.saveTable(insExp, false)

                            //Activities
                            const lisAct = this.view.state.actIndicatorData.filter((a: any) => a.idParent == expRes.id)

                            if (lisAct.length > 0){
                                const act = lisAct[0];

                                let insAct = this.getTableInstance(`${act.idParent}-${indexRes}-er-activities`)
                                await this.saveTable(insAct, false)
                            }
                        })
                    }
                    let insObj = this.getTableInstance(`${objInd.id}-${index}-objetive`)
                    return await this.saveTable(insObj, false)
                })
            ]);

            this.dispatcherProgress(50);

            //Indicadores institucionales
            if (this.institutionalIndicators){

                let insMI = this.getTableInstance(`mainIndicator`)
                await this.saveTable(insMI, false)

                let insSI = this.getTableInstance(`secondIndicator`)
                await this.saveTable(insSI, false)

                this.dispatcherProgress(70);
                
                let insRI = this.getTableInstance(`resultsIndicators`)
                await this.saveTable(insRI, false)

                let insPI = this.getTableInstance(`productIndicators`)
                await this.saveTable(insPI, false)
            }
            this.dispatcherProgress(90);
            this.view.state.setTableAlerts("All")
        }else if(data === 2){
            let tables = ['beneficiaries-by-activity','beneficiaries-totals-other-table',
                'beneficiaries-byage', 'beneficiaries-bylocation', 'beneficiaries-byrurality']

            for (const table of tables) {
                let tableInstance = this.getTableInstance(table)
                await tableInstance?.saveEditData()
            }

            let resFramework: any = this.resultsFramework || []
            for (const resFrame of resFramework) {
                let results = resFrame?.results || []
                for (const res of results) {
                    let tableInstance = this.getTableInstance(`${res.genericInfo.Id}-expected-result-beneficiaries`)
                    await tableInstance?.saveEditData()
                }
            }

            let overlapTable: any = this.getDataFromTable('beneficiaries-by-overlap')
            for (const table of overlapTable) {
                let overlap = table?.overlaps
                let overlapIns: any = this.getTableInstance(`beneficiaries-by-overlap-detail-${table.id}`)
                if(!!overlapIns){
                    await overlapIns.saveEditData()
                    await this.saveTable(overlapIns, false)
                }else{
                    await this.saveTable(null, false, null, overlap)
                }
            }

            this.view.state.setTableAlerts(null)
            this.dispatcherProgress(60);

            await Promise.all([
                ...tables.map(async (table: any) => {
                    let tableInstance = this.getTableInstance(table)
                    if(!!tableInstance){
                        return await this.saveTable(tableInstance, false)
                    }
                })
            ]);

            this.dispatcherProgress(80);


            await Promise.all([
                ...resFramework.map(async (resFrame: any) => {
                    let results = resFrame?.results || []
                    results.map(async (res: any) => {
                        let resInstance = this.getTableInstance(`${res.genericInfo.Id}-expected-result-beneficiaries`)
                        if(!!resInstance){
                            return await this.saveTable(resInstance, false)
                        }
                    })
                })
            ]);
            
            this.dispatcherProgress(90);
            this.view.state.setTableAlerts("All")
        }else if(data === 3){
            let tables = ['implementer', 'location', 'adt', 'sdc']
            let byAct = this.view.state?.sectionData?.results

            for (let i = 0; i < byAct.length; i++) {
                const table = byAct[i];
                let tableInstance = this.getTableInstance(`${table.id}-${table.section}`)
                if (tableInstance?.hasEditData()) await tableInstance.saveEditData()
            }

            for (let i = 0; i < tables.length; i++) {
                const section = tables[i];
                let tableInstance = this.getTableInstance(`${i}-${section}`)
                if (tableInstance?.hasEditData()) await tableInstance.saveEditData()
            }

            for (let i = 0; i < byAct.length; i++) {
                const table = byAct[i];
                let tableInstance = this.getTableInstance(`${table.id}-${table.section}`)
                await this.saveTable(tableInstance, false)
            }

            this.view.state.setTableAlerts(null)
            this.dispatcherProgress(60);

            for (let i = 0; i < tables.length; i++) {
                const section = tables[i];
                let tableInstance = this.getTableInstance(`${i}-${section}`)
                await this.saveTable(tableInstance, false)
            }

            this.dispatcherProgress(90);
            this.view.state.setTableAlerts("All")
        }else if(data === 4){
            const period = this.view.state.currentPeriod;
            const reports = this.view.state.reports;

            let lastReport = reports.find((x: any) => x.ReportePeriodoId === period.Id)
            let currentState = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === period.EstadoPeriodoCod)?.Id
            let presentedId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.presented)?.Id

            if(this.view.state?.chkEnded && period.EstadoPeriodoCod === Enums.FollowupState.process){
                period.EstadoPeriodo = presentedId
            }else period.EstadoPeriodo = currentState       

            const listEditors = ["DesviacionesExplicacion", "DesviacionesMedidas", "DesviacionesPlazos", 
                "RiesgosExplicaciones", "RiesgosMedidas", "RiesgosPlazos", "ModificacionesSustanciales",
                "ModificacionesNoSustanciales", "OtrosComentarios"];

            let infToSave: any = {};
            let itemsNarrative = [], updateNarrative = [];
            infToSave.SubProyectoAnioId = Number(followupId);
            infToSave.ReportePeriodoId = period.Id;
            infToSave.Finalizado = (!!this.view.state?.chkEnded || false)
            
            for (const ed of listEditors) {
                let informe = this.getDataFromEditor(ed);
                infToSave[ed] = informe;
            }

            if (!!lastReport){
                infToSave.Id = lastReport.Id;
                updateNarrative.push(infToSave)
            } else {
                itemsNarrative.push(infToSave)
            }

            if(this.view.state?.chkEnded && period.EstadoPeriodoCod === Enums.FollowupState.process) {
                let auxPeriod = Utilities.copyObject(period)
                auxPeriod.EstadoPeriodo = period.EstadoPeriodo?.toString()
                delete auxPeriod?.EstadoPeriodoCod
                delete auxPeriod.ProximoCierre
                delete auxPeriod.UltimoCierre
                if(auxPeriod?.Id) await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, auxPeriod)
                else await this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, auxPeriod)

                period.EstadoPeriodoCod = Enums.FollowupState.presented
            }

            this.dispatcherProgress(60);

            period.EstadoPeriodo = this.formMasters.followupState.find((x: any) => x.Id === Number(period.EstadoPeriodo))?.Title
            if (itemsNarrative.length) await this.followupService.addIndexFollowup(itemsNarrative, Enums.ListTypes.SeguimientoInformeNarrativo)
            if (updateNarrative.length) await this.followupService.updateIndexFollowup(updateNarrative, Enums.ListTypes.SeguimientoInformeNarrativo)
            this.dispatcherProgress(90);
        }

        if(!Utilities.isNullObject(this.view.state.setRefresh)){
            this.view.state.setRefresh(this.view.state.refresh)
            this.view.state.setRefresh(!this.view.state.refresh)
        }

        setTimeout(() => {
            Utilities.notifySuccess(this.currentLabels.get("Guardado exitoso"), this.currentLabels.get("Guardar"))
            .then(() => this.dispatcherUnsetSetLoading())
        }, 500);
    }

    public tableIsValid(instance: any, dimensionState: any, dimension: any, auxItems?: any){
        //debug 
        let debug: boolean = false;

        const monthEn = Constants.Months.English
        let tableValid = true
        let auxState = dimension?.CodigoDimension === Enums.FollowupState.followup ? Enums.FollowupState.followup : dimensionState

        if (!!instance || !!auxItems){
            let items = (!!auxItems) ? auxItems : instance.getDataSource().items()
            let keys = (items.length) ? Object.keys(items[0]) : []
            for (const item of items) {
                for (const _key of keys) {
                    const fieldProps = this.view.state.dataColumns.get(_key);
                    let req = fieldProps?.EstadoDimension?.includes(auxState)
                    if(_key === (Enums.FollowupState.january + 'Re') || _key === (Enums.FollowupState.february + 'Re') ||
                        _key === (Enums.FollowupState.march + 'Re') || _key === (Enums.FollowupState.april + 'Re') ||
                        _key === (Enums.FollowupState.may + 'Re') || _key === (Enums.FollowupState.june + 'Re') ||
                        _key === (Enums.FollowupState.july + 'Re') || _key === (Enums.FollowupState.august + 'Re') ||
                        _key === (Enums.FollowupState.september + 'Re') || _key === (Enums.FollowupState.october + 'Re') ||
                        _key === (Enums.FollowupState.november + 'Re') || _key === (Enums.FollowupState.december + 'Re')
                        ){
                            let month_ = monthEn.findIndex((el) => el === _key.split('Re')[0])
                            let monthEnd = monthEn.findIndex((el) => el === dimension?.ProximoCierreEn)
                            let auxReq = !!(month_ <= monthEnd)
                            if(auxReq && req && tableValid) tableValid = !Utilities.isNullEmptyObject(item[_key])
                            if(debug && auxReq && req && !tableValid) console.log('tableValid1',tableValid,item,_key,item[_key], 'month_', month_, 'monthEnd', monthEnd, 'auxReq', auxReq)
                    }else{
                        if(!!req && tableValid && (fieldProps?.Title !== Enums.FollowupBasicFields.currentBudgetMod && fieldProps?.Title !== Enums.FollowupBasicFields.totalBudgetMod)) tableValid = !Utilities.isNullEmptyObject(item[_key])
                        if(debug &&!!req && !tableValid) console.log('tableValid2',tableValid,item,_key,item[_key])
                    }
                }
            }
        }

        return tableValid;
    }

    public checkActPlan(item: any, dimension: any){
        const monthEn = Constants.Months.English
        let actValid = true
        let auxState = dimension?.CodigoDimension

        let keys = (!!item) ? Object.keys(item) : []

        for (const _key of keys) {
            const fieldProps = this.view.state.dataColumns.get(_key);
            let req = fieldProps?.EstadoDimension?.includes(auxState)
            if(_key === (Enums.FollowupState.january + 'Re') || _key === (Enums.FollowupState.february + 'Re') ||
                _key === (Enums.FollowupState.march + 'Re') || _key === (Enums.FollowupState.april + 'Re') ||
                _key === (Enums.FollowupState.may + 'Re') || _key === (Enums.FollowupState.june + 'Re') ||
                _key === (Enums.FollowupState.july + 'Re') || _key === (Enums.FollowupState.august + 'Re') ||
                _key === (Enums.FollowupState.september + 'Re') || _key === (Enums.FollowupState.october + 'Re') ||
                _key === (Enums.FollowupState.november + 'Re') || _key === (Enums.FollowupState.december + 'Re')
                ){
                    let month_ = monthEn.findIndex((el) => el === _key.split('Re')[0])
                    let monthEnd = monthEn.findIndex((el) => el === dimension?.ProximoCierreEn)
                    let auxReq = !!(month_ <= monthEnd)
                    if(auxReq && req && actValid){
                        actValid = !Utilities.isNullEmptyObject(item[_key])
                    } 
            }
            else if(_key === (Enums.FollowupState.january + 'Pl') || _key === (Enums.FollowupState.february + 'Pl') ||
                _key === (Enums.FollowupState.march + 'Pl') || _key === (Enums.FollowupState.april + 'Pl') ||
                _key === (Enums.FollowupState.may + 'Pl') || _key === (Enums.FollowupState.june + 'Pl') ||
                _key === (Enums.FollowupState.july + 'Pl') || _key === (Enums.FollowupState.august + 'Pl') ||
                _key === (Enums.FollowupState.september + 'Pl') || _key === (Enums.FollowupState.october + 'Pl') ||
                _key === (Enums.FollowupState.november + 'Pl') || _key === (Enums.FollowupState.december + 'Pl')
                ){
                    if(req && actValid) actValid = !Utilities.isNullEmptyObject(item[_key])
            }
        }

        return !actValid;
    }



    public checkBenPlan(data: any, dimensionState: any){
        let tableValid = true
        if(dimensionState === Enums.FollowupState.substantialModifications || dimensionState === Enums.FollowupState.planning){
            if(!data?.isCalculated && !data?.isSubtracted){
                let sumMonths = this.calTotalMonthPl(data)
                tableValid = (data.beneficiariesByActivity === sumMonths)
            }
        }
        return tableValid;
    }

    public checkBenTotals(table: any, dimensionState: any){
        let tableValid: boolean = true
        if(table?.length < 4) return tableValid

        const keys = ["beneficiariesByActivity"]
        // let monthsEn = Constants.Months.English

        // monthsEn.forEach((month: string) => {
        //     keys.push(month + 'Pl')
        //     if(dimensionState === Enums.FollowupState.followup) keys.push(month + 'Re')
        // });

        for (const _key of keys) {
            let keyMax = 0
            for (const row of table) {
                if(!row.isCalculated && !row.isModified && !row.isSubtracted){
                    if(row[_key] > keyMax) keyMax = row[_key]
                }
            }
            let totalResult = table.find((x: any) => x.isSubtracted)
            if(tableValid) tableValid = (keyMax <= totalResult[_key])
        }

        return tableValid;
    }

    public checkDisaggregation(disTable: any, dimensionState: any){
        let tableValid = true
        if(dimensionState === Enums.FollowupState.followup && disTable?.length){
            tableValid = (disTable[0]?.descriptionValue) === (disTable[1]?.descriptionValue || 0) + (disTable[2]?.descriptionValue || 0)
        }
        return tableValid;
    }

    public checkDisaggregationEqual(disTable: any, currentTable: any, dimensionState: any){
        let tableValid = true
        if(dimensionState === Enums.FollowupState.followup && disTable?.length && currentTable?.length){
            let sumMen = 0, sumWomen = 0, sumTotal = 0
            for (const item of currentTable) {
                sumMen += (item.fieldMen|| 0)
                sumWomen += (item.fieldWomen|| 0)
                sumTotal += (item.fieldMen|| 0) + (item.fieldWomen|| 0)
            }
            tableValid = ((disTable[0]?.descriptionValue || 0) === sumTotal) && ((disTable[1]?.descriptionValue || 0) === sumMen) && ((disTable[2]?.descriptionValue || 0) === sumWomen)
        }
        return tableValid;
    }

    public checkBenPlanByRes(overLapTable: any, actTable: any){
        let tableValid = true
        let auxOverTable = (!Array.isArray(overLapTable)) ? overLapTable?.data || [] : overLapTable || []
        let auxActTable = (!Array.isArray(actTable)) ? actTable?.data || [] : actTable || []

        if(auxOverTable?.length && auxActTable?.length){
            let totalBenPlan = 0
            for (const parent of auxOverTable) {
                let overlap = parent.overlaps
                totalBenPlan += (parent?.beneficiariesByActivity || 0)
                for (const child of overlap) {
                    totalBenPlan -= (child?.beneficiariesByActivity || 0)
                }
            }

            if(tableValid) tableValid = !Utilities.isNullEmptyObject(auxActTable[0]?.totalMod) ? (auxActTable[0]?.totalMod === totalBenPlan) : (auxActTable[0]?.planningTotal === totalBenPlan)
        }
        return tableValid;
    }

    public checkAchievement(data: any, year: any, debug: boolean = false){
        let plannedAchievement = this.calPlannedAchievement(data, year)
        let plannedAchievementMod = this.calPlannedAchievementMod(data, year)
        let totalMonthPl = this.calTotalMonthPl(data)

        if(debug) console.log('Meta planeada año:', plannedAchievement, ', Total mes a mes:', totalMonthPl)

        return (!Utilities.isNullEmptyObject(plannedAchievementMod) && plannedAchievementMod === totalMonthPl ||
            Utilities.isNullEmptyObject(plannedAchievementMod) && plannedAchievement === totalMonthPl);
    }

    public checkReqFields(item: any, dimension: any){
        const monthEn = Constants.Months.English
        const monthEs = Constants.Months.Spanish
        let tableValid = true

        let keys = (!!item) ? Object.keys(item) : []
        for (const _key of keys) {
            const fieldProps = this.view.state.dataColumns.get(_key);
            let req = fieldProps?.EstadoDimension?.includes(dimension?.CodigoDimension)
            if(_key === (Enums.FollowupState.january + 'Re') || _key === (Enums.FollowupState.february + 'Re') ||
                _key === (Enums.FollowupState.march + 'Re') || _key === (Enums.FollowupState.april + 'Re') ||
                _key === (Enums.FollowupState.may + 'Re') || _key === (Enums.FollowupState.june + 'Re') ||
                _key === (Enums.FollowupState.july + 'Re') || _key === (Enums.FollowupState.august + 'Re') ||
                _key === (Enums.FollowupState.september + 'Re') || _key === (Enums.FollowupState.october + 'Re') ||
                _key === (Enums.FollowupState.november + 'Re') || _key === (Enums.FollowupState.december + 'Re')
                ){
                    let month_ = monthEn.findIndex((el) => el === _key.split('Re')[0])
                    let monthEnd = monthEn.findIndex((el) => el === dimension.ProximoCierreEn)
                    let auxReq = !!(month_ <= monthEnd)
                    
                    if(auxReq && req && tableValid) tableValid = !Utilities.isNullEmptyObject(item[_key])
            }else{
                if(!!req && tableValid && (fieldProps?.Title !== Enums.FollowupBasicFields.currentBudgetMod && fieldProps?.Title !== Enums.FollowupBasicFields.totalBudgetMod)) tableValid = !Utilities.isNullEmptyObject(item[_key])
            }
        }

        return tableValid;
    }

    public checkResume(item: any, dimensionState: any, checkSDC: boolean = true, data: any[]){
        let hasError = false
        let haveEqual = false

        data.forEach((el: any, i: number) => {
            if(![2,3].includes(i) && ((!checkSDC && i !== 4) || checkSDC)){
                if(dimensionState === Enums.FollowupState.planning){
                    if(!hasError) hasError = (el.yearBudget?.toFixed(2) !== item?.yearBudget?.toFixed(2))
                    if(!haveEqual && item?.budgetTypeId !== el.budgetTypeId) haveEqual = (el.yearBudget?.toFixed(2) === item?.yearBudget?.toFixed(2) )
                }else if(dimensionState === Enums.FollowupState.substantialModifications){
                    if(!hasError) hasError = (el.yearBudgetMod?.toFixed(2) !== item?.yearBudgetMod?.toFixed(2))
                    if(!haveEqual && item?.budgetTypeId !== el.budgetTypeId) haveEqual = (el.yearBudgetMod?.toFixed(2) === item?.yearBudgetMod?.toFixed(2))
                }else if(dimensionState === Enums.FollowupState.followup){
                    if(!hasError) hasError = (el.yearBudgetExecuted?.toFixed(2) !== item?.yearBudgetExecuted?.toFixed(2))
                    if(!haveEqual && item?.budgetTypeId !== el.budgetTypeId) haveEqual = (el.yearBudgetExecuted?.toFixed(2) === item?.yearBudgetExecuted?.toFixed(2))
                }
            }
        });

        return (hasError && !haveEqual);
    }

    public checkResumePlanning(data: any[]){
        let hasError = (data?.[0]?.yearBudget?.toFixed(2) !== data?.[1]?.yearBudget?.toFixed(2))
        
        return hasError;
    }

    public checkSumMonth(item: any, dimensionState: any){
        let hasError = false
        let totalMonthPl = this.calTotalMonthPl(item)
        
        hasError = (!Utilities.isNullEmptyObject(item.currentBudgetMod)) ? (totalMonthPl?.toFixed(2) !== item.currentBudgetMod?.toFixed(2)) : (totalMonthPl?.toFixed(2) !== item.currentBudget?.toFixed(2))
        return hasError;
    }

    public checkAsk(dimension: any, instance: any){
        if(dimension?.CodigoDimension === Enums.FollowupState.substantialModifications){
            if (!!instance){
                let items = instance.getDataSource().items()
                for (const it of items) {
                    if(Utilities.isNullEmptyObject(it.plannedGoalMod) || Utilities.isNullEmptyObject(it.endGoalMod)) return true
                }
            }
        }
        return false
    }

    public checkGoal(e: any, dimension: any, year: number){ // cambiar
        let isValidRow = true
        if(dimension?.CodigoDimension === Enums.FollowupState.followup){
            if(Utilities.isNullEmptyObject(e.actualAchievedGoal)) isValidRow = false
        }
        return isValidRow
    }

    public checkEmptyValues(item: any, dimension: any, typeInd: number){
        let isEmpty = true
        if(dimension?.CodigoDimension === Enums.FollowupState.planning){
            if(typeInd === Enums.IndicatorType.type1){
                isEmpty = (Utilities.isNullEmptyObject(item.plannedGoal) || Utilities.isNullEmptyObject(item.endGoal) || 
                    Utilities.isNullEmptyObject(item.lbUnity) || Utilities.isNullEmptyObject(item.lbAnio))
            }else{
                isEmpty = (Utilities.isNullEmptyObject(item.plannedGoal) || Utilities.isNullEmptyObject(item.endGoal) || 
                    Utilities.isNullEmptyObject(item.lbUnity)) || Utilities.isNullEmptyObject(item.frecuency)
            }
        }else {
            isEmpty = false
        }
        return isEmpty
    }

    public checkEmptyValuesActivities(item: any, dimension: any){
        let isEmpty = true
        if(dimension?.CodigoDimension === Enums.FollowupState.planning){
            isEmpty = Utilities.isNullEmptyObject(item.frecuency)
        }else {
            isEmpty = false
        }
        return isEmpty
    }

    public checkEmptyPlan(item: any){
        let isEmpty = true
        isEmpty = this.checkActPlan(item, this.view.state.dimensionData)
        return isEmpty
    }

    public checkFinancialValues(item: any, dimension: any, section: string){
        let isEmpty = true
        if(dimension?.CodigoDimension === Enums.FollowupState.planning){
            if(section === Enums.FollowupBasicFields.activity){
                isEmpty = (Utilities.isNullObject(item?.currentBudget) || Utilities.isNullObject(item?.totalBudget))
            }else{
                isEmpty = Utilities.isNullObject(item?.currentBudget)
            }
        }
        return isEmpty
    }

    public checkInfoBeneficiaries(benActData: any){
        if(!benActData?.length) return false

        let infoYear = benActData?.[0]
        let isValid = ((!Utilities.isNullObject(infoYear?.planningMen) && infoYear?.planningMen >= 0) 
            && (!Utilities.isNullObject(infoYear?.planningWomen) && infoYear?.planningWomen >= 0) 
            && (!Utilities.isNullObject(infoYear?.planningTotal) && infoYear?.planningTotal >= 0))

        return isValid
    }

    public checkCriteriaTable(instance: any, dimension: any){
        let isGood = true
        if(dimension?.CodigoDimension === Enums.FollowupState.followup && !!instance){
            let items = instance.getDataSource().items()
            for (const it of items) {
                if(isGood) isGood = this.checkCriteriaLineBase(it, dimension)
            }
        }
        return isGood
    }

    public checkCriteriaLineBase(e: any, dimension: any){
        let isValidRow = true

        if(dimension?.CodigoDimension === Enums.FollowupState.followup){
            const reduceId = this.formMasters.criterioDeExito?.find((a: any) => a.OCTAVIACodigo === Enums.FollowupBasicFields.reduce)?.Id
            if(e.actualAchievedGoal < (e.lbValue || 0)) isValidRow = (reduceId === e.criteria)
            if(e.actualAchievedGoal < (e.achievedGoalAnt || 0)) isValidRow = (reduceId === e.criteria)
        }
        return isValidRow
    }

    public checkAchievedGoal(instance: any, dimension: any, currentYear: number){
        let isGood = true
        if(dimension?.CodigoDimension === Enums.FollowupState.followup){
            if (!!instance){
                let items = instance.getDataSource().items()
                for (const it of items) {
                    if(isGood) isGood = this.checkGoal(it, dimension, currentYear)
                }
            }
        }
        return isGood
    }

    public checkSumPl(e: any){
        const months = Constants.Months.English
        let sumMonths: number = 0;
        for (const month of months) sumMonths += (e?.[month + 'Pl'] || 0);

        return (!Utilities.isNullEmptyObject(e.plannedGoalMod) ? e.plannedGoalMod === sumMonths : e.plannedGoal === sumMonths)
    }

    public async checkData(data: any, dimensionData: any, currentYear: number){

        //debug
        let debug = false;

        let followupId = this.getParamId()
        this.dispatcherProgress(30);
        this.dispatcherPendingLoad(1);
        this.dispatcherSetLoading();
        
        let nextState = Enums.FollowupState.followup
        let isClose = (dimensionData.CodigoDimension === Enums.FollowupState.followup)
        let currUser = this.currentUser.currentUser.userPrincipalName

        let [subProjectDimensions, subProjectPeriods] = await Promise.all([
            this.followupService.getSubProjectDimensions(),
            this.followupService.getSubProjectPeriods()
        ]);

        //Indicadores  
        if (data === 1){
            let isValid: boolean = true
            let canSave: boolean = true
            let makeAsk: boolean = false
            let nextStep = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === nextState)?.Id

            for (let index = 0; index < this.view.state.objIndicatorData.length; index++) {
                const objInd = this.view.state.objIndicatorData[index];
                let insObj = this.getTableInstance(`${objInd.id}-${index}-objetive`)
                if (insObj?.hasEditData()) await insObj.saveEditData()
                if(!makeAsk) makeAsk = this.checkAsk(dimensionData.CodigoDimension, insObj)
                if(isValid) isValid = this.tableIsValid(insObj, Enums.FollowupState.planning, dimensionData)
                if(debug) console.log('isValid1', isValid)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkCriteriaTable(insObj, dimensionData)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkAchievedGoal(insObj, dimensionData, currentYear)
                
                const lisExpRes = this.view.state.resIndicatorData.filter((a: any) => a.idParent == objInd.id)

                if (lisExpRes.length > 0){
                    for (let indexRes = 0; indexRes < lisExpRes.length; indexRes++) {
                        const expRes = lisExpRes[indexRes];
                        
                        let insExp = this.getTableInstance(`${expRes.id}-${indexRes}-er-indicator`)
                        if (insExp?.hasEditData()) await insExp.saveEditData()
                        if(!makeAsk) makeAsk = this.checkAsk(dimensionData.CodigoDimension, insExp)
                        if(isValid) isValid = this.tableIsValid(insExp, Enums.FollowupState.planning, dimensionData)
                        if(debug) console.log('isValid2', isValid)
                        if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkCriteriaTable(insExp, dimensionData)
                        if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkAchievedGoal(insExp, dimensionData, currentYear)
                       
                        const lisAct = this.view.state.actIndicatorData.filter((a: any) => a.idParent == expRes.id)

                        if (lisAct.length > 0){
                            const act = lisAct[0];

                            let insAct = this.getTableInstance(`${act.idParent}-${indexRes}-er-activities`)
                            
                            if (insAct?.hasEditData()) await insAct.saveEditData()
                            if(!makeAsk) makeAsk = this.checkAsk(dimensionData.CodigoDimension, insAct)
                            if(isValid) isValid = this.tableIsValid(insAct, Enums.FollowupState.planning, dimensionData)
                            if(debug) console.log('isValid3', isValid)
                            if (!!insAct){
                                let itemsAct: any = insAct.getDataSource().items()
                                for (const itemAc of itemsAct) {
                                    if(isValid){
                                        isValid = this.checkAchievement(itemAc, currentYear)
                                        if(debug) console.log('isValid4', isValid)
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (this.institutionalIndicators){
                let insMI = this.getTableInstance(`mainIndicator`)
                if (insMI?.hasEditData()) await insMI.saveEditData()
                if(!makeAsk) makeAsk = this.checkAsk(dimensionData, insMI)
                if(isValid) isValid = this.tableIsValid(insMI, Enums.FollowupState.planning, dimensionData)
                if(debug) console.log('isValid5', isValid)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkCriteriaTable(insMI, dimensionData)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkAchievedGoal(insMI, dimensionData, currentYear)
                    
                let insSI = this.getTableInstance(`secondIndicator`)
                if (insSI?.hasEditData())await insSI.saveEditData()
                if(!makeAsk) makeAsk = this.checkAsk(dimensionData, insSI)
                if(isValid) isValid = this.tableIsValid(insSI, Enums.FollowupState.planning, dimensionData)
                if(debug) console.log('isValid6', isValid)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkCriteriaTable(insSI, dimensionData)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkAchievedGoal(insSI, dimensionData, currentYear)
                
                let insRI = this.getTableInstance(`resultsIndicators`)
                if (insRI?.hasEditData())await insRI.saveEditData()
                if(!makeAsk) makeAsk = this.checkAsk(dimensionData, insRI)
                if(isValid) isValid = this.tableIsValid(insRI, Enums.FollowupState.planning, dimensionData)
                if(debug) console.log('isValid7', isValid)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkCriteriaTable(insRI, dimensionData)
                if(canSave && dimensionData.CodigoDimension === Enums.FollowupState.followup) canSave = this.checkAchievedGoal(insRI, dimensionData, currentYear)

                let insPI = this.getTableInstance(`productIndicators`)
                if (insPI?.hasEditData())await insPI.saveEditData()
                if(!makeAsk) makeAsk = this.checkAsk(dimensionData, insPI)
                if(isValid) isValid = this.tableIsValid(insPI, Enums.FollowupState.planning, dimensionData)
                if(debug) console.log('isValid8', isValid)
                if (!!insPI){
                    let itemsPI: any = insPI.getDataSource().items()
                    for (const itemPi of itemsPI) {
                        if(isValid){
                            isValid = this.checkAchievement(itemPi, currentYear)
                            if(debug) console.log('isValid9', isValid)
                        }
                    }
                }
            }

            for (let index = 0; index < this.view.state.objIndicatorData.length; index++) {
                const objInd = this.view.state.objIndicatorData[index];
                
                let insObj = this.getTableInstance(`${objInd.id}-${index}-objetive`)
                await this.saveTable(insObj, false, (isClose && canSave && isValid))
                
                const lisExpRes = this.view.state.resIndicatorData.filter((a: any) => a.idParent == objInd.id)

                if (lisExpRes.length > 0){
                    for (let indexRes = 0; indexRes < lisExpRes.length; indexRes++) {
                        const expRes = lisExpRes[indexRes];
                        
                        let insExp = this.getTableInstance(`${expRes.id}-${indexRes}-er-indicator`)
                        await this.saveTable(insExp, false, (isClose && canSave && isValid))
                        
                        const lisAct = this.view.state.actIndicatorData.filter((a: any) => a.idParent == expRes.id)

                        if (lisAct.length > 0){
                            const act = lisAct[0];

                            let insAct = this.getTableInstance(`${act.idParent}-${indexRes}-er-activities`)
                            await this.saveTable(insAct, false, (isClose && canSave && isValid))
                        }
                    }
                }
            }

            this.dispatcherProgress(50);

            //Indicadores institucionales
            if (this.institutionalIndicators){
                let insMI = this.getTableInstance(`mainIndicator`)
                await this.saveTable(insMI, false, (isClose && canSave && isValid))
                
                let insSI = this.getTableInstance(`secondIndicator`)
                await this.saveTable(insSI, false, (isClose && canSave && isValid))
                
                this.dispatcherProgress(70);

                let insRI = this.getTableInstance(`resultsIndicators`)
                await this.saveTable(insRI, false, (isClose && canSave && isValid))

                let insPI = this.getTableInstance(`productIndicators`)
                await this.saveTable(insPI, false, (isClose && canSave && isValid))
            }

            this.dispatcherProgress(80);
            
            let subPr = subProjectDimensions.find((y: any) => y.DimensionId === dimensionData.DimensionId && y.SubProyectoAnioId.toString() === followupId)
            subPr.EstadoDimension = nextStep.toString()

            this.view.state.setTableAlerts(null)

            if(isValid && dimensionData.CodigoDimension !== Enums.FollowupState.followup) {
                if(makeAsk){
                    const title = this.followupLabels.get("alertSubstantialModificationsTitle")
                    const msg = this.followupLabels.get("alertSubstantialModificationsMsg")
                    Utilities.showConfirmation(msg, title).then( async (response) => {
                        if(response){
                            await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr)  

                            this.dispatcherProgress(90);  
                            this.view.state?.setTableAlerts?.(null)
                            Utilities.notifySuccess(
                                this.followupLabels.get("alertStateChangedMsg"),
                                this.followupLabels.get("alertStateChangedTitle"),
                            ).then(() => this.initialize(true, data))            
                        } else {
                            this.dispatcherUnsetSetLoading();
                            this.view.state.setTableAlerts("All")
                        }
                    });
                }else{
                    await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr)

                    this.dispatcherProgress(90);  
                    this.view.state?.setTableAlerts?.(null)
                    Utilities.notifySuccess(
                        this.followupLabels.get("alertStateChangedMsg"),
                        this.followupLabels.get("alertStateChangedTitle"),
                    ).then(() => this.initialize(true, data))    
                }
            }else if(canSave && isValid && dimensionData.CodigoDimension === Enums.FollowupState.followup){
                let dataMonths = this.subProjectMonths.find((x: any) => x.SubProyectoAnioId === subPr.SubProyectoAnioId && x.DimensionId == dimensionData.DimensionId)  
                const months = Constants.Months.Spanish
                let monthEndNum = Number(moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])

                let monthOpenId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthOpen)?.Id?.toString()
                let monthClosedId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthClosed)?.Id?.toString()
                let newMonths: any = {}
                let updateMonths: boolean = !!dataMonths

                if(!dataMonths){
                    newMonths = {
                        DimensionId: subPr.DimensionId,
                        AbrilEstado: monthOpenId,
                        AgostoEstado: monthOpenId,
                        DiciembreEstado: monthOpenId,
                        EneroEstado: monthOpenId,
                        FebreroEstado: monthOpenId,
                        JulioEstado: monthOpenId,
                        JunioEstado: monthOpenId,
                        MarzoEstado: monthOpenId,
                        MayoEstado: monthOpenId,
                        NoviembreEstado: monthOpenId,
                        OctubreEstado: monthOpenId,
                        SeptiembreEstado: monthOpenId,
                        SubProyectoAnioId: subPr.SubProyectoAnioId
                    }
                }else{
                    newMonths = {...dataMonths}
                }

                for (let monthIdx = 0; monthIdx < monthEndNum; monthIdx++) {
                    newMonths[months[monthIdx] + 'Estado'] = monthClosedId
                }

                subPr.UltimoCierre = subPr.ProximoCierre
                subPr.ProximoCierre = moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("MM-DD-YYYY").split('-')
                subPr.ProximoCierre[0] = (monthEndNum !== 12) ? monthEndNum + 1 : monthEndNum
                subPr.ProximoCierre = subPr.ProximoCierre.join('-') 
                subPr.EnEdicion = false

                this.dispatcherProgress(90);  

                await Promise.all([
                    this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr, currUser), 
                    updateMonths 
                        ? this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                        : this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                ]);

                this.view.state?.setTableAlerts?.(null)
                Utilities.notifySuccess(
                    this.followupLabels.get("alertStateClosedMsg"),
                    this.followupLabels.get("alertStateClosedTitle"),
                ).then(() => this.initialize(true, data))
            }else{
                this.dispatcherUnsetSetLoading();
                this.view.state.setTableAlerts("All")
                Utilities.notifyError(
                    this.followupLabels.get("alertFormErrorsMsg"),
                    this.followupLabels.get("alertFormErrorsTitle"),
                )
                
            }
        }else if(data === 2){ // Beneficiarios check
            let isValid = true
            let tables = ['beneficiaries-by-activity','beneficiaries-totals-other-table',
                'beneficiaries-byage', 'beneficiaries-bylocation', 'beneficiaries-byrurality']

            for (const table of tables) {
                let tableInstance = this.getTableInstance(table)
                await tableInstance?.saveEditData()
            }

            let resFramework: any = this.resultsFramework || []
            for (const resFrame of resFramework) {
                let results = resFrame?.results || []
                for (const res of results) {
                    let tableInstance = this.getTableInstance(`${res.genericInfo.Id}-expected-result-beneficiaries`)
                    await tableInstance?.saveEditData()
                }
            }

            let overlapTable: any = this.getDataFromTable('beneficiaries-by-overlap')

            for (const table of overlapTable) {
                let overlapIns: any = this.getTableInstance(`beneficiaries-by-overlap-detail-${table.id}`)
                if(!!overlapIns) await overlapIns.saveEditData()
            }

            for (const table of overlapTable) {
                let overlap = table?.overlaps
                let overlapIns: any = this.getTableInstance(`beneficiaries-by-overlap-detail-${table.id}`)
                if(!!overlapIns){
                    await this.saveTable(overlapIns, false, (isClose && isValid))
                }else{
                    await this.saveTable(null, false, (isClose && isValid), overlap)
                }
                for (const item of overlap) {
                    if(isValid) isValid = this.tableIsValid(overlapIns, Enums.FollowupState.planning, dimensionData)
                    if(debug) console.log('isValid1', isValid)
                    if(isValid) isValid = this.checkBenPlan(item, dimensionData.CodigoDimension)
                    if(debug) console.log('isValid2', isValid)
                }
            }

            this.dispatcherProgress(50);

            for (const table of tables) {
                let tableInstance = this.getTableInstance(table)
                if(!!tableInstance){
                    await this.saveTable(tableInstance, false, (isClose && isValid))
                    if(isValid) isValid = this.tableIsValid(tableInstance, Enums.FollowupState.planning, dimensionData)
                    if(debug) console.log('isValid3', isValid)
                }
            }

            this.dispatcherProgress(70);

            for (const resFrame of resFramework) {
                let results = resFrame?.results || []
                for (const res of results) {
                    let tableInstance = this.getTableInstance(`${res.genericInfo.Id}-expected-result-beneficiaries`)
                    await this.saveTable(tableInstance, false, (isClose && isValid))

                    if(isValid) isValid = this.tableIsValid(tableInstance, Enums.FollowupState.planning, dimensionData)
                    if(debug) console.log('isValid4', isValid)
                    let tableData = this.getDataFromTable(`${res.genericInfo.Id}-expected-result-beneficiaries`)
                    if(isValid) isValid = this.checkBenTotals(tableData, dimensionData.CodigoDimension)
                    if(debug) console.log('isValid5', isValid)
                    for (const table of tableData) {
                        
                        if(isValid) isValid = this.checkBenPlan(table, dimensionData.CodigoDimension)
                        if(debug) console.log('isValid6', isValid)
                    }
                }
            }

            this.dispatcherProgress(80);

            let nextStep = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === nextState)?.Id

            let subPr = subProjectDimensions.find((y: any) => y.DimensionId === dimensionData.DimensionId && y.SubProyectoAnioId.toString() === followupId)
            subPr.EstadoDimension = nextStep.toString()

            let disTable: any = this.getDataFromTable('beneficiaries-totals-other-table')
            let totalData: any = this.getDataFromTable('beneficiaries-totals')

            if(this.view.state.benResData?.length && this.view.state.benOverData?.data) {
				let auxTotalData = await this.getBeneficiariesTotalsData(this.view.state.benResData, this.view.state.benOverData)
				disTable = await this.getBeneficiariesDissagregateData(auxTotalData, null, disTable) 
                totalData = auxTotalData?.data || totalData
			}
            
            let ageTable: any = this.getDataFromTable('beneficiaries-byage')
            let locTable: any = this.getDataFromTable('beneficiaries-bylocation')
            let rurTable: any = this.getDataFromTable('beneficiaries-byrurality')

            if(isValid) isValid = this.checkDisaggregation(disTable, dimensionData.CodigoDimension)
            if(debug) console.log('isValid7', isValid)
            if(isValid) isValid = this.checkDisaggregationEqual(disTable, ageTable, dimensionData.CodigoDimension)
            if(debug) console.log('isValid8', isValid)
            if(isValid) isValid = this.checkDisaggregationEqual(disTable, locTable, dimensionData.CodigoDimension)
            if(debug) console.log('isValid9', isValid)
            if(isValid) isValid = this.checkDisaggregationEqual(disTable, rurTable, dimensionData.CodigoDimension)
            if(debug) console.log('isValid10', isValid)

            let actData: any = this.getDataFromTable('beneficiaries-by-activity')
            let projectValid = this.checkProjectIsValid(actData, totalData)

            this.view.state.setTableAlerts(null)
            
            if(!isValid || !projectValid){
                this.dispatcherUnsetSetLoading();
                this.view.state.setTableAlerts("All")
                if(!isValid){
                    Utilities.notifyError(
                        this.followupLabels.get("alertFormErrorsMsg"),
                        this.followupLabels.get("alertFormErrorsTitle"),
                    )
                }
                else{
                    Utilities.notifyError(
                        this.followupLabels.get("projectNoValid"),
                        this.followupLabels.get("alertFormErrorsTitle"),
                    )
                }
            }else if(dimensionData.CodigoDimension === Enums.FollowupState.followup && isValid){
                let dataMonths = this.subProjectMonths.find((x: any) => x.SubProyectoAnioId === subPr.SubProyectoAnioId && x.DimensionId === dimensionData.DimensionId)  
                const months = Constants.Months.Spanish
                let monthStartNum = Number(moment.utc(new Date(subPr.UltimoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])-1
                let monthEndNum = Number(moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])

                let monthOpenId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthOpen)?.Id?.toString()
                let monthClosedId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthClosed)?.Id?.toString()
                let newMonths: any = {}
                let updateMonths: boolean = !!dataMonths

                if(!dataMonths){
                    newMonths = {
                        AbrilEstado: monthOpenId,
                        AgostoEstado: monthOpenId,
                        DiciembreEstado: monthOpenId,
                        DimensionId: subPr.DimensionId,
                        EneroEstado: monthOpenId,
                        FebreroEstado: monthOpenId,
                        JulioEstado: monthOpenId,
                        JunioEstado: monthOpenId,
                        MarzoEstado: monthOpenId,
                        MayoEstado: monthOpenId,
                        NoviembreEstado: monthOpenId,
                        OctubreEstado: monthOpenId,
                        SeptiembreEstado: monthOpenId,
                        SubProyectoAnioId: subPr.SubProyectoAnioId
                    }
                }else{
                    newMonths = {...dataMonths}
                }

                for (monthStartNum; monthStartNum < monthEndNum; monthStartNum++) {
                    newMonths[months[monthStartNum] + 'Estado'] = monthClosedId
                }

                subPr.UltimoCierre = subPr.ProximoCierre
                subPr.ProximoCierre = moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("MM-DD-YYYY").split('-')
                subPr.ProximoCierre[0] = (monthEndNum !== 12) ? monthEndNum + 1 : monthEndNum
                subPr.ProximoCierre = subPr.ProximoCierre.join('-') 
                subPr.EnEdicion = false

                this.dispatcherProgress(90);  

                await Promise.all([
                    this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr, currUser),
                    updateMonths 
                        ? this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                        : this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                ]);

                this.view.state?.setTableAlerts?.(null)
                Utilities.notifySuccess(
                    this.followupLabels.get("alertStateClosedMsg"),
                    this.followupLabels.get("alertStateClosedTitle"),
                ).then(() => this.initialize(true, data))
            }else{
                await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr)
                this.dispatcherProgress(90);  
                this.view.state?.setTableAlerts?.(null)
                Utilities.notifySuccess(
                    this.followupLabels.get("alertStateChangedMsg"),
                    this.followupLabels.get("alertStateChangedTitle"),
                ).then(() => this.initialize(true, data))
            }
        }else if(data === 3){ // Financiera check
            let finError: boolean = false
            let tables = ['implementer', 'location', 'adt', 'sdc']
            let byAct = this.view.state?.sectionData?.results

            for (let i = 0; i < byAct.length; i++) {
                const table = byAct[i];
                let insExp = this.getTableInstance(`${table.id}-${table.section}`)
                if (insExp?.hasEditData()) await insExp.saveEditData()
            }

            for (let i = 0; i < tables.length; i++) {
                const section = tables[i];
                let insTable = this.getTableInstance(`${i}-${section}`)
                if (insTable?.hasEditData()) await insTable.saveEditData()
                
            }

            for (let i = 0; i < byAct.length; i++) {
                const table = byAct[i];
                let tableData = this.getDataFromTable(`${table.id}-${table.section}`)
                let insExp = this.getTableInstance(`${table.id}-${table.section}`)
                await this.saveTable(insExp, false, (isClose && !finError))
                for (const item of tableData) {
                    if(!finError) finError = this.checkSumMonth(item, dimensionData.CodigoDimension)
                    if(debug) console.log('finError1', finError)
                    if(!finError) finError = !this.checkReqFields(item, dimensionData)
                    if(debug) console.log('finError2', finError)
                }
            }

            this.dispatcherProgress(50);

            for (let i = 0; i < tables.length; i++) {
                const section = tables[i];
                let tableData = this.getDataFromTable(`${i}-${section}`)
                let insTable = this.getTableInstance(`${i}-${section}`)
                await this.saveTable(insTable, false, (isClose && !finError))
                for (const item of tableData) {
                    if(!finError) finError = this.checkSumMonth(item, dimensionData.CodigoDimension)
                    if(debug) console.log('finError3', finError)
                    if(!finError) finError = !this.checkReqFields(item, dimensionData)
                    if(debug) console.log('finError4', finError)
                }
            }

            this.dispatcherProgress(80);

            let byResume = this.getFinancialResume(this.view.state?.sectionData)
            let dataRes = byResume.data || []
            for (let idx = 0; idx < dataRes.length; idx++) {
                const item = dataRes[idx];
                if(!finError && ![2,3,4].includes(idx)) finError = this.checkResume(item, dimensionData.CodigoDimension, false, dataRes)
                if(debug) console.log('finError5', finError)
            }

            let nextStep = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === nextState)?.Id

            let subPr = subProjectDimensions.find((y: any) => y.DimensionId === dimensionData.DimensionId && y.SubProyectoAnioId.toString() === followupId)
            subPr.EstadoDimension = nextStep.toString()
            subPr.EnEdicion = dimensionData.EnEdicion

            if(!Utilities.isNullObject(this.view.state.setRefresh)){
                this.view.state.setRefresh(this.view.state.refresh)
                this.view.state.setRefresh(!this.view.state.refresh)
            }

            this.view.state.setTableAlerts(null)

            if(finError){
                this.view.state.setTableAlerts("All")
                this.dispatcherUnsetSetLoading();

                Utilities.notifyError(
                    this.followupLabels.get("alertFormErrorsMsg"),
                    this.followupLabels.get("alertFormErrorsTitle"),
                )
            }else if(dimensionData.CodigoDimension === Enums.FollowupState.followup && !finError){ //cerrarSeguimiento
                let dataMonths = this.subProjectMonths.find((x: any) => x.SubProyectoAnioId === subPr.SubProyectoAnioId && x.DimensionId == dimensionData.DimensionId)  
                const months = Constants.Months.Spanish
                let monthStartNum = Number(moment.utc(new Date(subPr.UltimoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])-1
                let monthEndNum = Number(moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])

                let monthOpenId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthOpen)?.Id?.toString()
                let monthClosedId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.monthClosed)?.Id?.toString()
                let newMonths: any = {}
                let updateMonths: boolean = !!dataMonths

                if(!dataMonths){
                    newMonths = {
                        AbrilEstado: monthOpenId,
                        AgostoEstado: monthOpenId,
                        DiciembreEstado: monthOpenId,
                        DimensionId: subPr.DimensionId,
                        EneroEstado: monthOpenId,
                        FebreroEstado: monthOpenId,
                        JulioEstado: monthOpenId,
                        JunioEstado: monthOpenId,
                        MarzoEstado: monthOpenId,
                        MayoEstado: monthOpenId,
                        NoviembreEstado: monthOpenId,
                        OctubreEstado: monthOpenId,
                        SeptiembreEstado: monthOpenId,
                        SubProyectoAnioId: subPr.SubProyectoAnioId
                    }
                }else{
                    newMonths = {...dataMonths}
                }

                for (monthStartNum; monthStartNum < monthEndNum; monthStartNum++) {
                    newMonths[months[monthStartNum] + 'Estado'] = monthClosedId
                }

                subPr.UltimoCierre = subPr.ProximoCierre
                subPr.ProximoCierre = moment.utc(new Date(subPr.ProximoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("MM-DD-YYYY").split('-')
                subPr.ProximoCierre[0] = (monthEndNum !== 12) ? monthEndNum + 1 : monthEndNum
                subPr.ProximoCierre = subPr.ProximoCierre.join('-') 
                subPr.EnEdicion = false

                this.dispatcherProgress(90);  

                await Promise.all([
                    this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr, currUser), 
                    updateMonths 
                        ? this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                        : this.followupService.addItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoMeses, newMonths)
                ]);

                this.view.state?.setTableAlerts?.(null)
                
                Utilities.notifySuccess(
                    this.followupLabels.get("alertStateClosedMsg"),
                    this.followupLabels.get("alertStateClosedTitle"),
                ).then(() => this.initialize(true, data))
            }else{
                await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoDimensiones, subPr)
                this.dispatcherProgress(90);  
                this.view.state?.setTableAlerts?.(null)
               
                Utilities.notifySuccess(
                    this.followupLabels.get("alertStateChangedMsg"),
                    this.followupLabels.get("alertStateChangedTitle"),
                ).then(() => this.initialize(true, data))
            }
        }else if(data === 4){
            this.dispatcherProgress(70); 
            let currUser = this.currentUser.currentUser.userPrincipalName
            const period = this.view.state.currentPeriod
            delete period.ProximoCierre
            delete period.UltimoCierre

            const reports = Utilities.copyObject(this.view.state.reports);
            let lastReport = reports.find((x: any) => x.ReportePeriodoId === period.Id)
            let approvedId = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.approved)?.Id?.toString()
            period.EstadoPeriodo = approvedId
            delete period.EstadoPeriodoCod

            let yearPeriods: any = this.formMasters.inPeriods?.filter((x: any) => x.Anio === currentYear) || []

            if(yearPeriods?.length){
                let periods = []
                for (let i = 0; i < yearPeriods.length; i++) {
                    const yp = yearPeriods[i];
                    let dataSaved = subProjectPeriods.find((x: any) => x.SubProyectoAnioId === Number(followupId) && x.SeguimientoPeriodosId === yp.Id)
                    if(dataSaved) periods.push(dataSaved)
                }
                let auxPeriods = Utilities.copyObject(periods)
                auxPeriods = auxPeriods.sort((a: any, b: any) => ((a.OCTAVIAOrder < b.OCTAVIAOrder) ? -1 : ((a.OCTAVIAOrder > b.OCTAVIAOrder) ? 1 : 0)));
                for (let index = 0; index < auxPeriods.length; index++) {
                    let pr = auxPeriods[index]
                    if(pr.Id === period.Id){
                        let nextPr = auxPeriods[index + 1]
                        if(!!nextPr){
                            let statePer = this.formMasters.followupState.find((x: any) => x.Id === Number(nextPr.EstadoPeriodo))
                            nextPr.EstadoPeriodo = statePer?.Title
                            nextPr.EstadoPeriodoCod = statePer?.OCTAVIACodigo
                            if(nextPr.EstadoPeriodoCod === Enums.FollowupState.suspended){
                                let stateProcess = this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === Enums.FollowupState.process)
                                nextPr.EstadoPeriodo = stateProcess?.Id?.toString()
                                delete nextPr.EstadoPeriodoCod
                                await this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, nextPr)
                            }
                        }
                    }
                };
            }

            const listEditors = ["DesviacionesExplicacion", "DesviacionesMedidas", "DesviacionesPlazos", 
                "RiesgosExplicaciones", "RiesgosMedidas", "RiesgosPlazos", "ModificacionesSustanciales",
                "ModificacionesNoSustanciales", "OtrosComentarios"];

            let infToSave: any = {};
            infToSave.Id = lastReport.Id;
            
            for (const ed of listEditors) {
                let informe = this.getDataFromEditor(ed);
                infToSave[ed] = informe;
            }

            await Promise.all([
                this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoEstadoSubproyectoPeriodos, period), 
                this.followupService.updateItemIndexFollowup(Enums.ListTypes.SeguimientoInformeNarrativo, infToSave, currUser)
            ]);

            this.dispatcherProgress(90);
            this.view.state.setReports(null)
            this.view.state.setCurrentPeriod(null)
            Utilities.notifySuccess(
                this.followupLabels.get("alertApproveNarrativeMsg"),
                this.followupLabels.get("alertApproveNarrativeTitle"),
            ).then(() => this.context.history.go(0))/* this.initialize(true, data) */
        }
        
    }

    public getBeneficiariesSummary() {
        return [
            { column: "fieldMen", summaryType: 'sum', displayFormat: "{0}" },
            { column: "fieldWomen", summaryType: 'sum', displayFormat: "{0}" },
            { column: "fieldTotal", summaryType: 'sum', displayFormat: "{0}" },
        ]
    }

    public getFinancialSummaryMonths(months: any[]) {
        let monthsColumns = []
        
        for (const month of months) {
            monthsColumns.push(
                { column: month + 'Pl', summaryType: 'custom', name: month + 'Pl', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
                { column: month + 'Re', summaryType: 'custom', name: month + 'Re', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
                { column: month + 'Pl', summaryType: 'custom', name: month + 'Pl accum', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
                { column: month + 'Re', summaryType: 'custom', name: month + 'Re accum', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
                { column: month + 'Pl', summaryType: 'custom', name: month + 'Pl percent', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.PERCENTAGE_SUMMARY},
                { column: month + 'Re', summaryType: 'custom', name: month + 'Re percent', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.PERCENTAGE_SUMMARY},
            )
        }

        return monthsColumns
    }

    public getFinancialSummary(section: string) {
        let months = Constants.Months.English
        let baseItems = [
          { column: "currentBudget", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
          { column: "currentBudget", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
          { column: "currentBudget", summaryType: 'custom', name:"currentBudget percent", displayFormat:"{0}", valueFormat: Constants.GeneralFormats.PERCENTAGE_SUMMARY},
          
          { column: "currentBudgetMod", summaryType: 'custom', name:"currentBudgetMod", displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },

          ...this.getFinancialSummaryMonths(months),
          { column: "totalMonthPl", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },

          { column: "executedBudget", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },

          { column: "accumBudgetExecuted", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },
        ]

        switch (section) {
            case 'activity':
                return [
                    { column: "activity", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetActivity'), name: "notValue" },
                    { column: "activity", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumActivity'), name: "notValue" },
                    { column: "activity", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetActivity'), name: "notValue" },

                    { column: "result", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetResult'), name: "notValue" },
                    { column: "result", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumResult'), name: "notValue" },
                    { column: "result", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetResult'), name: "notValue" },

                    { column: "totalBudget", summaryType: 'custom', displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },

                    { column: "totalBudgetMod", summaryType: 'custom', name:"totalBudgetMod", displayFormat:"{0}", valueFormat: Constants.GeneralFormats.CURRENCY_SUMMARY },

                    ...baseItems
                ]
            case 'implementer':
                return [
                    { column: "implementer", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetImplementer'), name: "notValue" },
                    { column: "implementer", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumImplementer'), name: "notValue" },
                    { column: "implementer", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetImplementer'), name: "notValue" },

                    ...baseItems
                ]
            case 'location':
                return [
                    { column: "location", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetLocation'), name: "notValue" },
                    { column: "location", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumLocation'), name: "notValue" },
                    { column: "location", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetLocation'), name: "notValue" },

                    ...baseItems
                ]
            case 'adt':
                return [
                    { column: "adt", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAdt'), name: "notValue" },
                    { column: "adt", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumAdt'), name: "notValue" },
                    { column: "adt", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetAdt'), name: "notValue" },

                    ...baseItems
                ]
            case 'sdc':
                return [
                    { column: "counterpart", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetSdc'), name: "notValue" },
                    { column: "counterpart", summaryType: 'custom', displayFormat: this.followupLabels.get('totalBudgetAccumSdc'), name: "notValue" },
                    { column: "counterpart", summaryType: 'custom', displayFormat: this.followupLabels.get('percentBudgetSdc'), name: "notValue" },

                    ...baseItems
                ]
            default:
                return []
        }
    }

    public getBeneficiariesColumns(dimension?: string, currentYear?: any, section?: string){

        switch (section) {
            case 'bySubProject':
                return [
                    this.getCommonFieldProps("description"),
                    this.getCommonFieldProps("men", dimension),
                    this.getCommonFieldProps("women", dimension),
                    this.getCommonFieldProps("total", dimension)
                ]
            case 'byActivity':
                return [
                    this.getCommonFieldProps("activity", null, null, null, true),
                    this.getCommonFieldProps("beneficiariesByActivity", dimension, null, null, null, true),
                    ...this.getMonthsDetailsColumns(dimension, currentYear, true),
                    this.getCommonFieldProps("totalMonthPl"),
                    this.getCommonFieldProps("achievedBeneficiaries", dimension, null, currentYear, null, true),
                    this.getCommonFieldProps("accumBeneficiaries", dimension, null, null, null, true),
                    this.getCommonFieldProps("activityPercentage", dimension, null, currentYear),
                ]
            case 'byOverlaps':
                return [
                    this.getCommonFieldProps("result", null, null, null),
                    this.getCommonFieldProps("beneficiariesByResult", dimension),
                    ...this.getMonthsDetailsColumns(dimension, currentYear),
                    this.getCommonFieldProps("totalMonthPl"),
                    this.getCommonFieldProps("achievedBeneficiaries", dimension, null, currentYear),
                    this.getCommonFieldProps("accumBeneficiaries", dimension),
                    this.getCommonFieldProps("activityPercentage", dimension, null, currentYear),
                ]
            case 'byTotalSubPr':
                return [
                    this.getCommonFieldProps("activity", null, null, null, true),
                    this.getCommonFieldProps("beneficiariesByActivity", dimension),
                    ...this.getMonthsDetailsColumns(dimension, currentYear),
                    this.getCommonFieldProps("totalMonthPl"),
                    this.getCommonFieldProps("achievedBeneficiaries", dimension, null, currentYear),
                    this.getCommonFieldProps("accumBeneficiaries", dimension),
                ]
            case 'byDisaggregation':
                return [
                    this.getCommonFieldProps("disaggregationDescription"),
                    this.getCommonFieldProps("descriptionValue", dimension),
                ]
            case 'byAge':
            case 'byLocation':
            case 'byRurality':
                return [
                    this.getCommonFieldProps("fieldDescription", dimension),
                    this.getCommonFieldProps("fieldMen", dimension),
                    this.getCommonFieldProps("fieldWomen", dimension),
                    this.getCommonFieldProps("fieldTotal", dimension),
                ]
        }
    }

    public getFinancialColumns(dimension?: string, currentYear?: any, section?: string){

        let baseColumns = [
            this.getCommonFieldProps("currentBudget", null, null, currentYear),
            this.getCommonFieldProps("currentBudgetMod", dimension, null, currentYear),
            ...this.getMonthsDetailsColumns(dimension, currentYear),
            this.getCommonFieldProps("totalMonthPl"),
            this.getCommonFieldProps("executedBudget", dimension),
            this.getCommonFieldProps("accumBudgetExecuted", dimension),
            this.getCommonFieldProps("yearPercentage", dimension),
        ]

        switch (section) {
            case 'resume':
                return [
                    this.getCommonFieldProps("budgetType"),
                    this.getCommonFieldProps("yearBudget"),
                    this.getCommonFieldProps("yearBudgetMod"),
                    this.getCommonFieldProps("yearBudgetExecuted")
                ]
            case 'activity':
                return [
                    this.getCommonFieldProps("activity", null, null, null, true),
                    this.getCommonFieldProps("result", null, null, null, true),
                    this.getCommonFieldProps("currentBudget", null, null, currentYear),
                    this.getCommonFieldProps("currentBudgetMod", dimension, null, currentYear),
                    this.getCommonFieldProps("totalBudget"),
                    this.getCommonFieldProps("totalBudgetMod", dimension),
                    ...this.getMonthsDetailsColumns(dimension, currentYear),
                    this.getCommonFieldProps("totalMonthPl"),
                    this.getCommonFieldProps("executedBudget", dimension),
                    this.getCommonFieldProps("accumBudgetExecuted", dimension),
                    this.getCommonFieldProps("yearPercentage", dimension),
                    this.getCommonFieldProps("projectPercentage", dimension),
                ]
            case 'implementer':
                return [
                    this.getCommonFieldProps("implementer", null, null, null, true),
                    ...baseColumns
                ]
            case 'location':
                return [
                    this.getCommonFieldProps("location", null, null, null, true),
                    ...baseColumns
                ]
            case 'adt':
                return [
                    this.getCommonFieldProps("adt", null, null, null, true),
                    ...baseColumns
                ]
            case 'sdc':
                return [
                    this.getCommonFieldProps("sdc"),
                    this.getCommonFieldProps("counterpart", null, null, null, true),
                    ...baseColumns
                ]
            default:
                return baseColumns
        }
    }

    public getIndType1Columns(dimension?: any, currentYear?: any){

        return [
            this.getCommonFieldProps("indicator", null, null, null, true),
            this.getCommonFieldProps("baseLine"),
            this.getCommonFieldProps("criteria", dimension),
            this.getCommonFieldProps("frecuency", dimension),
            this.getCommonFieldProps("plannedGoal", null, null, currentYear),
            this.getCommonFieldProps("plannedGoalMod", dimension, null, currentYear),
            this.getCommonFieldProps("endGoal"),
            this.getCommonFieldProps("endGoalMod", dimension),
            this.getCommonFieldProps("achievedGoal", null, null, currentYear), 
            this.getCommonFieldProps("actualAchievedGoal", dimension, Enums.IndicatorType.type1, currentYear), 
            this.getCommonFieldProps("currentProgressPercent", undefined, Enums.IndicatorType.type1, currentYear),
            this.getCommonFieldProps("totalProgressPercent", undefined, Enums.IndicatorType.type1, currentYear),
        ]
    }
    
    public getIndType2Columns(dimension?: any, currentYear?: any){
        return [
            this.getCommonFieldProps("selected", null, null, null, true),
            this.getCommonFieldProps("indicator", null, null, null, true),
            this.getCommonFieldProps("lbUnity"),
            this.getCommonFieldProps("frecuency", dimension),
            this.getCommonFieldProps("plannedGoal", null, null, currentYear),
            this.getCommonFieldProps("achievedGoal", null, null, currentYear),
            this.getCommonFieldProps("plannedAchievement", null, null, currentYear),
            this.getCommonFieldProps("plannedGoalMod", dimension, null, currentYear),
            this.getCommonFieldProps("plannedAchievementMod", null, null, currentYear),
            this.getCommonFieldProps("endGoal"),
            this.getCommonFieldProps("endGoalMod", dimension), 
            this.getCommonFieldProps("totalMonthPl"),
            this.getCommonFieldProps("currentAchievementAch", null, null, currentYear),
            this.getCommonFieldProps("actualActAchievedGoal", null, Enums.IndicatorType.type2, currentYear),
            this.getCommonFieldProps("totalAchievement"),
            this.getCommonFieldProps("currentProgressPercent", undefined, Enums.IndicatorType.type2, currentYear),
            this.getCommonFieldProps("totalProgressPercent", undefined, Enums.IndicatorType.type2, currentYear),
        ]
    }

    public getMonthsDetailsColumns(dimension?: string, currentYear?: any, isTotal: boolean = false){
        return [
            this.getCommonFieldProps("january", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("february", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("march", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("april", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("may", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("june", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("july", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("august", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("september", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("october", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("november", dimension, null, currentYear, null, isTotal),
            this.getCommonFieldProps("december", dimension, null, currentYear, null, isTotal),
        ]
    }

    private getCommonFieldProps(fieldName: string, dimension?: any, indicatorType?: number, year?:number, fixed?: boolean, isTotal: boolean = false) {
        const fieldProps = this.view.state.dataColumns.get(fieldName);
        const monthsEn = Constants.Months.English
        year = Number(year)

        const field: any = {
            id: `${fieldProps?.Id || 0}_followup`,
            caption: fieldProps?.OCTAVIAValorTraduccion || "",
            dataField: fieldProps?.Title || "",
            dataType: fieldProps?.OCTAVIATipoCampo || "string",
            allowEditing: !fieldProps?.SoloLectura,
            alignment: fieldProps?.Alineacion || "center",
            format: fieldProps?.Format || "",
        };

        if (fixed){
            field.fixed = true
            field.fixedPosition = "left"
        }
        if(!monthsEn.includes(fieldName?.split('Re')?.[0]?.split('Pl')?.[0])) field.minWidth = 120
        if(fieldName === Enums.FollowupBasicFields.indicator || fieldName === Enums.FollowupBasicFields.sdc ||
            fieldName === Enums.FollowupBasicFields.budgetType || fieldName === Enums.FollowupBasicFields.fieldDescription) field.width = 300
        if(fieldName === Enums.FollowupBasicFields.activity || fieldName === Enums.FollowupBasicFields.implementer ||
            fieldName === Enums.FollowupBasicFields.location || fieldName === Enums.FollowupBasicFields.adt ||
            fieldName === Enums.FollowupBasicFields.counterpart || fieldName === Enums.FollowupBasicFields.result){
            field.width = 400
        }
        if(fieldName === Enums.FollowupBasicFields.selected) field.width = 50
        if(fieldName === Enums.FollowupBasicFields.totalProgressPercent) field.editorOptions = { min: 0 }
        if(fieldName === Enums.FollowupBasicFields.beneficiariesByResult) field.dataField = Enums.FollowupBasicFields.beneficiariesByActivity
        if(fieldName === Enums.FollowupBasicFields.plannedGoal || fieldName === Enums.FollowupBasicFields.plannedGoalMod ||
            fieldName === Enums.FollowupBasicFields.currentBudget || fieldName === Enums.FollowupBasicFields.currentBudgetMod ||
            fieldName === Enums.FollowupBasicFields.achievedBeneficiaries || fieldName === Enums.FollowupBasicFields.activityPercentage){
            field.caption += ` ${year}`
        }
        if(fieldName === Enums.FollowupBasicFields.achievedGoal){
            field.caption += ` ${(year -1)}`
            field.dataField += `Ant`  
        }
        if(fieldName === Enums.FollowupBasicFields.actualAchievedGoal || fieldName === Enums.FollowupBasicFields.actualActAchievedGoal ||
            fieldName === Enums.FollowupBasicFields.plannedAchievement || fieldName === Enums.FollowupBasicFields.plannedAchievementMod ||
            fieldName === Enums.FollowupBasicFields.currentAchievementAch){
            field.caption += ` ${year}`
        }
        if(fieldName === Enums.FollowupBasicFields.baseLine){
            field.width = 300
            field.columns = [
                this.getCommonFieldProps("lbValue"),
                this.getCommonFieldProps("lbUnity"),
                this.getCommonFieldProps("lbAnio")
            ]
        }
        if(!!fieldProps.OCTAVIALista){
            field.lookup = { 
                dataSource: this.formMasters[fieldProps.OCTAVIALista], displayExpr: "Title", valueExpr: "Id" 
            }
        }
        if(fieldProps?.EstadoDimension){
            field.allowEditing = (!!dimension?.CodigoDimension) ? fieldProps?.EstadoDimension?.includes(dimension?.CodigoDimension) : false
            // field.allowEditing = true // Tests
        }
        if(fieldProps?.EstadoDimensionOculto){
            field.visible = (!!dimension?.CodigoDimension) ? !fieldProps?.EstadoDimensionOculto?.includes(dimension?.CodigoDimension) : false
        }
        if(fieldName === Enums.FollowupState.january || fieldName === Enums.FollowupState.february ||
            fieldName === Enums.FollowupState.march || fieldName === Enums.FollowupState.april ||
            fieldName === Enums.FollowupState.may || fieldName === Enums.FollowupState.june ||
            fieldName === Enums.FollowupState.july || fieldName === Enums.FollowupState.august ||
            fieldName === Enums.FollowupState.september || fieldName === Enums.FollowupState.october ||
            fieldName === Enums.FollowupState.november || fieldName === Enums.FollowupState.december 
            ){
                field.columns = [
                    this.getCommonFieldProps(fieldName + 'Pl', dimension, null, null, null, isTotal),
                    this.getCommonFieldProps(fieldName + 'Re', dimension, null, null, null, isTotal),
                ]
        }
        if(fieldName === Enums.FollowupBasicFields.men){
            field.columns = [
                this.getCommonFieldProps(Enums.FollowupBasicFields.planningMen, dimension),
                this.getCommonFieldProps(Enums.FollowupBasicFields.menMod, dimension),
            ]
        }
        if(fieldName === Enums.FollowupBasicFields.women){
            field.columns = [
                this.getCommonFieldProps(Enums.FollowupBasicFields.planningWomen, dimension),
                this.getCommonFieldProps(Enums.FollowupBasicFields.womenMod, dimension),
            ]
        }
        if(fieldName === Enums.FollowupBasicFields.total){
            field.columns = [
                this.getCommonFieldProps(Enums.FollowupBasicFields.planningTotal, dimension),
                this.getCommonFieldProps(Enums.FollowupBasicFields.totalMod, dimension),
            ]
        }
        if(fieldName === (Enums.FollowupState.january + 'Re') || fieldName === (Enums.FollowupState.february + 'Re') ||
            fieldName === (Enums.FollowupState.march + 'Re') || fieldName === (Enums.FollowupState.april + 'Re') ||
            fieldName === (Enums.FollowupState.may + 'Re') || fieldName === (Enums.FollowupState.june + 'Re') ||
            fieldName === (Enums.FollowupState.july + 'Re') || fieldName === (Enums.FollowupState.august + 'Re') ||
            fieldName === (Enums.FollowupState.september + 'Re') || fieldName === (Enums.FollowupState.october + 'Re') ||
            fieldName === (Enums.FollowupState.november + 'Re') || fieldName === (Enums.FollowupState.december + 'Re')
            ){
                if(dimension?.CodigoDimension === Enums.FollowupState.followup){
                    const monthEn = Constants.Months.English
                    const monthEs = Constants.Months.Spanish
                    let month_ = fieldName.split('Re')[0]
                    month_ = monthEs[monthEn.findIndex((el) => el === month_)] + "Estado"
                    let stateSubpr = this.subProjectMonths.find((x: any) => x.SubProyectoAnioId === Number(this.getParamId()) && x.DimensionId === dimension.DimensionId)
                    let stateMo = this.formMasters.followupState.find((y: any) => y.Id === Number(stateSubpr?.[month_]))?.OCTAVIACodigo || Enums.FollowupState.monthOpen
                    field.allowEditing = (stateMo !== Enums.FollowupState.monthClosed)
                }
        }

        if(fieldName === Enums.FollowupBasicFields.actualAchievedGoal) field.onValueChanged = true

        if(fieldName === Enums.FollowupBasicFields.plannedAchievement) field.calculateCellValue = (data: any) => this.calPlannedAchievement(data, year)  
        if(fieldName === Enums.FollowupBasicFields.plannedAchievementMod) field.calculateCellValue = (data: any) => this.calPlannedAchievementMod(data, year)  
        if(fieldName === Enums.FollowupBasicFields.fieldTotal) field.calculateCellValue = (data: any) => this.calFieldTotal(data)
        if(fieldName === Enums.FollowupBasicFields.totalMonthPl) field.calculateCellValue = (data: any) => this.calTotalMonthPl(data)
        if(fieldName === Enums.FollowupBasicFields.currentAchievementAch || fieldName === Enums.FollowupBasicFields.executedBudget) field.calculateCellValue = this.sumRealMonths
        if(fieldName === Enums.FollowupBasicFields.actualActAchievedGoal) field.calculateCellValue = (data: any) => this.calActualActAchievedGoal(data, year) 
        if(fieldName === Enums.FollowupBasicFields.totalAchievement) field.calculateCellValue = (data: any) => this.calTotalAchievement(data) 
        if(fieldName === Enums.FollowupBasicFields.accumBudgetExecuted) field.calculateCellValue = (data: any) => this.calTotalExecutedBudget(data) 
        if(fieldName === Enums.FollowupBasicFields.currentProgressPercent && indicatorType === Enums.IndicatorType.type1) field.calculateCellValue = (data: any) => this.calCurrentProgressPercentT1(data, year)
        if(fieldName === Enums.FollowupBasicFields.totalProgressPercent && indicatorType === Enums.IndicatorType.type1) field.calculateCellValue = (data: any) => this.calTotalProgressPercentT1(data, year) 
        if(fieldName === Enums.FollowupBasicFields.currentProgressPercent && indicatorType === Enums.IndicatorType.type2) field.calculateCellValue = (data: any) => this.calCurrentProgressPercentT2(data, year)
        if(fieldName === Enums.FollowupBasicFields.totalProgressPercent && indicatorType === Enums.IndicatorType.type2) field.calculateCellValue = (data: any) => this.calTotalProgressPercentT2(data, year)
        if(fieldName === Enums.FollowupBasicFields.yearPercentage) field.calculateCellValue = (data: any) => this.calYearPercentage(data)
        if(fieldName === Enums.FollowupBasicFields.projectPercentage) field.calculateCellValue = (data: any) => this.calProjectPercentage(data)
        if(fieldName === Enums.FollowupBasicFields.activityPercentage) field.calculateCellValue = (data: any) => this.calActivityPercentage(data)
        
        if(isTotal) field.calculateCellValue = (data: any) => this.calResultFields(data, fieldName)

        return field;
    }

    public checkCloseMonths(dimensionData: any){
        let followupId = this.getParamId()
        let subPr = this.subProjectDimensions.find((y: any) => y.DimensionId === dimensionData.DimensionId && y.SubProyectoAnioId.toString() === followupId)
        let lastClose = Number(moment.utc(new Date(subPr?.UltimoCierre)).tz(Constants.GeneralFormats.TIME_ZONE).format("DD-MM-YYYY").split('-')[1])
        return (lastClose === 12)
    }

    public calResultFields(data: any, fieldName: string){
        let resultTable = this.getDataFromTable(data.resultId + '-expected-result-beneficiaries')
        let value = data?.[fieldName]

        if(data?.isCalculated){
            value = 0
            let auxData = null
            if(fieldName === Enums.FollowupBasicFields.achievedBeneficiaries) value = this.sumRealMonths(data)
            if(fieldName === Enums.FollowupBasicFields.accumBeneficiaries){
                let auxValue = this.sumRealMonths(data)
                value = (data?.previousAchievedBeneficiaries || 0) + (auxValue || 0)
            }
            for (const it of resultTable) {
                if(!it.isModified && !it.isSubtracted && !it.isCalculated) value += (it?.[fieldName] || 0)
                if(it.isCalculated) auxData = it
            }
            if(auxData) auxData[fieldName] = value
            this.calTotalMonthPl(data)
        }else if(data.isSubtracted){
            value = 0
            let roWPpal = resultTable.find((x: any) => x.isCalculated)
            let rowSecond = resultTable.find((x: any) => x.isModified)
            let auxData = resultTable.find((x: any) => x.isSubtracted)

            if(fieldName === Enums.FollowupBasicFields.achievedBeneficiaries){
                let ppalData = this.sumRealMonths(roWPpal)
                let secData = this.sumRealMonths(rowSecond)
                value = (ppalData || 0) - (secData || 0)
            }
            else if(fieldName === Enums.FollowupBasicFields.accumBeneficiaries){
                let ppalData = this.sumRealMonths(roWPpal)
                let secData = this.sumRealMonths(rowSecond)
                let ppalDataAc = (roWPpal?.previousAchievedBeneficiaries || 0) + (ppalData || 0)
                let secDataAc = (rowSecond?.previousAchievedBeneficiaries || 0) + (secData || 0)
                value = (ppalDataAc || 0) - (secDataAc || 0)
            }else{
                value = ((roWPpal?.[fieldName] || 0) - (rowSecond?.[fieldName] || 0))
                if(auxData) auxData[fieldName] = value
            }
        }else{
            if(fieldName === Enums.FollowupBasicFields.accumBeneficiaries) value = this.calAccumBeneficiaries(data)
            if(fieldName === Enums.FollowupBasicFields.achievedBeneficiaries) value = this.sumRealMonths(data)
        }

        
        return value
    }

    private calPlannedAchievement(data: any, year: number){
        let achievedGoal =  data[`achievedGoalAnt`]
        let value = (!Utilities.isNullEmptyObject(achievedGoal)) 
            ? data[Enums.FollowupBasicFields.plannedGoal] - achievedGoal 
            : data[Enums.FollowupBasicFields.plannedGoal]
        if(!isFinite(value)) value = 0;
        value = value % 1 !== 0 ? Number(value.toFixed(2)) : value;
        return value;
    }

    private calPlannedAchievementMod(data: any, year: number){
        let achievedGoal =  data[`achievedGoalAnt`]
        let value = ((!Utilities.isNullEmptyObject(achievedGoal)) && (!Utilities.isNullEmptyObject(data?.[Enums.FollowupBasicFields.plannedGoalMod]))) 
            ? data[Enums.FollowupBasicFields.plannedGoalMod] - achievedGoal 
            : data[Enums.FollowupBasicFields.plannedGoalMod]
        if(value === undefined) value = null
        if(!isFinite(value)) {
            value = 0;
        }
        value = value % 1 !== 0 ? Number(value.toFixed(2)) : value;
        return value;
    }

    public calTotalMonthPl(data: any){
        const months = Constants.Months.English;
        let value: number = 0
        for (const month of months) value += (data?.[month + 'Pl'] || 0)
        if(!isFinite(value)) value = 0; 
        else value = Number(value.toFixed(2))

        return value;
    }

    private sumRealMonths(data: any){
        const months = Constants.Months.English;
        let value: number = 0

        for (const month of months) value += (data?.[month + 'Re'] || 0)
        if(!isFinite(value)) value = 0;
        else value = Number(value.toFixed(2))

        return value;
    }

    private calActualActAchievedGoal(data: any, year: number){
        let achievedGoal =  data[`achievedGoalAnt`]
        let currentAchievementAch = this.sumRealMonths(data)
        let value = achievedGoal ? currentAchievementAch + achievedGoal : currentAchievementAch
        if(!isFinite(value)) value = 0;

        return value;
    }

    private calTotalAchievement(data: any){
        let value = this.sumRealMonths(data)
        value += (data?.previousAchievements || 0)
        if(!isFinite(value)) value = 0;

        return value;
    }

    private calTotalExecutedBudget(data: any){
        let value = this.sumRealMonths(data)
        value += (data?.previousExecutedBudget || 0)
        if(!isFinite(value)) value = 0;

        return value;
    }

    private calAccumBeneficiaries(data: any){
        let value = this.sumRealMonths(data)
        value += (data?.previousAchievedBeneficiaries || 0)
        if(!isFinite(value)) value = 0;

        return value;
    }

    private calCurrentProgressPercentT1(data: any, year: number){
        let achievedGoal =  data[`achievedGoalAnt`]
        let plannedGoalMod = data[Enums.FollowupBasicFields.plannedGoalMod]
        let actualAchievedGoal = data[Enums.FollowupBasicFields.actualAchievedGoal]
        let lbValue = data[Enums.FollowupBasicFields.lbValue] || 0
        let plannedGoal = data[Enums.FollowupBasicFields.plannedGoal]
        
        let value = 0

        if(!Utilities.isNullEmptyObject(plannedGoalMod)){
            if(!Utilities.isNullEmptyObject(achievedGoal)){
                value = (!Utilities.isNullEmptyObject(actualAchievedGoal)) ? ((actualAchievedGoal - achievedGoal) / (plannedGoalMod - achievedGoal)) : 0
            }else{
                value = (!Utilities.isNullEmptyObject(actualAchievedGoal)) ? ((actualAchievedGoal - lbValue) / (plannedGoalMod - lbValue)) : 0
            }
        }else{
            if(!Utilities.isNullEmptyObject(achievedGoal)){
                value = (!Utilities.isNullEmptyObject(actualAchievedGoal) && !Utilities.isNullEmptyObject(plannedGoal)) ? ((actualAchievedGoal - achievedGoal) / (plannedGoal - achievedGoal)) : 0
            }else{
                value = (!Utilities.isNullEmptyObject(actualAchievedGoal) && !Utilities.isNullEmptyObject(plannedGoal)) ? ((actualAchievedGoal - lbValue) / (plannedGoal - lbValue)) : 0
            }
        }

        if(!isFinite(value)) value = 1;
        //console.log('value', value, data[`indicator`], {achievedGoal, plannedGoalMod,actualAchievedGoal,lbValue, plannedGoal})
        return value;
    }

    private calTotalProgressPercentT1(data: any, year: number){
        let endGoalMod = data[Enums.FollowupBasicFields.endGoalMod] 
        let actualAchievedGoal = data[Enums.FollowupBasicFields.actualAchievedGoal]
        let endGoal = data[Enums.FollowupBasicFields.endGoal]
        let lbValue = data[Enums.FollowupBasicFields.lbValue] || 0
        
        let value = (!Utilities.isNullEmptyObject(endGoalMod)) 
            ? (!Utilities.isNullEmptyObject(actualAchievedGoal)) ? ((actualAchievedGoal - lbValue) / (endGoalMod - lbValue)) : 0 
            : (!Utilities.isNullEmptyObject(actualAchievedGoal)) ? ((actualAchievedGoal - lbValue) / (endGoal - lbValue)) : 0

        if(!isFinite(value)) value = 1;

        return value;
    }

    private calCurrentProgressPercentT2(data: any, year: number){
        let plannedAchievementMod = this.calPlannedAchievementMod(data, year)
        let currentAchievementAch = this.sumRealMonths(data)
        let plannedAchievement = this.calPlannedAchievement(data, year)

        let value = (!Utilities.isNullEmptyObject(currentAchievementAch)) 
            ? (!Utilities.isNullEmptyObject(plannedAchievementMod)) ? (currentAchievementAch/plannedAchievementMod) 
                : (!Utilities.isNullEmptyObject(plannedAchievement)) ? (currentAchievementAch/plannedAchievement) : 0 
            : 0

        if(!isFinite(value)) value = 1;
        return value;
    }

    private calYearPercentage(data: any){
        let executedBudget = this.sumRealMonths(data)
        let currentBudgetMod = data[Enums.FollowupBasicFields.currentBudgetMod]
        let currentBudget  = data[Enums.FollowupBasicFields.currentBudget]
        
        let value = (!Utilities.isNullEmptyObject(executedBudget))  
            ? (!Utilities.isNullEmptyObject(currentBudgetMod)) ? (executedBudget /currentBudgetMod) 
                : (!Utilities.isNullEmptyObject(currentBudget)) ? (executedBudget/currentBudget) : 0 
            : 0

        if(!isFinite(value)) value = 1;
        return value;
    }

    private calProjectPercentage(data: any){
        let accumBudgetExecuted = this.calTotalExecutedBudget(data)
        let totalBudgetMod = data[Enums.FollowupBasicFields.totalBudgetMod]
        let totalBudget  = data[Enums.FollowupBasicFields.totalBudget]
        
        let value = (!Utilities.isNullEmptyObject(accumBudgetExecuted)) 
            ? (!Utilities.isNullEmptyObject(totalBudgetMod)) ? (accumBudgetExecuted /totalBudgetMod) 
                : (!Utilities.isNullEmptyObject(totalBudget)) ? (accumBudgetExecuted/totalBudget) : 0 
            : 0

        if(!isFinite(value)) value = 1;
        return value;
    }

    private calActivityPercentage(data: any){
        let achievedBeneficiaries = this.sumRealMonths(data)
        let beneficiariesByActivity = data[Enums.FollowupBasicFields.beneficiariesByActivity]
        
        let value = ((!Utilities.isNullEmptyObject(achievedBeneficiaries)) && (!Utilities.isNullEmptyObject(beneficiariesByActivity)))
            ? (achievedBeneficiaries/beneficiariesByActivity) : 0 

        if(!isFinite(value)) value = 1;
        return value;
    }
    
    private calTotalProgressPercentT2(data: any, year: number){
        let endGoalMod = data[Enums.FollowupBasicFields.endGoalMod] 
        let totalAchievement = this.calTotalAchievement(data)
        let endGoal = data[Enums.FollowupBasicFields.endGoal]
        
        let value = (!Utilities.isNullEmptyObject(totalAchievement)) 
            ? (!Utilities.isNullEmptyObject(endGoalMod)) ? (totalAchievement/endGoalMod) 
            : (!Utilities.isNullEmptyObject(endGoal)) ? (totalAchievement/endGoal) : 0 : 0

        if(!isFinite(value)) value = 1;
        return value;
    }

    private calFieldTotal(data: any){
        let value = (data.fieldMen || 0) + (data.fieldWomen || 0)
        return value;
    }

    public getCurrentUser(){
        return this.currentUser
    }

    public getTableInstance(tableId: string): DataGrid {
        let element = document.getElementById(tableId);
        if (Utilities.isNullEmptyObject(element)) {
          return null;
        }
    
        return DataGrid.getInstance(element) as DataGrid;
    }

    public getDataFromTable(tableId: string): Array<any> {  
        let element = document.getElementById(tableId);
        if (Utilities.isNullEmptyObject(element)) {
          return [];
        }
    
        let instance = DataGrid.getInstance(element) as DataGrid;
        return instance.getDataSource().items();
    }

    private getDataFromEditor(editorId: string): any {  
        let element = document.getElementById(editorId);
        if (Utilities.isNullEmptyObject(element)) {
          return '';
        }
    
        let instance = HtmlEditor.getInstance(element) as HtmlEditor;
        return instance.option("value");
    }

    private getMonthName(index: number | string){
        const months = Constants.Months.English;
        const i = Number(index) - 1;
        return  this.formMasters.followupState.find((x: any) => x.OCTAVIACodigo === months[i])?.Title;
    }

    private getMonthNameEn(index: number | string){
        const months = Constants.Months.English;
        const i = Number(index) - 1;
        return  months[i];
    }
}

export default FollowupSubprojectPresenter;
